import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { compareOperationalRecords } from "../lib/sortUtils";
import { CattleIcon, GrassClumpIcon, WeedGrassClumpIcon } from "../lib/icons";
import { buildNextRecordNumber } from "../lib/recordNumberUtils";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { computePastureCapacity, normalizePastureCapacityConfig } from "../lib/pastureCapacity";
import { getFarmClimateProfile, describeFarmClimateProfile, computeClimateCapacityAdjustment, normalizeClimateDatabaseSummary } from "../lib/climateConfig";
import { fetchFarmClimateSummary } from "../lib/climateApi";
import { estimatePastureOccupancy } from "../lib/pastureStocking";
import { fetchPastureOccupancySummary } from "../lib/livestockRegistryApi";
import { getAccounts } from "../lib/authUtils";
import { isAdminNotificationEnabled } from "../lib/adminNotificationPrefs";


function buildClimateWindowSummary(summary, windowDays) {
  const normalized = normalizeClimateDatabaseSummary(summary);
  const key = Number(windowDays) === 15 ? 15 : (Number(windowDays) === 90 ? 90 : 30);
  return {
    latest_observed_on: normalized.latestObservedOn,
    effective_source: normalized.effectiveSource,
    integration_status: normalized.integrationStatus,
    windows: normalized.windows,
    windowsRaw: normalized.windows,
    source_station_name: normalized.sourceStationName,
    source_station_id: normalized.sourceStationId,
    windowDays: key,
  };
}

function getPastureClimateImpactMeta(level = "attention") {
  const key = String(level || "attention").trim() || "attention";
  if (key === "favorable") return { label: "Favorável", color: "#22C55E", icon: "🌱" };
  if (key === "critical") return { label: "Crítica", color: "#EF4444", icon: "🛑" };
  if (key === "restrictive") return { label: "Restritiva", color: "#F97316", icon: "⚠️" };
  return { label: "Atenção", color: "#F59E0B", icon: "👀" };
}

function resolvePastureClimateImpactLevel(climateCalc, hasClimateData) {
  const factor = Number(climateCalc?.factor || 1);
  if (!hasClimateData) return "attention";
  if (factor >= 1.03) return "favorable";
  if (factor >= 0.93) return "attention";
  if (factor >= 0.82) return "restrictive";
  return "critical";
}

function buildPastureClimateTrendMeta(currentCalc, calc15, calc30, calc90) {
  const factorCurrent = Number(currentCalc?.factor || calc30?.factor || 1);
  const factor15 = Number(calc15?.factor || factorCurrent);
  const factor90 = Number(calc90?.factor || calc30?.factor || factorCurrent);
  const shortDelta = factor15 - factorCurrent;
  const longDelta = factorCurrent - factor90;
  if (shortDelta >= 0.03 || longDelta >= 0.05) return { direction: "improving", label: "Melhorando" };
  if (shortDelta <= -0.03 || longDelta <= -0.05) return { direction: "worsening", label: "Piorando" };
  return { direction: "stable", label: "Estável" };
}

function displayLocationRecordNumber(record) {
  if (!record) return "—";
  const rn = String(record.recordNumber || record.id || "").trim();
  if (!rn) return "—";
  if (record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
  return rn;
}

function markLocationCreate(record, userName) {
  return markOperationalCreatePending(record, { userName });
}

function markLocationUpdate(record, userName, operationType = "update") {
  if (record?.pendingLocalCreate) return record;
  return markOperationalUpdatePending(record, { userName, operationType });
}

export function createLocationModule(deps){
  const {
    useState,
    useEffect,
    useRef,
    makeSeed,
    useSysLists,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useToast,
    useBgSyncToast,
    useReliableSyncOnSave,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    canEditCostCenter,
    getAllowedCostCentersForModule,
    getDeniedCostCentersForModule,
    itemMatchesCostCenterAccess,
    itemEditableByCostCenter,
    T,
    COST_CENTERS,
    isCompactViewport,
    createClientId,
    fmtDate,
    GRASS_TYPES,
    WEED_LEVELS,
    POI_STATUSES,
    makeCreateMeta,
    makeUpdateMeta,
    Btn,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
    CCBadge,
    normalizeServiceType,
    PASTURE_COLORS,
    PASTURE_COLOR_NAMES,
    ServicePhotoStatusIcon,
    setEditingFlag,
  } = deps;

function getPoiFarmName(item) {
  return item?.farmName || item?.farm?.name || item?.farm || "";
}

function getConfiguredMapId() {
  const id = String(window.__fzMapsConfig?.mapId || "").trim();
  // AdvancedMarkerElement exige mapId — usa DEMO_MAP_ID se não configurado
  return id || "DEMO_MAP_ID";
}

function createMarkerContentElement(options = {}) {
  const markerLib = window.google?.maps?.marker;
  const maps = window.google?.maps;
  if (!maps) return null;

  const normalizedTitle = String(options.title || "").trim();
  const icon = options.icon;
  const labelText = typeof options.label === "string"
    ? options.label
    : (options.label?.text != null ? String(options.label.text) : "");

  if (markerLib?.PinElement && icon?.path === maps.SymbolPath.CIRCLE) {
    const scale = Number(icon.scale) || 10;
    const borderColor = icon.strokeColor || "#FFFFFF";
    const background = icon.fillColor || "#2563EB";
    const glyphColor = options.label?.color || "#FFFFFF";
    const glyphText = labelText.trim() || "•";
    return new markerLib.PinElement({
      scale: Math.max(0.8, Math.min(1.8, scale / 10)),
      borderColor,
      background,
      glyphColor,
      glyphText,
    });
  }

  if (icon?.url) {
    const wrapper = document.createElement("div");
    wrapper.style.display = "inline-flex";
    wrapper.style.alignItems = "center";
    wrapper.style.justifyContent = "center";
    wrapper.style.transform = "translateY(-2px)";

    const img = document.createElement("img");
    img.alt = normalizedTitle || labelText || "marker";
    img.decoding = "async";
    img.loading = "eager";
    img.draggable = false;
    img.src = icon.url;
    const width = Number(icon?.scaledSize?.width) || Number(icon?.size?.width) || 32;
    const height = Number(icon?.scaledSize?.height) || Number(icon?.size?.height) || 32;
    img.style.width = `${width}px`;
    img.style.height = `${height}px`;
    img.style.pointerEvents = "none";
    wrapper.appendChild(img);
    return wrapper;
  }

  if (markerLib?.PinElement) {
    return new markerLib.PinElement({
      glyphText: labelText.trim() || undefined,
      glyphColor: options.label?.color || "#FFFFFF",
      background: "#2563EB",
      borderColor: "#FFFFFF",
    });
  }

  const fallback = document.createElement("div");
  fallback.style.width = "18px";
  fallback.style.height = "18px";
  fallback.style.borderRadius = "999px";
  fallback.style.background = "#2563EB";
  fallback.style.border = "2px solid #FFFFFF";
  fallback.style.boxShadow = "0 2px 8px rgba(0,0,0,.25)";
  return fallback;
}


// ── AdvancedMarkerElement — funciona em todos os navegadores (Chrome, Firefox, Safari, Edge) ──
function createMapMarker(options = {}) {
  const maps = window.google?.maps;
  const markerLib = maps?.marker;
  if (!maps) return null;

  const AdvancedMarkerElement = markerLib?.AdvancedMarkerElement;

  // Sem AdvancedMarkerElement (Maps ainda carregando) — fallback temporário
  if (!AdvancedMarkerElement) {
    console.warn("[FazendaApp] AdvancedMarkerElement não disponível ainda — usando Marker legado temporariamente");
    return new maps.Marker(options);
  }

  try {
    const raw = createMarkerContentElement(options);
    // PinElement retorna objeto com .element; div/img já são Element direto
    const content = (raw instanceof Element)
      ? raw
      : (raw?.element instanceof Element ? raw.element : null);
    if (!content) throw new Error("createMarkerContentElement retornou conteúdo inválido");

    const marker = new AdvancedMarkerElement({
      map: options.map || null,
      position: options.position,
      title: options.title || "",
      content,
      gmpClickable: true,
      ...(options.draggable ? { gmpDraggable: true } : {}),
    });

    return {
      __advancedMarker: true,
      rawMarker: marker,
      setMap(nextMap) { marker.map = nextMap || null; },
      addListener(eventName, handler) {
        if (typeof marker.addEventListener === "function") {
          const eventMap = { click: "gmp-click", dragend: "gmp-dragend", dragstart: "gmp-dragstart", drag: "gmp-drag" };
          const mappedEvent = eventMap[eventName] || eventName;
          const wrapped = (ev) => handler?.(ev);
          marker.addEventListener(mappedEvent, wrapped);
          return { remove: () => marker.removeEventListener?.(mappedEvent, wrapped) };
        }
        return marker.addListener?.(eventName, handler);
      },
      setPosition(nextPosition) { marker.position = nextPosition; },
      getPosition() {
        const pos = marker.position;
        if (!pos) return null;
        if (typeof pos.lat === "function" && typeof pos.lng === "function") return pos;
        return {
          lat: () => Number(pos.lat),
          lng: () => Number(pos.lng),
        };
      },
    };
  } catch (err) {
    console.warn("[FazendaApp] AdvancedMarkerElement falhou, usando Marker legado:", err);
    return new maps.Marker(options);
  }
}

function openInfoWindow(infoWindow, map, marker) {
  if (!infoWindow || !map) return;
  // Wrapper interno __advancedMarker — extrair rawMarker
  if (marker?.__advancedMarker) {
    infoWindow.open({ map, anchor: marker.rawMarker });
    return;
  }
  // AdvancedMarkerElement nativo direto
  const AdvancedMarkerElement = window.google?.maps?.marker?.AdvancedMarkerElement;
  if (AdvancedMarkerElement && marker instanceof AdvancedMarkerElement) {
    infoWindow.open({ map, anchor: marker });
    return;
  }
  // Fallback legado (não deve ocorrer com AdvancedMarker forçado)
  infoWindow.open(map, marker);
}


const LOCATION_SYNC_DEBUG_KEY = "fz:admin:locationSyncStats:v2";


function getLocationSyncDeviceId() {
  try {
    const key = "fz:device:id";
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = `dev-${Math.random().toString(36).slice(2,10)}-${Date.now().toString(36)}`;
    localStorage.setItem(key, next);
    return next;
  } catch {
    return "device-unknown";
  }
}


function readLocationSyncDebug() {
  try {
    const raw = localStorage.getItem(LOCATION_SYNC_DEBUG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocationSyncDebug(next) {
  try {
    localStorage.setItem(LOCATION_SYNC_DEBUG_KEY, JSON.stringify(next));
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent("fz:location-sync-debug", { detail: next }));
  } catch {}
}

function recordLocationSyncDebug(eventType, detail = {}) {
  const nowIso = new Date().toISOString();
  const current = readLocationSyncDebug();
  const next = {
    ...current,
    module: "Localização",
    storageKey: LOCATION_SYNC_DEBUG_KEY,
    strategy: "1 sync inicial global + atualizações apenas quando houver mudança real",
    googleUsage: "Este painel não faz novas chamadas à Google Maps API nem consulta a Maps Key.",
    updatedAt: nowIso,
    lastEventType: eventType,
    lastEventAt: nowIso,
    counts: {
      opened: Number(current?.counts?.opened || 0),
      initialSnapshots: Number(current?.counts?.initialSnapshots || 0),
      bootstrapSkipped: Number(current?.counts?.bootstrapSkipped || 0),
      saveSyncs: Number(current?.counts?.saveSyncs || 0),
      remoteSignals: Number(current?.counts?.remoteSignals || 0),
      localWriteSignals: Number(current?.counts?.localWriteSignals || 0),
    },
    recentEvents: Array.isArray(current?.recentEvents) ? current.recentEvents.slice(0, 11) : [],
    context: {
      ...(current?.context || {}),
      ...(detail?.context && typeof detail.context === "object" ? detail.context : {}),
      deviceId: getLocationSyncDeviceId(),
    },
  };

  if (eventType === "module-open") next.counts.opened += 1;
  if (eventType === "initial-snapshot-ready") next.counts.initialSnapshots += 1;
  if (eventType === "bootstrap-skipped") next.counts.bootstrapSkipped += 1;
  if (eventType === "save-sync") next.counts.saveSyncs += 1;
  if (eventType === "remote-signal") next.counts.remoteSignals += 1;
  if (eventType === "local-write-signal") next.counts.localWriteSignals += 1;

  const payload = { ...detail };
  delete payload.context;
  next.recentEvents = [
    {
      at: nowIso,
      type: eventType,
      detail: payload,
    },
    ...next.recentEvents,
  ].slice(0, 12);

  writeLocationSyncDebug(next);
  return next;
}

function useServerManagedMapsApiKey() {
  const [mapsConfigured, setMapsConfigured] = React.useState(
    !!window.__fzMapsConfig?.key || !!window.google?.maps || !!window.__gmapsPromise
  );

  React.useEffect(() => {
    if (mapsConfigured) return;

    let cancelled = false;
    let intervalId = null;

    const hasMapsConfig = () => !!window.__fzMapsConfig?.key || !!window.google?.maps || !!window.__gmapsPromise;
    const markConfigured = () => {
      if (!cancelled && hasMapsConfig()) setMapsConfigured(true);
    };

    if (hasMapsConfig()) {
      markConfigured();
      return;
    }

    if (window.__gmapsPromise) {
      Promise.resolve(window.__gmapsPromise).then(markConfigured).catch(() => {});
    }

    intervalId = window.setInterval(() => {
      if (hasMapsConfig()) {
        markConfigured();
        window.clearInterval(intervalId);
      }
    }, 400);

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [mapsConfigured]);

  return mapsConfigured ? "server-managed" : "";
}
function waitForGoogleMaps(cb, maxWait=20000) {
  // Garante marker E drawing disponíveis via importLibrary (obrigatório com loading=async)
  async function ensureLibraries() {
    try {
      if (window.google?.maps?.importLibrary) {
        if (!window.google?.maps?.marker?.AdvancedMarkerElement) {
          await window.google.maps.importLibrary("marker");
        }
        if (!window.google?.maps?.drawing) {
          await window.google.maps.importLibrary("drawing");
        }
      }
    } catch (e) {}
  }

  // Ambas as libraries prontas = mapa pode inicializar
  function isReady() {
    return !!(window.google?.maps?.drawing && window.google?.maps?.marker?.AdvancedMarkerElement);
  }

  // Se promise existe, aguarda e depois importa libraries
  if (window.__gmapsPromise) {
    window.__gmapsPromise.then(async () => {
      await ensureLibraries();
      const start = Date.now();
      function check() {
        if (isReady()) { cb(); return; }
        if (Date.now() - start > 5000) { cb(); return; }
        setTimeout(check, 200);
      }
      check();
    });
    return;
  }
  // Fallback: poll direto
  const start = Date.now();
  function check() {
    if (isReady()) { cb(); return; }
    if (Date.now() - start > maxWait) {
      console.warn("[FazendaApp] Google Maps timed out esperando libraries");
      ensureLibraries().finally(cb);
      return;
    }
    setTimeout(check, 300);
  }
  check();
}

function GoogleMapPicker({ apiKey, lat, lng, polygon, pois, color, onChange }) {
  const mapRef    = useRef(null);
  const mapObj    = useRef(null);
  const polyObj   = useRef(null);
  const poiMarkers= useRef([]);
  const drawMgr   = useRef(null);
  const [googleReady, setGoogleReady] = useState(
    !!(window.google?.maps?.drawing && window.google?.maps?.marker?.AdvancedMarkerElement)
  );
  const polyColor = color || "#10B981";

  // Wait for Google Maps + drawing + marker library (obrigatório com loading=async)
  useEffect(() => {
    if (window.google?.maps?.drawing && window.google?.maps?.marker?.AdvancedMarkerElement) {
      setGoogleReady(true); return;
    }
    if (!apiKey) return;
    waitForGoogleMaps(() => setGoogleReady(true));
  }, [apiKey]);

  // Init map once Google is ready
  useEffect(() => {
    if (!googleReady || !apiKey || !mapRef.current) return;
    // Cleanup previous
    if (drawMgr.current) { drawMgr.current.setMap(null); drawMgr.current = null; }
    if (polyObj.current) { polyObj.current.setMap(null); polyObj.current = null; }
    poiMarkers.current.forEach(m => m.setMap(null));
    poiMarkers.current = [];

    const center = { lat: parseFloat(lat)||(-15.8), lng: parseFloat(lng)||(-47.9) };
    mapObj.current = new window.google.maps.Map(mapRef.current, {
      center, zoom: 16,
      mapTypeId: window.google.maps.MapTypeId.HYBRID,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        mapTypeIds: [
          window.google.maps.MapTypeId.SATELLITE,
          window.google.maps.MapTypeId.HYBRID,
          window.google.maps.MapTypeId.ROADMAP,
          window.google.maps.MapTypeId.TERRAIN,
        ]
      },
      streetViewControl: false,
      fullscreenControl: true,
      ...(getConfiguredMapId() ? { mapId: getConfiguredMapId() } : {}),
    });

    // Render existing polygon (editable)
    if (polygon?.length > 0) {
      polyObj.current = new window.google.maps.Polygon({
        paths: polygon,
        strokeColor: polyColor, strokeOpacity: 1, strokeWeight: 2,
        fillColor: polyColor, fillOpacity: 0.28,
        editable: true, map: mapObj.current
      });
      const onEdit = () => {
        const path = polyObj.current.getPath().getArray().map(p=>({lat:p.lat(),lng:p.lng()}));
        onChange?.({ polygon: path });
      };
      polyObj.current.addListener("mouseup", onEdit);
      polyObj.current.addListener("dragend", onEdit);
      const bounds = new window.google.maps.LatLngBounds();
      polygon.forEach(p => bounds.extend(p));
      mapObj.current.fitBounds(bounds);
    }

    // Render POI markers — AdvancedMarker com PinElement colorido
    (pois||[]).forEach(poi => {
      if (!poi.lat || !poi.lng) return;
      const poiColor = poi.isGlobalPoi ? "#8B5CF6" : "#3B82F6";
      const m = createMapMarker({
        position:{lat:parseFloat(poi.lat),lng:parseFloat(poi.lng)},
        map: mapObj.current, title: poi.name,
        icon:{
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10, fillColor: poiColor, fillOpacity: 1,
          strokeColor: "#fff", strokeWeight: 2,
        },
        label:{ text: (poi.name||"P").charAt(0).toUpperCase(), color: "#fff", fontSize: "11px", fontWeight: "700" },
      });
      const iw = new window.google.maps.InfoWindow({content:`<div style="color:#111;font-weight:600">${`${poi.name}${getPoiFarmName(poi) ? ` - ${getPoiFarmName(poi)}` : ""}`}</div>`});
      m.addListener("click",()=>openInfoWindow(iw, mapObj.current, m));
      poiMarkers.current.push(m);
    });

    // Drawing Manager — always added, drawing library is guaranteed ready
    try {
      drawMgr.current = new window.google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_LEFT,
          drawingModes: [
            window.google.maps.drawing.OverlayType.POLYGON,
            window.google.maps.drawing.OverlayType.MARKER,
          ]
        },
        polygonOptions: {
          strokeColor: polyColor, strokeOpacity: 1, strokeWeight: 2,
          fillColor: polyColor, fillOpacity: 0.28,
          editable: true, draggable: true,
        },
      });
      drawMgr.current.setMap(mapObj.current);
    } catch(e) {
      console.warn("DrawingManager init failed:", e);
    }

    drawMgr.current.addListener("polygoncomplete", poly => {
      if (polyObj.current) polyObj.current.setMap(null);
      polyObj.current = poly;
      const getPath = () => poly.getPath().getArray().map(p=>({lat:p.lat(),lng:p.lng()}));
      onChange?.({ polygon: getPath() });
      poly.addListener("mouseup", ()=>onChange?.({ polygon: getPath() }));
      poly.addListener("dragend", ()=>onChange?.({ polygon: getPath() }));
      drawMgr.current.setDrawingMode(null);
    });

    drawMgr.current.addListener("markercomplete", marker => {
      const pos = { lat: marker.getPosition().lat(), lng: marker.getPosition().lng() };
      marker.setMap(null);
      onChange?.({ newPoi: pos });
      drawMgr.current.setDrawingMode(null);
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleReady, apiKey, color]);

  if (!apiKey) return (
    <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:20,textAlign:"center",color:T.muted,fontSize:13}}>
      Configure a Google Maps API Key no Painel do Admin &gt; 🗺️ Mapa para habilitar o mapa.
    </div>
  );
  if (!googleReady) return (
    <div style={{background:`${T.yellow}18`,border:`1px solid ${T.yellow}44`,borderRadius:10,padding:20,textAlign:"center",color:T.yellow,fontSize:13}}>
      <div style={{fontSize:24,marginBottom:8}}>⏳</div>
      Carregando Google Maps e ferramentas de desenho...
    </div>
  );
  return (
    <div style={{position:"relative",width:"100%",height:420,borderRadius:10,border:`1px solid ${T.border}`,overflow:"visible"}}>
      <div ref={mapRef} style={{width:"100%",height:"100%",borderRadius:10,overflow:"hidden"}}/>
    </div>
  );
}

// ── Overview map — all pastures + all POIs ────────────────────────────────────
function OverviewMap({ apiKey, pastures, pois, onSelectPasture }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const objects= useRef([]);
  // Must declare before useEffects to avoid TDZ error
  const [ovReady, setOvReady] = useState(
    !!(window.google?.maps?.drawing && window.google?.maps?.marker?.AdvancedMarkerElement)
  );

  useEffect(() => {
    if (!ovReady || !apiKey || !window.google) return;
    // Compute center from all polygon/coord data
    let centerLat = -15.8, centerLng = -47.9, count = 0;
    pastures.forEach(p => {
      if (p.lat && p.lng) { centerLat += parseFloat(p.lat); centerLng += parseFloat(p.lng); count++; }
      (p.polygon||[]).forEach(pt => { centerLat += pt.lat; centerLng += pt.lng; count++; });
    });
    if (count > 0) { centerLat /= count; centerLng /= count; }

    mapObj.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: centerLat, lng: centerLng },
      zoom: 14,
      mapTypeId: window.google.maps.MapTypeId.HYBRID,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        mapTypeIds: [
          window.google.maps.MapTypeId.SATELLITE,
          window.google.maps.MapTypeId.HYBRID,
          window.google.maps.MapTypeId.ROADMAP,
          window.google.maps.MapTypeId.TERRAIN,
        ]
      },
      streetViewControl: false,
      fullscreenControl: true,
      ...(getConfiguredMapId() ? { mapId: getConfiguredMapId() } : {}),
    });

    const bounds = new window.google.maps.LatLngBounds();
    let boundsHasPoints = false;

    // Draw each pasture polygon
    pastures.forEach(p => {
      const color = p.color || PASTURE_COLORS[0];
      if ((p.polygon||[]).length > 0) {
        const poly = new window.google.maps.Polygon({
          paths: p.polygon,
          strokeColor: color, strokeOpacity: 1, strokeWeight: 2,
          fillColor: color, fillOpacity: 0.28,
          map: mapObj.current,
        });
        const iw = new window.google.maps.InfoWindow({
          content:`<div style="font-family:sans-serif;min-width:140px">
            <div style="font-weight:700;font-size:14px;margin-bottom:4px">${p.recordNumber ? `${p.recordNumber} · ${p.name}` : p.name}</div>
            <div style="font-size:12px;color:#555">${p.grassType||""} · ${p.areHa||"—"} ha</div>
            <div style="font-size:12px;color:#555">Cap: ${p.capacityUaTotal||"—"} UA</div>
          </div>`
        });
        poly.addListener("click", e => {
          iw.setPosition(e.latLng);
          iw.open(mapObj.current);
          onSelectPasture?.(p);
        });
        p.polygon.forEach(pt => { bounds.extend(pt); boundsHasPoints=true; });
        objects.current.push(poly);
      } else if (p.lat && p.lng) {
        // No polygon — AdvancedMarker com PinElement colorido
        const m = createMapMarker({
          position:{lat:parseFloat(p.lat),lng:parseFloat(p.lng)},
          map:mapObj.current,
          title:p.name,
          icon:{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10, fillColor: color, fillOpacity: .9,
            strokeColor:"#fff", strokeWeight:2,
          },
          label:{ text:p.name.charAt(0).toUpperCase(), color:"#fff", fontSize:"11px", fontWeight:"700" },
        });
        const iw = new window.google.maps.InfoWindow({content:`<div style="font-weight:700">${p.name}</div>`});
        m.addListener("click",()=>{ openInfoWindow(iw, mapObj.current, m); onSelectPasture?.(p); });
        bounds.extend({lat:parseFloat(p.lat),lng:parseFloat(p.lng)});
        boundsHasPoints=true;
        objects.current.push(m);
      }
    });

    // Draw POI markers
    pois.forEach(poi => {
      const lat = parseFloat(poi.lat||(poi.customFields||[]).find(cf=>cf.key==="lat")?.value);
      const lng = parseFloat(poi.lng||(poi.customFields||[]).find(cf=>cf.key==="lng")?.value);
      if (!lat||!lng||isNaN(lat)||isNaN(lng)) return;
      const statusColor={"Ativo":"#10B981","Inativo":"#64748B","Em Manutenção":"#F59E0B"}[poi.status]||"#3B82F6";
      const m = createMapMarker({
        position:{lat,lng}, map:mapObj.current, title:poi.name,
        icon:{
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10, fillColor: statusColor, fillOpacity: 1,
          strokeColor: "#fff", strokeWeight: 2,
        },
        label:{ text:(poi.name||"P").charAt(0).toUpperCase(), color:"#fff", fontSize:"11px", fontWeight:"700" },
      });
      const iw = new window.google.maps.InfoWindow({
        content:`<div style="font-family:sans-serif;min-width:130px">
          <div style="font-weight:700;font-size:13px">${`${poi.name}${getPoiFarmName(poi)?` - ${getPoiFarmName(poi)}`:""}`}</div>
          <div style="font-size:11px;color:#555;margin-top:3px">${poi.costCenter||""}</div>
          <div style="font-size:11px;margin-top:3px"><span style="color:${statusColor};font-weight:600">${poi.status||"Ativo"}</span></div>
        </div>`
      });
      m.addListener("click",()=>openInfoWindow(iw, mapObj.current, m));
      bounds.extend({lat,lng});
      boundsHasPoints=true;
      objects.current.push(m);
    });

    if (boundsHasPoints && !bounds.isEmpty()) {
      mapObj.current.fitBounds(bounds);
      // Don't zoom in too much on single point
      window.google.maps.event.addListenerOnce(mapObj.current,"idle",()=>{
        if(mapObj.current.getZoom()>17) mapObj.current.setZoom(17);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovReady, apiKey, pastures, pois]);

  // Wait for Maps to be ready (script injected by global loader)
  useEffect(() => {
    if (window.google?.maps?.drawing && window.google?.maps?.marker?.AdvancedMarkerElement) {
      setOvReady(true); return;
    }
    if (!apiKey) return;
    waitForGoogleMaps(() => setOvReady(true));
  }, [apiKey]);

  if (!apiKey) return (
    <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:40,textAlign:"center",color:T.muted}}>
      <div style={{fontSize:32,marginBottom:12}}>🗺️</div>
      <div style={{fontWeight:700,color:T.text,marginBottom:4}}>Mapa não configurado</div>
      <div style={{fontSize:13}}>Configure a Google Maps API Key no Painel do Admin &gt; 🗺️ Mapa para visualizar o mapa geral da fazenda.</div>
    </div>
  );
  if (!ovReady) return (
    <div style={{background:`${T.yellow}18`,border:`1px solid ${T.yellow}44`,borderRadius:12,padding:20,color:T.yellow,fontSize:13,textAlign:"center"}}>
      <div style={{fontSize:24,marginBottom:8}}>⏳</div>
      Carregando Google Maps...
      <button onClick={()=>window.location.reload()} style={{display:"block",margin:"10px auto 0",padding:"6px 16px",borderRadius:8,border:`1px solid ${T.yellow}`,background:"none",color:T.yellow,cursor:"pointer",fontSize:12}}>🔄 Recarregar</button>
    </div>
  );
  return <div ref={mapRef} style={{width:"100%",height:520,borderRadius:12,border:`1px solid ${T.border}`,overflow:"hidden"}}/>;
}

function PasturesModule({ session, isGestor, onOpenPOI, hideHeaderMenu=false, initialTab="overview" }) {
  const seed = makeSeed();
  const SL = useSysLists();
  const { farm }                  = useFarm();
  const [pastures,  setPastures]  = useFarmCollection("pastures",  []);
  const [services,  ]             = useFarmCollection("services",   []);
  const [animalLots, ]            = useFarmCollection("animalLots", []);
  const [backendOccupancyMap, setBackendOccupancyMap] = useState({});
  const [, setNotifications]      = useFarmCollection("notifications", []);
  const [adminGlobalsConfigRows]    = useGlobalCollection("adminGlobalsConfig", []);
  const adminGlobalsConfig          = React.useMemo(()=> (adminGlobalsConfigRows||[])[0] || null, [adminGlobalsConfigRows]);
  const pastureCapacityConfig       = React.useMemo(()=> normalizePastureCapacityConfig(adminGlobalsConfig?.pastureCapacityConfig), [adminGlobalsConfig]);
  const farmClimateProfile          = React.useMemo(()=> getFarmClimateProfile(adminGlobalsConfig?.farmClimateProfiles, farm?.fid), [adminGlobalsConfig, farm?.fid]);
  const [climateDbSummary, setClimateDbSummary] = useState(null);
  const [pastureClimateSummaryMap, setPastureClimateSummaryMap] = useState({});
  const farmClimateMeta             = React.useMemo(()=> describeFarmClimateProfile(farmClimateProfile, climateDbSummary), [farmClimateProfile, climateDbSummary]);
  const normalizedClimateDbSummary = React.useMemo(()=> normalizeClimateDatabaseSummary(climateDbSummary), [climateDbSummary]);

  useEffect(()=>{
    let alive = true;
    const farmId = String(farm?.fid || "").trim();
    if(!farmId){ setBackendOccupancyMap({}); return; }
    fetchPastureOccupancySummary({ farmId, userName: session?.name || "" })
      .then((data)=>{
        if(!alive) return;
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        const next = {};
        rows.forEach((row)=>{ const key = String(row?.pasture_id || row?.pastureId || "").trim(); if(key) next[key] = row; });
        setBackendOccupancyMap(next);
      })
      .catch(()=>{ if(alive) setBackendOccupancyMap({}); });
    return ()=>{ alive = false; };
  }, [farm?.fid, session?.name]);
  useEffect(()=>{
    let cancelled = false;
    const farmId = String(farm?.fid || "").trim();
    if (!farmId) { setPastureClimateSummaryMap({}); return; }
    const candidates = (Array.isArray(pastures) ? pastures : []).filter(Boolean);
    if (!candidates.length) { setPastureClimateSummaryMap({}); return; }
    const getPoint = (pasture)=>{
      const lat = Number(pasture?.lat);
      const lng = Number(pasture?.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { latitude: lat, longitude: lng };
      const polygon = Array.isArray(pasture?.polygon) ? pasture.polygon.map((pt)=>({ lat:Number(pt?.lat), lng:Number(pt?.lng) })).filter((pt)=>Number.isFinite(pt.lat) && Number.isFinite(pt.lng)) : [];
      if (!polygon.length) return { latitude: null, longitude: null };
      const latitude = polygon.reduce((sum, pt)=>sum + pt.lat, 0) / polygon.length;
      const longitude = polygon.reduce((sum, pt)=>sum + pt.lng, 0) / polygon.length;
      return { latitude, longitude };
    };
    Promise.all(candidates.map(async (pasture)=>{
      try {
        const point = getPoint(pasture);
        const summary = await fetchFarmClimateSummary({
          farmId,
          preferredSource: farmClimateProfile?.preferredSource || "wunderground",
          fallbackToInmet: farmClimateProfile?.fallbackToInmet !== false,
          latitude: point.latitude,
          longitude: point.longitude,
          stationCandidates: farmClimateProfile?.stations || [],
          contextType: "location",
          contextId: String(pasture?.id || ""),
          contextName: String(pasture?.name || pasture?.title || pasture?.recordNumber || ""),
          windowDays: Number(farmClimateProfile?.analysisWindowDays || 30),
          includeHistory: true,
          userName: session?.name || "",
        });
        return [String(pasture?.id || ""), summary || null];
      } catch (_e) {
        return [String(pasture?.id || ""), null];
      }
    })).then((entries)=>{
      if (cancelled) return;
      const next = {};
      entries.forEach(([key, value])=>{ if (key) next[key] = value; });
      setPastureClimateSummaryMap(next);
    }).catch(()=>{ if (!cancelled) setPastureClimateSummaryMap({}); });
    return ()=>{ cancelled = true; };
  }, [farm?.fid, pastures, farmClimateProfile?.preferredSource, farmClimateProfile?.fallbackToInmet, farmClimateProfile?.stations, farmClimateProfile?.analysisWindowDays, session?.name]);
  const ensureLocationRecordNumbers = React.useCallback((records=[])=>{
    const working = Array.isArray(records) ? records.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{ if (!record || record.recordNumber) return; Object.assign(record, buildNextRecordNumber(working.filter(Boolean), { moduleKey:"locations" })); });
    return working;
  }, []);
  const [stock,     ]             = useGlobalCollection("stock",      seed.stock);
  const [globalPois,]             = useFarmCollection("poi",        []);
  const [moduleSyncFrozen, setModuleSyncFrozen] = useState(false);
  const [frozenPastures, setFrozenPastures] = useState([]);
  const [frozenPois, setFrozenPois] = useState([]);
  const pasturesSourceRef = useRef([]);
  const poisSourceRef = useRef([]);
  const syncOnSave = useReliableSyncOnSave();
  const [form,      setForm]      = useState(null);
  const [selected,  setSelected]  = useState(null);
  const [heightUpdate, setHeightUpdate] = useState(null);
  const [manualOverride, setManualOverride] = useState(null);
  const [toast,     show]         = useToast();
  useBgSyncToast("pastures", show);
  const [tab,       setTab]       = useState(initialTab || "overview");
  const canCreate = canCreateInModule(session,"pastures") || canEditInModule(session,"pastures");
  const canEdit = canEditInModule(session,"pastures");
  const canDelete = canDeleteInModule(session,"pastures");
  const allowedCostCenters = React.useMemo(()=>getAllowedCostCentersForModule(session, "pastures"), [session]);
  const deniedCostCenters = React.useMemo(()=>getDeniedCostCentersForModule(session, "pastures"), [session]);
  const apiKey = useServerManagedMapsApiKey();
  const locationSyncContext = React.useMemo(() => ({
    farmId: farm?.fid || "",
    farmName: farm?.name || "",
    user: session?.name || "",
    mapsKeyConfigured: !!window.__fzMapsConfig?.key,
    mapIdConfigured: !!window.__fzMapsConfig?.mapId,
  }), [farm?.fid, farm?.name, session?.name]);
  const recordLocationSync = React.useCallback((eventType, detail = {}) => {
    const payload = { ...detail, context: locationSyncContext };
    recordLocationSyncDebug(eventType, payload);
    return payload;
  }, [locationSyncContext]);
  const syncOnSaveTracked = React.useCallback((collection, reason = "") => {
    if (reason) recordLocationSync("save-sync", { collection, reason });
    syncOnSave(collection);
  }, [recordLocationSync, syncOnSave]);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function loadClimate() {
      if (!farm?.fid) return;
      try {
        const summary = await fetchFarmClimateSummary({
          farmId: farm.fid,
          preferredSource: farmClimateProfile?.preferredSource || "wunderground",
          fallbackToInmet: farmClimateProfile?.fallbackToInmet !== false,
          userName: session?.name || "",
        });
        if (!cancelled) setClimateDbSummary(summary || null);
      } catch (_e) {
        if (!cancelled) setClimateDbSummary(null);
      }
    }
    loadClimate();
    const refreshHours = Number.isFinite(Number(farmClimateProfile?.climateRefreshHours)) ? Number(farmClimateProfile?.climateRefreshHours) : 1;
    const refreshMs = Math.max(15, refreshHours <= 0 ? 0.5 : refreshHours) * 60 * 60 * 1000;
    timer = setInterval(loadClimate, refreshMs);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [farm?.fid, farmClimateProfile?.preferredSource, farmClimateProfile?.fallbackToInmet, farmClimateProfile?.climateRefreshHours, session?.name]);
  const herbProducts = stock.filter(i => i.vetUse || i.category === "Defensivos");
  const moduleCardsConfig = React.useMemo(()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){
      return {};
    }
  }, []);
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.pastures;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  useEffect(() => {
    const nextTab = initialTab === "list" ? "list" : "overview";
    setTab(nextTab);
    if (nextTab === "overview") setSelected(null);
  }, [initialTab]);

  const allowedOverviewCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    if(Array.isArray(direct) && direct.length) return direct;
    const menuCards = getConfiguredSectionCards("menu:localizacao", []);
    return Array.isArray(menuCards) && menuCards.length ? menuCards : ["pastos","area_total","pontos_interesse","capacidade_animais"];
  }, [getConfiguredSectionCards]);
  const allowedListCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("menu:lugares", []);
    return Array.isArray(direct) && direct.length ? direct : ["pastos","area_total","capacidade_animais","sujos_muito_sujos"];
  }, [getConfiguredSectionCards]);

  useEffect(() => {
    pasturesSourceRef.current = Array.isArray(pastures) ? pastures : [];
  }, [pastures]);

  useEffect(() => {
    poisSourceRef.current = Array.isArray(globalPois) ? globalPois : [];
  }, [globalPois]);

  useEffect(() => {
    if (moduleSyncFrozen) return;
    setFrozenPastures(Array.isArray(pastures) ? pastures.map(item => ({ ...item })) : []);
    setFrozenPois(Array.isArray(globalPois) ? globalPois.map(item => ({ ...item })) : []);
  }, [moduleSyncFrozen, pastures, globalPois]);

  useEffect(() => {
    let cancelled = false;
    setModuleSyncFrozen(false);
    setFrozenPastures(Array.isArray(pasturesSourceRef.current) ? pasturesSourceRef.current.map(item => ({ ...item })) : []);
    setFrozenPois(Array.isArray(poisSourceRef.current) ? poisSourceRef.current.map(item => ({ ...item })) : []);

    recordLocationSync("module-open", {
      visiblePastures: Array.isArray(pasturesSourceRef.current) ? pasturesSourceRef.current.length : 0,
      visiblePoi: Array.isArray(poisSourceRef.current) ? poisSourceRef.current.length : 0,
    });

    const finalizeInitialLocationSync = () => {
      if (cancelled) return;
      const nextPastures = Array.isArray(pasturesSourceRef.current) ? pasturesSourceRef.current.map(item => ({ ...item })) : [];
      const nextPois = Array.isArray(poisSourceRef.current) ? poisSourceRef.current.map(item => ({ ...item })) : [];
      setFrozenPastures(nextPastures);
      setFrozenPois(nextPois);
      setModuleSyncFrozen(true);
    };

    // O bootstrap global já roda na abertura do app/entrada da fazenda.
    // Aqui o módulo só congela a foto inicial para evitar re-render/sync extra
    // a cada entrada em Localização. Novas mudanças remotas continuam chegando
    // pelo ciclo global de sync, apenas quando houver alteração real no servidor.
    const timeoutId = setTimeout(finalizeInitialLocationSync, 180);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      setModuleSyncFrozen(false);
    };
  }, [farm?.fid, recordLocationSync]);


  const activePastures = React.useMemo(() => (
    moduleSyncFrozen ? frozenPastures : (Array.isArray(pastures) ? pastures : [])
  ), [moduleSyncFrozen, frozenPastures, pastures]);
  const activePois = React.useMemo(() => (
    moduleSyncFrozen ? frozenPois : (Array.isArray(globalPois) ? globalPois : [])
  ), [moduleSyncFrozen, frozenPois, globalPois]);
  const visiblePois = React.useMemo(()=> activePois.filter(item=>!(item?._deleted || item?.deletedAt || item?.deleted_at)).filter(item=>itemMatchesCostCenterAccess(session, "poi", item)).sort(compareOperationalRecords), [activePois, session]);

  useEffect(()=>{
    const normalized = ensureLocationRecordNumbers(pastures||[]);
    if ((normalized||[]).some((item, idx)=>String(item?.recordNumber||"") !== String((pastures||[])[idx]?.recordNumber||""))) {
      setPastures(normalized);
    }
  }, [ensureLocationRecordNumbers, pastures, setPastures]);


  // UA/ha calc: 1 UA = 450kg; assume 100g DM/kg BW/day; grass yield varies by type
  function computePolygonAreaHa(points = []) {
    const normalized = (Array.isArray(points) ? points : [])
      .map((pt) => ({ lat: Number(pt?.lat), lng: Number(pt?.lng) }))
      .filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lng));
    if (normalized.length < 3) return "";
    try {
      const areaFn = window.google?.maps?.geometry?.spherical?.computeArea;
      if (typeof areaFn === "function") {
        const areaM2 = Math.abs(areaFn(normalized));
        if (Number.isFinite(areaM2) && areaM2 > 0) return (areaM2 / 10000).toFixed(2);
      }
    } catch {}
    const avgLat = normalized.reduce((sum, pt) => sum + pt.lat, 0) / normalized.length;
    const metersPerDegLat = 111320;
    const metersPerDegLng = 111320 * Math.cos((avgLat * Math.PI) / 180);
    let area = 0;
    for (let i = 0; i < normalized.length; i += 1) {
      const a = normalized[i];
      const b = normalized[(i + 1) % normalized.length];
      const ax = a.lng * metersPerDegLng;
      const ay = a.lat * metersPerDegLat;
      const bx = b.lng * metersPerDegLng;
      const by = b.lat * metersPerDegLat;
      area += (ax * by) - (bx * ay);
    }
    const areaM2 = Math.abs(area) / 2;
    if (!Number.isFinite(areaM2) || areaM2 <= 0) return "";
    return (areaM2 / 10000).toFixed(2);
  }

  function calcCapacityMetrics(pasture) {
    if (!usesAnimalCapacity(pasture)) return null;
    return computePastureCapacity(pasture, pastureCapacityConfig);
  }

  const weedColor = (level) => ({ Limpo:"#10B981",Pouco:"#84CC16",Médio:"#F59E0B",Sujo:"#EF4444","Muito Sujo":"#991B1B" })[level] || T.muted;
  const usesAnimalCapacity = React.useCallback((pasture) => pasture?.useAnimalCapacity !== false, []);

  const getClimateAdjustedMetrics = React.useCallback((pasture) => {
    if (!usesAnimalCapacity(pasture)) return null;
    const base = computePastureCapacity(pasture, pastureCapacityConfig);
    const pastureClimateSummary = normalizeClimateDatabaseSummary(pastureClimateSummaryMap[String(pasture?.id || "")] || climateDbSummary);
    const climate = computeClimateCapacityAdjustment(farmClimateProfile, pastureClimateSummary);
    const uaPerHa = Number(base?.uaPerHa || 0) * Number(climate?.factor || 1);
    const uaTotal = uaPerHa * Number(pasture?.areHa || 0);
    return {
      ...base,
      climateFactor: Number(climate?.factor || 1).toFixed(2),
      climateSummary: climate?.summary || farmClimateMeta.climateSummary || "Clima não configurado",
      climateWindowDays: climate?.windowDays || farmClimateProfile.analysisWindowDays,
      climateRainfallMm: climate?.rainfallMm || 0,
      climateDryDays: climate?.dryDays || 0,
      uaPerHa: Number.isFinite(uaPerHa) ? uaPerHa.toFixed(2) : "0.00",
      uaTotal: Number.isFinite(uaTotal) ? uaTotal.toFixed(2) : "0.00",
    };
  }, [usesAnimalCapacity, pastureCapacityConfig, farmClimateProfile, pastureClimateSummaryMap, climateDbSummary, farmClimateMeta]);

  const syncPolygonArea = React.useCallback((payload = {}) => {
    const nextPolygon = Array.isArray(payload?.polygon) ? payload.polygon : [];
    const nextArea = computePolygonAreaHa(nextPolygon);
    return {
      ...payload,
      areHa: nextArea,
      areaCalculatedAt: nextArea ? new Date().toISOString() : payload?.areaCalculatedAt || "",
    };
  }, []);

  const getPastureModeLabel = React.useCallback((pasture) => usesAnimalCapacity(pasture) ? "Cálculo pecuário" : "Área simples", [usesAnimalCapacity]);
  const getHeightUpdateAgeDays = React.useCallback((pasture) => {
    const raw = pasture?.lastHeightAt || pasture?.createdAt || "";
    const ts = raw ? Date.parse(raw) : NaN;
    if (!Number.isFinite(ts)) return null;
    const diff = Date.now() - ts;
    return Math.max(0, Math.floor(diff / 86400000));
  }, []);
  const getHeightStatusMeta = React.useCallback((pasture) => {
    const days = getHeightUpdateAgeDays(pasture);
    if (days == null) return { color: T.muted, label: "Sem medição", hint: "Sem data de altura registrada" };
    if (days <= 15) return { color: T.green, label: "0–15 dias", hint: `Última altura informada há ${days} dia(s)` };
    if (days <= 30) return { color: T.yellow, label: "16–30 dias", hint: `Última altura informada há ${days} dia(s)` };
    return { color: T.red, label: "31+ dias", hint: `Última altura informada há ${days} dia(s)` };
  }, [getHeightUpdateAgeDays]); // eslint-disable-line react-hooks/exhaustive-deps
  const getStockingStatusMeta = React.useCallback((status) => {
    const normalized = String(status || "").trim();
    if (normalized === "Superlotação") return { color: T.red, tone: "critical", priority: 4, label: normalized };
    if (normalized === "Provável superlotação") return { color: T.yellow, tone: "warning", priority: 3, label: normalized };
    if (normalized === "Atenção") return { color: T.yellow, tone: "warning", priority: 2, label: normalized };
    if (normalized === "Sem capacidade definida") return { color: T.muted, tone: "muted", priority: 1, label: normalized };
    if (normalized === "Sem lotação") return { color: T.accent, tone: "info", priority: 1, label: normalized };
    return { color: T.green, tone: "ok", priority: 0, label: normalized || "Lotação dentro da capacidade" };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const getPastureAttentionState = React.useCallback((pasture) => {
    if (!usesAnimalCapacity(pasture)) {
      return {
        level: "simple",
        color: T.accent,
        label: "Área simples",
        recommendation: "Acompanhe somente área, polígono e observações.",
      };
    }
    const heightMeta = getHeightStatusMeta(pasture);
    const stockingMeta = getStockingStatusMeta(pasture?.stockingStatus);
    const heightDays = getHeightUpdateAgeDays(pasture);
    if (stockingMeta.priority >= 4) {
      return {
        level: "critical",
        color: T.red,
        label: "Superlotação",
        recommendation: "Reduza a carga animal ou redistribua os lotes deste local.",
      };
    }
    if (stockingMeta.priority >= 3) {
      return {
        level: "warning",
        color: T.yellow,
        label: "Provável superlotação",
        recommendation: "Revise altura, clima e lotação antes que o local entre em superlotação.",
      };
    }
    if (heightDays != null && heightDays >= 31) {
      return {
        level: "warning",
        color: T.red,
        label: "Altura vencida",
        recommendation: "Atualize a altura da pastagem para recalcular a capacidade com segurança.",
      };
    }
    if (heightDays != null && heightDays >= 16) {
      return {
        level: "attention",
        color: T.yellow,
        label: "Altura a revisar",
        recommendation: "Planeje uma nova medição de altura nos próximos dias.",
      };
    }
    return {
      level: "ok",
      color: stockingMeta.color || heightMeta.color || T.green,
      label: stockingMeta.label || "Dentro da capacidade",
      recommendation: "Local monitorado e com medições dentro do prazo.",
    };
  }, [getHeightStatusMeta, getHeightUpdateAgeDays, getStockingStatusMeta, usesAnimalCapacity]); // eslint-disable-line react-hooks/exhaustive-deps
  const getPastureSnapshot = React.useCallback((pasture) => ({
    areHa: String(pasture?.areHa || ""),
    currentHeight: String(pasture?.currentHeight || ""),
    weedLevel: String(pasture?.weedLevel || ""),
    capacityUaPerHa: String(pasture?.capacityUaPerHa || ""),
    capacityUaTotal: String(pasture?.capacityUaTotal || ""),
    capacityBaseUaPerHa: String(pasture?.capacityBaseUaPerHa || ""),
    entryHeight: String(pasture?.entryHeight || ""),
    exitHeight: String(pasture?.exitHeight || ""),
  }), []);
  const buildPastureNotificationRecipients = React.useCallback(async (pasture) => {
    const accounts = await getAccounts();
    const farmId = String(pasture?.farmId || farm?.fid || "").trim();
    const recipientsMap = new Map();
    const canAccessFarm = (acc)=>{
      const farmIds = Array.isArray(acc?.farmIds) ? acc.farmIds.filter(Boolean).map(v=>String(v)) : [];
      return !farmId || !farmIds.length || farmIds.includes(farmId);
    };
    const pushRecipient = (acc, roleOverride = "") => {
      if (!acc || acc?.active === false || !canAccessFarm(acc)) return;
      const accountId = String(acc?.id || "").trim();
      const username = String(acc?.username || "").trim().toLowerCase();
      if (!accountId && !username) return;
      const key = accountId || username;
      if (recipientsMap.has(key)) return;
      recipientsMap.set(key, {
        accountId,
        userId: String(acc?.userId || "").trim(),
        username,
        role: String(roleOverride || acc?.role || "").trim().toLowerCase(),
        name: String(acc?.name || "").trim(),
      });
    };
    (Array.isArray(accounts) ? accounts : []).forEach((acc)=>{
      const role = String(acc?.role || "").trim().toLowerCase();
      if (role === "admin") return pushRecipient(acc, "admin");
      if (role === "gestor") return pushRecipient(acc, "gestor");
      if (canEditInModule(acc, "pastures") || canCreateInModule(acc, "pastures")) return pushRecipient(acc, role || "user");
    });
    return Array.from(recipientsMap.values());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const appendPastureNotification = React.useCallback(async (pasture, meta = {}) => {
    try {
      if (!isAdminNotificationEnabled(adminGlobalsConfig, "pastures")) return;
      const recipients = await buildPastureNotificationRecipients(pasture);
      if (!recipients.length) return;
      const event = String(meta?.event || "pasture_alert").trim() || "pasture_alert";
      const entityId = String(pasture?.id || "").trim();
      const existingKey = `${event}:${entityId}`;
      setNotifications((prev)=>{
        const list = Array.isArray(prev) ? prev : [];
        const duplicate = list.find((item)=> String(item?.entityId || "").trim() === entityId && String(item?.event || "").trim() === event && String(item?.resolved || "") !== "true");
        if (duplicate) return list;
        const eventAt = String(meta?.eventAt || pasture?.updatedAt || pasture?.createdAt || new Date().toISOString()).trim() || new Date().toISOString();
        const summary = String(meta?.summary || "").trim() || `O local ${pasture?.name || pasture?.recordNumber || ""} precisa de atenção.`;
        const notifId = createClientId("ntf");
        const notification = {
          id: notifId,
          clientId: notifId,
          farmId: String(pasture?.farmId || farm?.fid || "").trim(),
          type: "pasture_alert",
          event,
          eventLabel: String(meta?.eventLabel || "Alerta do local").trim() || "Alerta do local",
          title: String(meta?.title || "Alerta em Localização").trim() || "Alerta em Localização",
          summary,
          body: String(meta?.body || summary).trim() || summary,
          entityType: "pasture",
          entityId,
          entityRecordNumber: pasture?.recordNumber || "",
          notificationModuleId: "pastures",
          notificationSectionKey: "pastures",
          status: String(meta?.status || pasture?.stockingStatus || "Atenção").trim() || "Atenção",
          orderStatus: String(meta?.status || pasture?.stockingStatus || "Atenção").trim() || "Atenção",
          costCenter: pasture?.costCenter || "",
          requester: session?.name || "Sistema",
          recipients,
          openedInApp: false,
          readBy: [],
          dedupeKey: existingKey,
          eventAt,
          ...makeCreateMeta(session?.name || "", { updatedAt: eventAt }),
        };
        return [...list, notification];
      });
      syncOnSave("notifications");
    } catch (_e) {}
  }, [adminGlobalsConfig, buildPastureNotificationRecipients, farm?.fid, session?.name, setNotifications, syncOnSave]); // eslint-disable-line react-hooks/exhaustive-deps
  const appendPastureHistory = React.useCallback((pasture, entry = {}) => {
    const history = Array.isArray(pasture?.capacityHistory) ? pasture.capacityHistory.slice() : [];
    history.unshift({
      id: createClientId("phist"),
      at: entry.at || new Date().toISOString(),
      by: entry.by || session?.name || "",
      source: entry.source || "auto",
      reason: entry.reason || "update",
      label: entry.label || "Atualização",
      notes: String(entry.notes || "").trim(),
      snapshot: entry.snapshot || getPastureSnapshot(pasture),
    });
    return history.slice(0, 30);
  }, [getPastureSnapshot, session?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  const openManualOverride = React.useCallback((pasture) => {
    if (!itemEditableByCostCenter(session, "pastures", pasture)) {
      show("Sem permissão para ajustar este local.", "error");
      return;
    }
    const auto = pasture?.autoCalculated || {};
    const existing = pasture?.manualOverride || {};
    setManualOverride({
      id: pasture.id,
      name: pasture.name || "",
      areaHa: String(existing.areaHa ?? pasture.areHa ?? ""),
      currentHeight: String(existing.currentHeight ?? pasture.currentHeight ?? ""),
      weedLevel: String(existing.weedLevel ?? pasture.weedLevel ?? "Limpo"),
      capacityUaPerHa: String(existing.capacityUaPerHa ?? pasture.capacityUaPerHa ?? ""),
      capacityUaTotal: String(existing.capacityUaTotal ?? pasture.capacityUaTotal ?? ""),
      reason: String(existing.reason || ""),
      autoSnapshot: {
        areHa: String(auto.areHa ?? pasture.areHa ?? ""),
        currentHeight: String(auto.currentHeight ?? pasture.currentHeight ?? ""),
        weedLevel: String(auto.weedLevel ?? pasture.weedLevel ?? ""),
        capacityUaPerHa: String(auto.capacityUaPerHa ?? pasture.capacityUaPerHa ?? ""),
        capacityUaTotal: String(auto.capacityUaTotal ?? pasture.capacityUaTotal ?? ""),
      },
    });
  }, [session, show]); // eslint-disable-line react-hooks/exhaustive-deps
  const openHeightUpdate = React.useCallback((pasture) => {
    if (!itemEditableByCostCenter(session, "pastures", pasture)) {
      show("Sem permissão para atualizar a altura desta pastagem.", "error");
      return;
    }
    setHeightUpdate({
      id: pasture.id,
      name: pasture.name || "",
      currentHeight: pasture.currentHeight || "",
      measuredAt: String(pasture.lastHeightAt || pasture.createdAt || "").slice(0,10) || new Date().toISOString().slice(0,10),
      notes: pasture.lastHeightNotes || "",
    });
  }, [session, show]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveHeightUpdate = React.useCallback(() => {
    if (!heightUpdate?.id) return;
    const numericHeight = String(heightUpdate.currentHeight || "").trim();
    if (!numericHeight) { show("Informe a altura atual da pastagem.", "error"); return; }
    const measuredAt = String(heightUpdate.measuredAt || "").slice(0,10) || new Date().toISOString().slice(0,10);
    const updateTs = new Date().toISOString();
    intentionalPasturesMutationRef.current = true;
    setPasturesForLocation(prev => dedupePastures((Array.isArray(prev) ? prev : []).map(item => String(item?.id) === String(heightUpdate.id)
      ? normalizePastureEntry({
          ...item,
          currentHeight: numericHeight,
          lastHeightAt: measuredAt,
          lastHeightNotes: String(heightUpdate.notes || "").trim(),
          manualOverride: item?.manualOverride?.active ? { ...(item.manualOverride||{}), currentHeight: numericHeight } : item?.manualOverride,
          capacityHistory: appendPastureHistory(item, {
            at: updateTs,
            source: "height-update",
            reason: "height-update",
            label: "Altura atualizada",
            notes: String(heightUpdate.notes || "").trim(),
            snapshot: {
              ...getPastureSnapshot(item),
              currentHeight: numericHeight,
              weedLevel: String(item?.manualOverride?.active ? (item?.manualOverride?.weedLevel || item?.weedLevel || "") : (item?.weedLevel || "")),
            },
          }),
          updatedAt: updateTs,
          updatedBy: session?.name || "",
        })
      : normalizePastureEntry(item))));
    setSelected(prev => prev && String(prev.id) === String(heightUpdate.id)
      ? normalizePastureEntry({
          ...prev,
          currentHeight: numericHeight,
          lastHeightAt: measuredAt,
          lastHeightNotes: String(heightUpdate.notes || "").trim(),
          manualOverride: prev?.manualOverride?.active ? { ...(prev.manualOverride||{}), currentHeight: numericHeight } : prev?.manualOverride,
          capacityHistory: appendPastureHistory(prev, {
            at: updateTs,
            source: "height-update",
            reason: "height-update",
            label: "Altura atualizada",
            notes: String(heightUpdate.notes || "").trim(),
            snapshot: { ...getPastureSnapshot(prev), currentHeight: numericHeight },
          }),
          updatedAt: updateTs,
          updatedBy: session?.name || "",
        })
      : prev);
    if (form && !form._new && String(form.id) === String(heightUpdate.id)) {
      setForm(prev => prev ? normalizePastureEntry({
        ...prev,
        currentHeight: numericHeight,
        lastHeightAt: measuredAt,
        lastHeightNotes: String(heightUpdate.notes || "").trim(),
        manualOverride: prev?.manualOverride?.active ? { ...(prev.manualOverride||{}), currentHeight: numericHeight } : prev?.manualOverride,
        capacityHistory: appendPastureHistory(prev, {
          at: updateTs,
          source: "height-update",
          reason: "height-update",
          label: "Altura atualizada",
          notes: String(heightUpdate.notes || "").trim(),
          snapshot: { ...getPastureSnapshot(prev), currentHeight: numericHeight },
        }),
        updatedAt: updateTs,
        updatedBy: session?.name || "",
      }) : prev);
    }
    setHeightUpdate(null);
    show("✅ Altura da pastagem atualizada e capacidade recalculada.");
    syncOnSaveTracked("pastures", "user-height-update");
  }, [appendPastureHistory, form, getPastureSnapshot, heightUpdate, session?.name, show, syncOnSaveTracked]); // eslint-disable-line react-hooks/exhaustive-deps


  const dedupePastures = React.useCallback((items = []) => {
    const pastureTs = (item) => Date.parse(item?.deletedAt || item?.updatedAt || item?.createdAt || "") || 0;
    const getPastureBusinessKey = (item) => {
      if (!item || typeof item !== "object") return "";
      const farmId = String(item.farmId || farm?.fid || "").trim();
      const name = String(item.name || item.title || item.label || "").trim().toLowerCase();
      const costCenter = String(item.costCenter || "").trim().toLowerCase();
      if (!farmId || !name) return "";
      return `pasture:biz:${farmId}:${name}:${costCenter}`;
    };
    const choosePreferredPasture = (prev, next) => {
      if (!prev) return next;
      if (!next) return prev;
      const prevDeleted = !!(prev?._deleted || prev?.deletedAt || prev?.deleted_at);
      const nextDeleted = !!(next?._deleted || next?.deletedAt || next?.deleted_at);
      const prevTs = pastureTs(prev);
      const nextTs = pastureTs(next);
      if (prevDeleted !== nextDeleted) {
        if (prevTs === nextTs) return nextDeleted ? next : prev;
        return nextTs > prevTs ? next : prev;
      }
      if (nextTs !== prevTs) return nextTs > prevTs ? next : prev;
      const completeness = (item) => {
        if (!item || typeof item !== "object") return 0;
        let score = 0;
        if (String(item.name || "").trim()) score += 5;
        if (String(item.costCenter || "").trim()) score += 3;
        if (String(item.grassType || "").trim()) score += 2;
        if (Array.isArray(item.polygon) && item.polygon.length) score += 6;
        if (Array.isArray(item.pois) && item.pois.length) score += 2;
        if (String(item.lat || "").trim()) score += 1;
        if (String(item.lng || "").trim()) score += 1;
        if (String(item.notes || "").trim()) score += 1;
        return score;
      };
      const prevScore = completeness(prev);
      const nextScore = completeness(next);
      if (nextScore !== prevScore) return nextScore > prevScore ? next : prev;
      const prevId = String(prev.clientId || prev.id || "");
      const nextId = String(next.clientId || next.id || "");
      return nextId >= prevId ? next : prev;
    };

    const byIdentity = new Map();
    const withoutIdentity = [];
    for (const raw of Array.isArray(items) ? items : []) {
      const item = raw && typeof raw === "object" ? raw : null;
      if (!item) continue;
      const directId = String(item.clientId || item.id || "").trim();
      const businessKey = getPastureBusinessKey(item);
      const key = directId ? `id:${directId}` : businessKey;
      if (!key) {
        withoutIdentity.push(item);
        continue;
      }
      const prev = byIdentity.get(key);
      const preferred = choosePreferredPasture(prev, item);
      byIdentity.set(key, preferred);
      const aliasKeys = [];
      if (directId) aliasKeys.push(`id:${directId}`);
      if (businessKey) aliasKeys.push(businessKey);
      for (const alias of aliasKeys) {
        const current = byIdentity.get(alias);
        byIdentity.set(alias, choosePreferredPasture(current, preferred));
      }
    }
    const finalMap = new Map();
    for (const item of byIdentity.values()) {
      const finalKey = getPastureBusinessKey(item) || `id:${String(item?.clientId || item?.id || createClientId("tmp"))}`;
      finalMap.set(finalKey, choosePreferredPasture(finalMap.get(finalKey), item));
    }
    for (const item of withoutIdentity) {
      finalMap.set(`loose:${String(item?.id || createClientId("tmp"))}`, item);
    }
    return Array.from(finalMap.values());
  }, [farm?.fid]);

  const normalizePastureEntry = React.useCallback((item) => {
    if(!item || typeof item !== "object") return item;
    const now = new Date().toISOString();
    const next = { ...item };
    if(!next.id) next.id = next.clientId || createClientId("past");
    if(typeof next.id !== "string") next.id = String(next.id);
    if(!next.clientId) next.clientId = String(next.id);
    if(typeof next.clientId !== "string") next.clientId = String(next.clientId);
    if(!next.farmId) next.farmId = farm?.fid || "";
    if(!next.createdAt) next.createdAt = next.updatedAt || now;
    if(!next.updatedAt) next.updatedAt = next.createdAt || now;
    if(!next.updatedBy) next.updatedBy = session?.name || "";
    if(!Array.isArray(next.polygon)) next.polygon = [];
    if(!Array.isArray(next.pois)) next.pois = [];
    if (typeof next.useAnimalCapacity !== "boolean") next.useAnimalCapacity = true;
    if (next.useAnimalCapacity !== false) {
      if (!String(next.lastHeightAt || "").trim()) next.lastHeightAt = String(next.createdAt || next.updatedAt || now).slice(0,10);
    } else {
      next.lastHeightAt = "";
      next.lastHeightNotes = "";
    }
    if (Array.isArray(next.polygon) && next.polygon.length) {
      const autoArea = computePolygonAreaHa(next.polygon);
      if (autoArea) {
        next.areHa = autoArea;
        if (!next.areaCalculatedAt) next.areaCalculatedAt = next.updatedAt || next.createdAt || now;
      }
    }
    if (next.useAnimalCapacity !== false) {
      const capacityMetrics = computePastureCapacity(next, pastureCapacityConfig);
      const pastureClimateSummary = normalizeClimateDatabaseSummary(pastureClimateSummaryMap[String(next.id || "")] || climateDbSummary);
      const climateMetrics = computeClimateCapacityAdjustment(farmClimateProfile, pastureClimateSummary);
      const climateUaPerHaValue = Number(capacityMetrics.uaPerHa || 0) * Number(climateMetrics.factor || 1);
      const climateUaTotalValue = climateUaPerHaValue * Number(next.areHa || 0);
      next.autoCalculated = {
        areHa: String(next.areHa || ""),
        currentHeight: String(next.currentHeight || ""),
        weedLevel: String(next.weedLevel || ""),
        capacityUaPerHa: capacityMetrics.uaPerHa,
        capacityUaTotal: capacityMetrics.uaTotal,
        climateUaPerHa: climateUaPerHaValue.toFixed(2),
        climateUaTotal: climateUaTotalValue.toFixed(2),
        climateFactor: Number(climateMetrics.factor || 1).toFixed(2),
        capacityBaseUaPerHa: capacityMetrics.baseUaPerHa,
        capacityWeedFactor: capacityMetrics.weedFactor,
        capacityHeightFactor: capacityMetrics.heightFactor,
      };
      next.capacityStructuralUaPerHa = capacityMetrics.uaPerHa;
      next.capacityStructuralUaTotal = capacityMetrics.uaTotal;
      next.capacityClimateFactor = Number(climateMetrics.factor || 1).toFixed(2);
      next.capacityClimateSummary = climateMetrics.summary || farmClimateMeta.climateSummary || "Clima não configurado";
      next.capacityClimateStationName = pastureClimateSummary?.sourceStationName || pastureClimateSummary?.sourceStationId || "";
      next.capacityClimateWindowDays = String(climateMetrics.windowDays || farmClimateProfile.analysisWindowDays || 30);
      next.capacityClimateRainfallMm = String(climateMetrics.rainfallMm || "");
      next.capacityClimateDryDays = String(climateMetrics.dryDays || "");
      next.capacityClimateUaPerHa = climateUaPerHaValue.toFixed(2);
      next.capacityClimateUaTotal = climateUaTotalValue.toFixed(2);
      next.capacityUaPerHa = climateUaPerHaValue.toFixed(2);
      next.capacityUaTotal = climateUaTotalValue.toFixed(2);
      next.capacityAnimalsEq = next.capacityUaTotal;
      next.capacityBaseUaPerHa = capacityMetrics.baseUaPerHa;
      next.capacityWeedFactor = capacityMetrics.weedFactor;
      next.capacityHeightFactor = capacityMetrics.heightFactor;
      if (!String(next.entryHeight || "").trim() && capacityMetrics.entryHeight) next.entryHeight = capacityMetrics.entryHeight;
      if (!String(next.exitHeight || "").trim() && capacityMetrics.exitHeight) next.exitHeight = capacityMetrics.exitHeight;
      if (next.manualOverride?.active) {
        const manual = next.manualOverride || {};
        const manualBase = {
          ...next,
          areHa: String(manual.areaHa ?? next.areHa ?? ""),
          currentHeight: String(manual.currentHeight ?? next.currentHeight ?? ""),
          weedLevel: String(manual.weedLevel || next.weedLevel || "Limpo"),
        };
        const manualMetrics = computePastureCapacity(manualBase, pastureCapacityConfig);
        next.areHa = manualBase.areHa;
        next.currentHeight = manualBase.currentHeight;
        next.weedLevel = manualBase.weedLevel;
        next.capacityUaPerHa = String(manual.capacityUaPerHa || manualMetrics.uaPerHa || "");
        next.capacityUaTotal = String(manual.capacityUaTotal || manualMetrics.uaTotal || "");
        next.capacityAnimalsEq = String((Number(next.capacityUaTotal || 0)).toFixed(2));
      }
      const occupancy = estimatePastureOccupancy(animalLots, next.id, farmClimateProfile?.climateRiskThreshold, Number(next.capacityUaTotal || 0));
      const backendOccupancy = backendOccupancyMap[String(next.id || "")] || null;
      if (backendOccupancy) {
        next.stockingUsedUa = Number(backendOccupancy.ua_allocated || 0).toFixed(2);
        next.stockingAvailableUa = Number(Math.max(0, Number(next.capacityUaTotal || 0) - Number(backendOccupancy.ua_allocated || 0))).toFixed(2);
        next.stockingRatio = Number(backendOccupancy.occupancy_ratio || 0).toFixed(2);
        next.stockingHeadCount = String(backendOccupancy.deduped_animals_count || 0);
        next.stockingLotsCount = String(Number(backendOccupancy.tracked_lot_animals_count || 0) + Number(backendOccupancy.collective_lot_animals_count || 0));
        next.stockingStatus = String(backendOccupancy.status_label || backendOccupancy.status || occupancy.status || "Sem lotação");
        next.stockingTone = String(backendOccupancy.tone || occupancy.tone || "muted");
        next.stockingSource = "backend";
      } else {
        next.stockingUsedUa = occupancy.usedUa ? occupancy.usedUa.toFixed(2) : "0.00";
        next.stockingAvailableUa = occupancy.availableUa ? occupancy.availableUa.toFixed(2) : "0.00";
        next.stockingRatio = occupancy.ratio ? occupancy.ratio.toFixed(2) : "0.00";
        next.stockingHeadCount = String(occupancy.headCount || 0);
        next.stockingLotsCount = String(occupancy.lotsCount || 0);
        next.stockingStatus = occupancy.status;
        next.stockingTone = occupancy.tone;
        next.stockingSource = "front";
      }
    } else {
      next.capacityUaPerHa = "";
      next.capacityUaTotal = "";
      next.capacityAnimalsEq = "";
      next.capacityBaseUaPerHa = "";
      next.capacityWeedFactor = "";
      next.capacityHeightFactor = "";
      next.entryHeight = "";
      next.exitHeight = "";
    }
    return next;
  }, [animalLots, backendOccupancyMap, farm?.fid, farmClimateMeta.climateSummary, farmClimateProfile, pastureClimateSummaryMap, climateDbSummary, pastureCapacityConfig, session?.name]);

  const visiblePastures = React.useMemo(()=> dedupePastures(activePastures.filter(item=>!(item?._deleted || item?.deletedAt || item?.deleted_at)).filter(item=>itemMatchesCostCenterAccess(session, "pastures", item))).sort(compareOperationalRecords), [activePastures, session, dedupePastures]);

  const buildPastureClimateOperationalMeta = React.useCallback((pasture) => {
    const summary = normalizeClimateDatabaseSummary(pastureClimateSummaryMap[String(pasture?.id || "")] || climateDbSummary);
    const calcCurrent = computeClimateCapacityAdjustment(farmClimateProfile, summary);
    const calc15 = computeClimateCapacityAdjustment({ ...farmClimateProfile, analysisWindowDays: 15 }, buildClimateWindowSummary(summary, 15));
    const calc30 = computeClimateCapacityAdjustment({ ...farmClimateProfile, analysisWindowDays: 30 }, buildClimateWindowSummary(summary, 30));
    const calc90 = computeClimateCapacityAdjustment({ ...farmClimateProfile, analysisWindowDays: 90 }, buildClimateWindowSummary(summary, 90));
    const level = resolvePastureClimateImpactLevel(calcCurrent, !!summary?.latestObservedOn);
    const impactMeta = getPastureClimateImpactMeta(level);
    const trendMeta = buildPastureClimateTrendMeta(calcCurrent, calc15, calc30, calc90);
    const adjustedUa = Number(pasture?.capacityUaTotal || 0);
    const baseUa = Number(pasture?.capacityStructuralUaTotal || computePastureCapacity(pasture, pastureCapacityConfig)?.uaTotal || 0);
    const sourceLabel = summary?.sourceStationName || summary?.sourceStationId || farmClimateMeta.sourceLabel || "Sem estação";
    return {
      level,
      impactMeta,
      trendMeta,
      adjustedUa,
      baseUa,
      factorLabel: calcCurrent?.factorLabel || `${Number(pasture?.capacityClimateFactor || 1).toFixed(2)}x`,
      rainLabel: `${Number(calcCurrent?.rainfallMm || 0).toFixed(1)} mm`,
      dryLabel: `${Math.round(Number(calcCurrent?.dryDays || 0))} dias secos`,
      sourceLabel,
      summaryLabel: calcCurrent?.summary || pasture?.capacityClimateSummary || farmClimateMeta.climateSummary || farmClimateMeta.summary,
      observedLabel: summary?.latestObservedOn ? fmtDate(summary.latestObservedOn) : "Sem leitura no banco",
      stationDistanceLabel: summary?.calculation?.stationUsed?.distanceKm == null ? "" : `${summary.calculation.stationUsed.distanceKm.toFixed(2)} km`,
      summary,
    };
  }, [pastureClimateSummaryMap, climateDbSummary, farmClimateProfile, pastureCapacityConfig, farmClimateMeta.sourceLabel, farmClimateMeta.climateSummary, farmClimateMeta.summary]);

  const listClimateOverview = React.useMemo(() => {
    const monitored = visiblePastures.filter(usesAnimalCapacity);
    if (!monitored.length) {
      const impactMeta = getPastureClimateImpactMeta('attention');
      return {
        impactMeta,
        trendMeta: { label: 'Estável' },
        monitoredCount: 0,
        adjustedUaTotal: 0,
        baseUaTotal: 0,
        sourceLabel: farmClimateMeta.sourceLabel || 'Sem estação',
        observedLabel: climateDbSummary?.latestObservedOn ? fmtDate(climateDbSummary.latestObservedOn) : 'Sem leitura no banco',
        factorLabel: '1.00x',
        summaryLabel: 'Nenhuma pastagem pecuária ativa cadastrada para leitura climática operacional.',
      };
    }
    const metas = monitored.map(buildPastureClimateOperationalMeta);
    const severityOrder = { favorable: 0, attention: 1, restrictive: 2, critical: 3 };
    const worstMeta = metas.reduce((worst, item) => (severityOrder[item.level] > severityOrder[worst.level] ? item : worst), metas[0]);
    const worseningCount = metas.filter(item => item.trendMeta?.direction === 'worsening').length;
    const improvingCount = metas.filter(item => item.trendMeta?.direction === 'improving').length;
    const trendMeta = worseningCount > improvingCount ? { label: 'Piorando' } : (improvingCount > worseningCount ? { label: 'Melhorando' } : { label: 'Estável' });
    return {
      impactMeta: worstMeta.impactMeta,
      trendMeta,
      monitoredCount: monitored.length,
      adjustedUaTotal: monitored.reduce((sum, item) => sum + Number(item?.capacityUaTotal || 0), 0),
      baseUaTotal: monitored.reduce((sum, item) => sum + Number(item?.capacityStructuralUaTotal || computePastureCapacity(item, pastureCapacityConfig)?.uaTotal || 0), 0),
      sourceLabel: worstMeta.sourceLabel,
      observedLabel: worstMeta.observedLabel,
      factorLabel: worstMeta.factorLabel,
      summaryLabel: worstMeta.summaryLabel,
    };
  }, [visiblePastures, buildPastureClimateOperationalMeta, pastureCapacityConfig, farmClimateMeta.sourceLabel, climateDbSummary?.latestObservedOn, usesAnimalCapacity]);

  const getStockingBalloonMeta = React.useCallback((pasture)=>{
    const usedUa = Number(pasture?.stockingUsedUa || 0);
    const capacityUa = Number(pasture?.capacityUaTotal || getClimateAdjustedMetrics(pasture)?.uaTotal || 0);
    const ratio = capacityUa > 0 ? usedUa / capacityUa : 0;
    if (!(capacityUa > 0)) {
      return {
        color: T.muted,
        label: "Lotação: Sem base",
        shortLabel: "Sem base",
        ratio: 0,
        usedUa,
        capacityUa,
      };
    }
    if (ratio < 0.75) {
      return {
        color: "#60A5FA",
        label: "Lotação: Abaixo",
        shortLabel: "Abaixo",
        ratio,
        usedUa,
        capacityUa,
      };
    }
    if (ratio > 1.4) {
      return {
        color: T.red,
        label: "Lotação: Extrema",
        shortLabel: "Extrema",
        ratio,
        usedUa,
        capacityUa,
      };
    }
    if (ratio > 1.15) {
      return {
        color: "#F97316",
        label: "Lotação: Alta",
        shortLabel: "Alta",
        ratio,
        usedUa,
        capacityUa,
      };
    }
    return {
      color: T.green,
      label: "Lotação: Ideal",
      shortLabel: "Ideal",
      ratio,
      usedUa,
      capacityUa,
    };
  }, [getClimateAdjustedMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  const defForm = {
    _new:true, name:"", grassType:GRASS_TYPES[0], areHa:"", capacity:"",
    costCenter: allowedCostCenters[0] || COST_CENTERS[0],
    useAnimalCapacity:true,
    currentHeight:"", entryHeight:"", exitHeight:"", lastUsed:"",
    lastHeightAt:"", lastHeightNotes:"",
    capacityUaPerHa:"", capacityUaTotal:"", capacityAnimalsEq:"",
    capacityBaseUaPerHa:"", capacityWeedFactor:"", capacityHeightFactor:"",
    weedLevel:"Limpo", lastHerbicide:"", herbicideDate:"", herbicideProduct:"",
    herbicideRecipe:"", lat:"", lng:"", polygon:[], pois:[], notes:"",
    areaCalculatedAt:"",
    capacityHistory:[],
    manualOverride:null,
    color: PASTURE_COLORS[0]
  };
  // Track editing state for sync
  useEffect(() => { setEditingFlag?.(!!form); }, [form]);
  const lastNonEmptyPasturesRef = useRef([]);
  const intentionalPasturesMutationRef = useRef(false);
  const bootstrapRetryRef = useRef("");

  const setPasturesForLocation = React.useCallback((updater) => {
    setPastures(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (moduleSyncFrozen) {
        setFrozenPastures(Array.isArray(next) ? next.map(item => ({ ...item })) : []);
      }
      return next;
    });
  }, [moduleSyncFrozen, setPastures]);

  useEffect(() => {
    if (!Array.isArray(pastures)) return;
    if (visiblePastures.length > 0) {
      lastNonEmptyPasturesRef.current = pastures.map(item => ({ ...item }));
      bootstrapRetryRef.current = "";
      return;
    }
    const shouldGuardFarm1 = String(farm?.fid || "") === "farm1";
    if (!shouldGuardFarm1) return;
    if (intentionalPasturesMutationRef.current) {
      intentionalPasturesMutationRef.current = false;
      return;
    }
    const retryKey = `${String(farm?.fid || "")}:${lastNonEmptyPasturesRef.current.length}`;
    if (bootstrapRetryRef.current === retryKey) return;
    bootstrapRetryRef.current = retryKey;
    if (lastNonEmptyPasturesRef.current.length === 0) return;
    const restore = lastNonEmptyPasturesRef.current.map(item => ({ ...item }));
    setPasturesForLocation(restore);
    show("⚠️ Os pastos da Fazenda Principal foram restaurados localmente, sem disparar novo sync do módulo.", "info");
  }, [pastures, visiblePastures.length, farm?.fid, session?.name, setPasturesForLocation, show]);

  function deletePastureRecord(pastureLike, options = {}) {
    const { skipConfirm = false } = options || {};
    const base = normalizePastureEntry(pastureLike || {});
    if (!base?.id) { show("Localização não encontrada.", "error"); return false; }
    if (!canDelete || !itemEditableByCostCenter(session, "pastures", base)) {
      show("Sem permissão para excluir este centro de custo.", "error");
      return false;
    }
    if (!skipConfirm) {
      const ok = typeof window === "undefined" ? true : window.confirm(`Deseja realmente excluir a localização "${base.name || "sem nome"}"?`);
      if (!ok) return false;
    }
    const actor = session?.name || "";
    const deletedAt = new Date().toISOString();
    const deletedPasture = markLocationUpdate(normalizePastureEntry({
      ...base,
      _deleted: true,
      deletedAt,
      deleted_at: deletedAt,
      deletedBy: actor,
      updatedAt: deletedAt,
      updatedBy: actor,
    }), actor, "delete");
    intentionalPasturesMutationRef.current = true;
    setPasturesForLocation(prev => dedupePastures([
      ...(Array.isArray(prev) ? prev : []).map(item => normalizePastureEntry(item)),
      deletedPasture,
    ]));
    setSelected(prev => prev && String(prev.id) === String(deletedPasture.id) ? null : prev);
    setForm(prev => prev && String(prev.id) === String(deletedPasture.id) ? null : prev);
    show("🗑️ Removido.");
    syncOnSaveTracked("pastures", "user-delete");
    return true;
  }

  useEffect(() => {
    if (!Array.isArray(pastures) || pastures.length === 0) return;
    const normalized = dedupePastures(pastures.map(normalizePastureEntry));
    const changed = normalized.length !== pastures.length || normalized.some((item, idx) => JSON.stringify(item) !== JSON.stringify(pastures[idx]));
    if (changed) {
      intentionalPasturesMutationRef.current = true;
      setPasturesForLocation(normalized);
    }
  }, [pastures, normalizePastureEntry, dedupePastures, setPasturesForLocation]);

  const pastureAlertSentRef = useRef(new Set());
  useEffect(() => {
    if (!Array.isArray(visiblePastures) || !visiblePastures.length) return;
    visiblePastures.forEach((pasture) => {
      if (!usesAnimalCapacity(pasture)) return;
      const heightDays = getHeightUpdateAgeDays(pasture);
      if (heightDays != null && heightDays >= 31) {
        const key = `height-overdue:${pasture.id}:${pasture.lastHeightAt || pasture.createdAt || ""}`;
        if (!pastureAlertSentRef.current.has(key)) {
          pastureAlertSentRef.current.add(key);
          appendPastureNotification(pasture, {
            event: "pasture_height_overdue",
            eventLabel: "Altura da pastagem vencida",
            title: `Atualize a altura de ${pasture.name || pasture.recordNumber || ""}`,
            summary: `O local ${pasture.name || pasture.recordNumber || ""} está há mais de 30 dias sem nova medição de altura.`,
            body: `O local ${pasture.name || pasture.recordNumber || ""} está há mais de 30 dias sem nova medição de altura. Atualize o botão Altura Pastagem para recalcular a capacidade.`,
            status: "Atenção",
          });
        }
      }
      const stockingStatus = String(pasture?.stockingStatus || "").trim();
      if (["Provável superlotação", "Superlotação"].includes(stockingStatus)) {
        const key = `stocking:${pasture.id}:${stockingStatus}:${pasture.stockingUsedUa || "0"}:${pasture.capacityUaTotal || "0"}`;
        if (!pastureAlertSentRef.current.has(key)) {
          pastureAlertSentRef.current.add(key);
          appendPastureNotification(pasture, {
            event: stockingStatus === "Superlotação" ? "pasture_overstock_critical" : "pasture_overstock_warning",
            eventLabel: stockingStatus,
            title: `${stockingStatus} em ${pasture.name || pasture.recordNumber || ""}`,
            summary: `UA alocada ${pasture.stockingUsedUa || "0.00"} / capacidade ${pasture.capacityUaTotal || "0.00"} UA.`,
            body: `O local ${pasture.name || pasture.recordNumber || ""} está com status ${stockingStatus.toLowerCase()}. UA alocada ${pasture.stockingUsedUa || "0.00"} de ${pasture.capacityUaTotal || "0.00"} UA disponíveis.`,
            status: stockingStatus,
          });
        }
      }
    });
  }, [appendPastureNotification, getHeightUpdateAgeDays, usesAnimalCapacity, visiblePastures]);

  function save() {
    if (form?._new && !canCreate) { show("Sem permissão para criar locais.", "error"); return; }
    if (!form?._new && !canEdit) { show("Sem permissão para editar locais.", "error"); return; }
    if (!form.name.trim()) { show("Nome do local obrigatório.", "error"); return; }
    if (!canEditCostCenter(session, "pastures", form.costCenter || allowedCostCenters[0] || COST_CENTERS[0])) { show("Sem permissão para editar este centro de custo.", "error"); return; }
    const { _new, ...d } = form;
    const existingPastureForSave = !_new ? (Array.isArray(pastures) ? pastures : []).find(item => String(item?.id) === String(d.id)) : null;
    const existingPolygonForSave = Array.isArray(existingPastureForSave?.polygon) ? existingPastureForSave.polygon : [];
    const newPolygonForSave = Array.isArray(d?.polygon) ? d.polygon : [];
    // v2.240.00: não permitir que uma edição salve polígono vazio sobre um polígono válido.
    if (!_new && existingPolygonForSave.length > 0 && newPolygonForSave.length === 0) {
      show("O mapa não pode ser salvo sem polígono. Desenhe os limites da área.", "error");
      return;
    }
    const createMeta = makeCreateMeta(session?.name || "");
    const updateMeta = makeUpdateMeta(session?.name || "");
    const computedArea = Array.isArray(d.polygon) && d.polygon.length ? computePolygonAreaHa(d.polygon) : (d.areHa || "");
    const base = {
      ...d,
      farmId: farm?.fid || d.farmId || "",
      areHa: computedArea,
      areaCalculatedAt: computedArea ? new Date().toISOString() : d.areaCalculatedAt || "",
      ...(d.useAnimalCapacity === false ? {
        capacity: "",
        capacityUaPerHa: "",
        capacityUaTotal: "",
        capacityAnimalsEq: "",
        capacityBaseUaPerHa: "",
        capacityWeedFactor: "",
        capacityHeightFactor: "",
        currentHeight: "",
        entryHeight: "",
        exitHeight: "",
        lastHeightAt: "",
        lastHeightNotes: "",
        lastUsed: "",
        lastHerbicide: "",
        herbicideDate: "",
        herbicideProduct: "",
        herbicideRecipe: "",
      } : {}),
      ...(d.useAnimalCapacity !== false ? {
        lastHeightAt: String(d.lastHeightAt || d.createdAt || new Date().toISOString()).slice(0,10),
        lastHeightNotes: String(d.lastHeightNotes || "").trim(),
      } : {}),
      ...updateMeta,
    };
    intentionalPasturesMutationRef.current = true;
    if (_new) {
      const stableId = String(d.id || d.clientId || createClientId("past"));
      const nextRecordNumber = buildNextRecordNumber(pastures || [], { moduleKey:"locations", costCenter: base.costCenter || d.costCenter || "" });
      const createdPasture = markLocationCreate(normalizePastureEntry({
        ...base,
        ...nextRecordNumber,
        recordNumber: appendProvisionalSuffix(nextRecordNumber.recordNumber),
        id: stableId,
        clientId: stableId,
        ...createMeta,
      }), session?.name || "");
      createdPasture.capacityHistory = appendPastureHistory(createdPasture, {
        source: "create",
        reason: "create",
        label: "Cadastro inicial",
        notes: createdPasture.useAnimalCapacity === false ? "Local simples criado" : "Local pecuário criado",
        snapshot: getPastureSnapshot(createdPasture),
      });
      setPasturesForLocation(p => dedupePastures([...p, createdPasture]));
    } else {
      setPasturesForLocation(p => dedupePastures(p.map(x => String(x.id) === String(d.id) ? (() => {
        const updated = markLocationUpdate(normalizePastureEntry({ ...x, ...base }), session?.name || "");
        updated.capacityHistory = appendPastureHistory(updated, {
          source: "edit",
          reason: "edit",
          label: "Cadastro atualizado",
          notes: "Campos do local atualizados manualmente",
          snapshot: getPastureSnapshot(updated),
        });
        return updated;
      })() : normalizePastureEntry(x))));
    }
    setEditingFlag?.(false);
    show("✅ Salvo!");
    setForm(null);
    syncOnSaveTracked("pastures", _new ? "user-create" : "user-save");
  }

  const saveManualOverride = React.useCallback(() => {
    if (!manualOverride?.id) return;
    if (!String(manualOverride.reason || "").trim()) { show("Informe a justificativa do ajuste manual.", "error"); return; }
    const ts = new Date().toISOString();
    const payload = {
      active: true,
      areaHa: String(manualOverride.areaHa || ""),
      currentHeight: String(manualOverride.currentHeight || ""),
      weedLevel: String(manualOverride.weedLevel || "Limpo"),
      capacityUaPerHa: String(manualOverride.capacityUaPerHa || ""),
      capacityUaTotal: String(manualOverride.capacityUaTotal || ""),
      reason: String(manualOverride.reason || "").trim(),
      by: session?.name || "",
      at: ts,
    };
    intentionalPasturesMutationRef.current = true;
    setPasturesForLocation(prev => dedupePastures((Array.isArray(prev) ? prev : []).map(item => String(item?.id) === String(manualOverride.id) ? (() => {
      const updated = markLocationUpdate(normalizePastureEntry({
        ...item,
        manualOverride: payload,
        capacityHistory: appendPastureHistory(item, {
          at: ts,
          source: "manual-override",
          reason: "manual-override",
          label: "Ajuste manual aplicado",
          notes: payload.reason,
          snapshot: {
            ...getPastureSnapshot(item),
            areHa: payload.areaHa || String(item?.areHa || ""),
            currentHeight: payload.currentHeight || String(item?.currentHeight || ""),
            weedLevel: payload.weedLevel || String(item?.weedLevel || ""),
            capacityUaPerHa: payload.capacityUaPerHa || String(item?.capacityUaPerHa || ""),
            capacityUaTotal: payload.capacityUaTotal || String(item?.capacityUaTotal || ""),
          },
        }),
        updatedAt: ts,
        updatedBy: session?.name || "",
      }), session?.name || "");
      return updated;
    })() : normalizePastureEntry(item))));
    setSelected(prev => prev && String(prev.id) === String(manualOverride.id) ? normalizePastureEntry({ ...prev, manualOverride: payload, capacityHistory: appendPastureHistory(prev, {
      at: ts,
      source: "manual-override",
      reason: "manual-override",
      label: "Ajuste manual aplicado",
      notes: payload.reason,
      snapshot: {
        ...getPastureSnapshot(prev),
        areHa: payload.areaHa || String(prev?.areHa || ""),
        currentHeight: payload.currentHeight || String(prev?.currentHeight || ""),
        weedLevel: payload.weedLevel || String(prev?.weedLevel || ""),
        capacityUaPerHa: payload.capacityUaPerHa || String(prev?.capacityUaPerHa || ""),
        capacityUaTotal: payload.capacityUaTotal || String(prev?.capacityUaTotal || ""),
      },
    }), updatedAt: ts, updatedBy: session?.name || "" }) : prev);
    if (form && !form._new && String(form.id) === String(manualOverride.id)) {
      setForm(prev => prev ? normalizePastureEntry({ ...prev, manualOverride: payload, capacityHistory: appendPastureHistory(prev, {
        at: ts,
        source: "manual-override",
        reason: "manual-override",
        label: "Ajuste manual aplicado",
        notes: payload.reason,
        snapshot: {
          ...getPastureSnapshot(prev),
          areHa: payload.areaHa || String(prev?.areHa || ""),
          currentHeight: payload.currentHeight || String(prev?.currentHeight || ""),
          weedLevel: payload.weedLevel || String(prev?.weedLevel || ""),
          capacityUaPerHa: payload.capacityUaPerHa || String(prev?.capacityUaPerHa || ""),
          capacityUaTotal: payload.capacityUaTotal || String(prev?.capacityUaTotal || ""),
        },
      }), updatedAt: ts, updatedBy: session?.name || "" }) : prev);
    }
    setManualOverride(null);
    show("✅ Ajuste manual aplicado.");
    syncOnSaveTracked("pastures", "manual-override");
  }, [appendPastureHistory, dedupePastures, form, getPastureSnapshot, manualOverride, normalizePastureEntry, session?.name, setPasturesForLocation, show, syncOnSaveTracked]);

  const restoreAutoCalculation = React.useCallback((pasture) => {
    if (!itemEditableByCostCenter(session, "pastures", pasture)) { show("Sem permissão para restaurar este local.", "error"); return; }
    const ts = new Date().toISOString();
    intentionalPasturesMutationRef.current = true;
    setPasturesForLocation(prev => dedupePastures((Array.isArray(prev) ? prev : []).map(item => String(item?.id) === String(pasture.id) ? normalizePastureEntry({
      ...item,
      manualOverride: null,
      capacityHistory: appendPastureHistory(item, {
        at: ts,
        source: "auto-restore",
        reason: "auto-restore",
        label: "Cálculo automático restaurado",
        notes: "Override manual removido",
        snapshot: getPastureSnapshot(item),
      }),
      updatedAt: ts,
      updatedBy: session?.name || "",
    }) : normalizePastureEntry(item))));
    setSelected(prev => prev && String(prev.id) === String(pasture.id) ? normalizePastureEntry({ ...prev, manualOverride: null, capacityHistory: appendPastureHistory(prev, {
      at: ts,
      source: "auto-restore",
      reason: "auto-restore",
      label: "Cálculo automático restaurado",
      notes: "Override manual removido",
      snapshot: getPastureSnapshot(prev),
    }), updatedAt: ts, updatedBy: session?.name || "" }) : prev);
    if (form && !form._new && String(form.id) === String(pasture.id)) {
      setForm(prev => prev ? normalizePastureEntry({ ...prev, manualOverride: null, capacityHistory: appendPastureHistory(prev, {
        at: ts,
        source: "auto-restore",
        reason: "auto-restore",
        label: "Cálculo automático restaurado",
        notes: "Override manual removido",
        snapshot: getPastureSnapshot(prev),
      }), updatedAt: ts, updatedBy: session?.name || "" }) : prev);
    }
    show("✅ Cálculo automático restaurado.");
    syncOnSaveTracked("pastures", "auto-restore");
  }, [appendPastureHistory, form, getPastureSnapshot, session, show, syncOnSaveTracked]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleMapChange(pasture, change) {
    if (!canEdit) return;
    intentionalPasturesMutationRef.current = true;
    if (Array.isArray(change?.polygon)) {
      const existingPolygon = Array.isArray(pasture?.polygon) ? pasture.polygon : [];
      const nextPolygon = Array.isArray(change.polygon) ? change.polygon : [];
      // v2.240.00: proteger polígonos válidos contra update vazio vindo do mapa.
      if (existingPolygon.length > 0 && nextPolygon.length === 0) {
        show?.("O mapa não pode ser salvo sem polígono. Desenhe os limites da área.", "error");
        return;
      }
      const ts = new Date().toISOString();
      const nextArea = computePolygonAreaHa(nextPolygon);
      setPasturesForLocation(p => p.map(x => String(x.id) === String(pasture.id) ? normalizePastureEntry({
        ...x,
        polygon: nextPolygon,
        areHa: nextArea,
        areaCalculatedAt: nextArea ? ts : x.areaCalculatedAt || "",
        capacityHistory: appendPastureHistory(x, {
          at: ts,
          source: "map-change",
          reason: "map-change",
          label: "Polígono atualizado",
          notes: nextArea ? `Área recalculada para ${nextArea} ha` : "Polígono atualizado",
          snapshot: { ...getPastureSnapshot(x), areHa: String(nextArea || "") },
        }),
        updatedAt: ts,
        updatedBy: session?.name || "",
      }) : x));
      setSelected(s => s ? normalizePastureEntry({
        ...s,
        polygon: nextPolygon,
        areHa: nextArea,
        areaCalculatedAt: nextArea ? ts : s.areaCalculatedAt || "",
        updatedAt: ts,
        updatedBy: session?.name || "",
      }) : s);
      syncOnSaveTracked("pastures", "user-map-change");
    }
    if (change.newPoi) {
      onOpenPOI?.({
        pastureIds: [pasture.id],
        costCenter: pasture?.costCenter || allowedCostCenters[0] || COST_CENTERS[0],
        pastureName: pasture.name || "",
        lat: change.newPoi.lat,
        lng: change.newPoi.lng,
      });
    }
  }

  function openPoiModule(pastureId) {
    const pasture = visiblePastures.find(p=>p.id===pastureId);
    onOpenPOI?.({
      pastureIds: [pastureId],
      costCenter: pasture?.costCenter || allowedCostCenters[0] || COST_CENTERS[0],
      pastureName: pasture?.name || "",
    });
  }

  // Services for a pasture
  function pastureServices(pastureId) {
    return services.filter(s => s.pastureId === pastureId).filter(s=>itemMatchesCostCenterAccess(session, "services", s)).sort((a,b) => (b.dateStart||"").localeCompare(a.dateStart||""));
  }


  return (
    <div><Toast toast={toast}/>
      {!!deniedCostCenters.length && <div style={{background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.text}}>⚠️ Sem acesso a: <strong>{deniedCostCenters.join(", ")}</strong>. Esses centros ficam ocultos neste módulo.</div>}
      {/* Tabs */}
      {!hideHeaderMenu && <ModuleHeaderMenu
        T={T}
        title="Menu de Localização"
        items={[
          {
            id:"location-group",
            label:"Localização",
            icon:"🗺️",
            active:["overview","list"].includes(tab),
            children:[
              { id:"overview", label:"Mapa Geral", icon:"🗺️", active:tab==="overview", onClick:()=>{ setTab("overview"); setSelected(null); } },
              { id:"list", label:"Lugares", icon:"🌿", active:tab==="list", onClick:()=>setTab("list") },
            ],
          },
          {
            id:"poi-direct",
            label:"Pontos de Interesse",
            icon:"📍",
            onClick:()=>onOpenPOI?.({ costCenter:allowedCostCenters[0] || COST_CENTERS[0] }),
          },
        ]}
      />}


      {/* ── OVERVIEW MAP TAB ── */}
      {tab==="overview"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:20}}>
            {[
              { id:"pastos", icon:<GrassClumpIcon size={34} />, label:"Locais", value:visiblePastures.length, color:T.green, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{width:46,height:46} },
              { id:"area_total", icon:"📐", label:"Área Total", value:`${visiblePastures.reduce((s,p)=>s+(parseFloat(p.areHa)||0),0).toFixed(1)} ha`, color:T.accent, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:28} },
              { id:"pontos_interesse", icon:"📍", label:"Pontos de Interesse", value:visiblePois.length, color:T.purple, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:28} },
              { id:"capacidade_animais", icon:<CattleIcon size={28} />, label:"Cap. Total", value:`${visiblePastures.reduce((s,p)=>s+(parseFloat(p.capacityUaTotal)||0),0).toFixed(2)} UA`, color:T.yellow, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{width:42,height:42} },
            ].filter(card=>allowedOverviewCards.includes(card.id)).map(card=>(
              <StatCard key={card.id} icon={card.icon} label={card.label} value={card.value} color={card.color} iconWrapStyle={card.iconWrapStyle} iconStyle={card.iconStyle}/>
            ))}
          </div>

          {/* Legend */}
          {visiblePastures.length>0&&(
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14,padding:"10px 14px",background:T.card,border:`1px solid ${T.border}`,borderRadius:10}}>
              <span style={{fontSize:12,color:T.muted,alignSelf:"center",marginRight:4}}>Legenda:</span>
              {visiblePastures.map(p=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:T.text,cursor:"pointer",padding:"4px 8px",borderRadius:6,border:`1px solid ${p.color||PASTURE_COLORS[0]}55`,background:`${p.color||PASTURE_COLORS[0]}18`}}
                  onClick={()=>{setSelected(p);setTab("list");}}>
                  <div style={{width:10,height:10,borderRadius:2,background:p.color||PASTURE_COLORS[0]}}/>
                  {p.name}
                </div>
              ))}
              {visiblePois.length>0&&<div style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:T.purple}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:T.purple}}/>
                Pontos de Interesse ({visiblePois.length})
              </div>}
            </div>
          )}

          <OverviewMap
            apiKey={apiKey}
            pastures={visiblePastures}
            pois={visiblePois}
            onSelectPasture={p=>{setSelected(p);setTab("list");}}
          />
          {visiblePastures.length===0&&apiKey&&(
            <div style={{textAlign:"center",color:T.muted,fontSize:13,marginTop:16}}>
              Nenhum lugar cadastrado ainda. Vá em 🌿 Lugares e clique em "+ Novo Lugar".
            </div>
          )}
        </div>
      )}

      {/* ── LIST TAB ── */}
      {tab==="list"&&(<>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:20}}>
          {[
            { id:"pastos", icon:<GrassClumpIcon size={34} />, label:"Locais", value:visiblePastures.length, color:T.green, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{width:46,height:46} },
            { id:"area_total", icon:"📐", label:"Área Total", value:`${visiblePastures.reduce((s,p)=>s+(parseFloat(p.areHa)||0),0).toFixed(1)} ha`, color:T.accent, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:28} },
            { id:"capacidade_animais", icon:<CattleIcon size={28} />, label:"Cap. Total", value:`${visiblePastures.reduce((s,p)=>s+(parseFloat(p.capacityUaTotal)||0),0).toFixed(2)} UA`, color:T.yellow, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{width:42,height:42} },
            { id:"sujos_muito_sujos", icon:<WeedGrassClumpIcon size={34} />, label:"Sujos/Muito Sujos", value:visiblePastures.filter(p=>p.weedLevel==="Sujo"||p.weedLevel==="Muito Sujo").length, color:T.red, iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{width:46,height:46} },
          ].filter(card=>allowedListCards.includes(card.id)).map(card=>(
            <StatCard key={card.id} icon={card.icon} label={card.label} value={card.value} color={card.color} iconWrapStyle={card.iconWrapStyle} iconStyle={card.iconStyle}/>
          ))}
        </div>

        <div style={{background:T.card,border:`1px solid ${listClimateOverview.impactMeta.color}33`,borderRadius:16,padding:16,marginBottom:18,display:"grid",gap:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>ZIP 11 · CLIMA + LOCALIZAÇÃO</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Condição Climática das Pastagens</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <Badge color={listClimateOverview.impactMeta.color}>{`${listClimateOverview.impactMeta.icon} ${listClimateOverview.impactMeta.label}`}</Badge>
              <Badge color={listClimateOverview.trendMeta.label === 'Piorando' ? T.red : (listClimateOverview.trendMeta.label === 'Melhorando' ? T.green : T.blue)}>{`Tendência ${listClimateOverview.trendMeta.label}`}</Badge>
            </div>
          </div>
          <div style={{fontSize:13,color:T.text,lineHeight:1.55}}>{listClimateOverview.summaryLabel}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
            <StatCard icon="🌿" label="Monitoradas" value={`${listClimateOverview.monitoredCount}`} color={listClimateOverview.impactMeta.color}/>
            <StatCard icon="☁️" label="Fator Clima" value={listClimateOverview.factorLabel} color={T.accent}/>
            <StatCard icon="🐮" label="Cap. Ajustada" value={`${listClimateOverview.adjustedUaTotal.toFixed(2)} UA`} color={T.yellow}/>
            <StatCard icon="🏗️" label="Base Estrutural" value={`${listClimateOverview.baseUaTotal.toFixed(2)} UA`} color={T.blue}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10,fontSize:12}}>
            <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}>
              <div style={{color:T.muted,marginBottom:4}}>Fonte usada</div>
              <div style={{color:T.text,fontWeight:700}}>{listClimateOverview.sourceLabel}</div>
            </div>
            <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}>
              <div style={{color:T.muted,marginBottom:4}}>Última leitura banco</div>
              <div style={{color:T.text,fontWeight:700}}>{listClimateOverview.observedLabel}</div>
            </div>
          </div>
        </div>

        {!selected&&(<>
          {canCreate&&<div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
            <Btn onClick={()=>setForm({...defForm})}>+ Novo Lugar</Btn>
          </div>}
          {form?._new && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:16,marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Novo Lugar</div>
              <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
            </div>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
              <Field label="Nome do Local *" col="1/-1"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></Field>
              <Field label="Cor no Mapa" col="1/-1">
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:4,alignItems:"center"}}>
                  {PASTURE_COLORS.map((c,i)=>(
                    <button key={c} type="button" title={PASTURE_COLOR_NAMES[i]} onClick={()=>setForm(f=>({...f,color:c}))}
                      style={{width:30,height:30,borderRadius:6,background:c,border:`3px solid ${form.color===c?"#fff":"transparent"}`,
                      cursor:"pointer",boxShadow:form.color===c?`0 0 0 2px ${c}`:"none",transition:"all .15s"}}/>
                  ))}
                  <span style={{fontSize:12,color:T.muted,marginLeft:6}}>{PASTURE_COLOR_NAMES[PASTURE_COLORS.indexOf(form.color||PASTURE_COLORS[0])]||"Personalizado"}</span>
                </div>
              </Field>
              <Field label="Tipo de Pastagem / Cobertura"><Select value={form.grassType} onChange={e=>setForm(f=>normalizePastureEntry({...f,grassType:e.target.value}))}>{(SL.GRASS_TYPES||GRASS_TYPES).map(g=><option key={g}>{g}</option>)}</Select><div style={{fontSize:11,color:T.muted,marginTop:6}}>Entrada padrão: {computePastureCapacity(form, pastureCapacityConfig)?.entryHeight||"—"} cm • Saída padrão: {computePastureCapacity(form, pastureCapacityConfig)?.exitHeight||"—"} cm</div></Field>
              <Field label="Modo do Local">
                <label style={{display:"flex",alignItems:"center",gap:10,minHeight:42,color:T.text,fontWeight:600,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!form.useAnimalCapacity} onChange={e=>setForm(f=>({...f,useAnimalCapacity:e.target.checked}))} />
                  <span>Usar cálculo de capacidade animal neste local</span>
                </label>
                <div style={{fontSize:11,color:T.muted,marginTop:6}}>
                  Desmarque para cadastrar apenas a área do local, como gramado ou outra cobertura sem manejo pecuário.
                </div>
              </Field>
              {form.useAnimalCapacity && <>
                <Field label="Nível de Invasoras"><Select value={form.weedLevel} onChange={e=>setForm(f=>normalizePastureEntry({...f,weedLevel:e.target.value}))}>{(SL.WEED_LEVELS||WEED_LEVELS).map(w=><option key={w}>{w}</option>)}</Select></Field>
                <Field label="Altura Atual (cm)"><Input type="number" value={form.currentHeight} onChange={e=>setForm(f=>normalizePastureEntry({...f,currentHeight:e.target.value}))}/></Field>
              </>}
              <Field label="Latitude GPS"><Input value={form.lat} onChange={e=>setForm(f=>({...f,lat:e.target.value}))} placeholder="-15.8000"/></Field>
              <Field label="Longitude GPS"><Input value={form.lng} onChange={e=>setForm(f=>({...f,lng:e.target.value}))} placeholder="-47.9000"/></Field>
              {apiKey&&<Field label="🗺️ Mapa — Desenhe o Polígono" col="1/-1">
                <div style={{marginBottom:4,fontSize:11,color:T.muted}}>
                  Use o botão <strong style={{color:T.text}}>✏️ polígono</strong> no topo do mapa para marcar a área do local. A área em hectares é calculada automaticamente ao terminar de desenhar.
                </div>
                <GoogleMapPicker
                  apiKey={apiKey}
                  lat={parseFloat(form.lat)||undefined}
                  lng={parseFloat(form.lng)||undefined}
                  polygon={form.polygon||[]}
                  color={form.color||PASTURE_COLORS[0]}
                  pois={[]}
                  onChange={change=>{
                    if(change.polygon) setForm(f=>normalizePastureEntry(syncPolygonArea({ ...f, polygon: change.polygon })));
                    if(change.newPoi && change.newPoi.lat) {
                      setForm(f=>({...f,lat:String(change.newPoi.lat.toFixed(6)),lng:String(change.newPoi.lng.toFixed(6))}));
                    }
                  }}
                />
                {(form.polygon||[]).length>0&&(
                  <div style={{marginTop:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:11,color:T.green}}>✅ Polígono com {form.polygon.length} pontos desenhado</span>
                    {canEdit&&<button type="button" onClick={()=>setForm(f=>normalizePastureEntry(syncPolygonArea({ ...f, polygon: [] })))} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,padding:"3px 10px",color:T.red,fontSize:11,cursor:"pointer"}}>🗑️ Apagar polígono</button>}
                  </div>
                )}
              </Field>}
              <Field label="Área (ha)">
                <Input type="number" step="0.01" value={form.areHa} readOnly disabled />
                <div style={{fontSize:11,color:T.muted,marginTop:6}}>Valor calculado automaticamente pelo polígono desenhado no mapa.</div>
              </Field>
              {form.useAnimalCapacity && <>
                <Field label="Capacidade Base (UA/ha)"><Input type="number" step="0.01" value={form.capacityBaseUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Definida na tabela de pastagens do Admin.</div></Field>
                <Field label="Capacidade Estrutural (UA/ha)"><Input type="number" step="0.01" value={form.capacityStructuralUaPerHa||form.autoCalculated?.capacityUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Base por pastagem + invasoras + altura.</div></Field>
                <Field label="Capacidade Atual Climática (UA/ha)"><Input type="number" step="0.01" value={form.capacityUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Ajustada pelo fator climático da fazenda.</div></Field>
                <Field label="Capacidade Atual (UA)"><Input type="number" step="0.01" value={form.capacityUaTotal||""} readOnly disabled /></Field>
                <Field label="Fator climático"><Input type="text" value={form.capacityClimateFactor?`${form.capacityClimateFactor}x`:""} readOnly disabled /></Field>
                <Field label="Altura de Entrada (cm)"><Input type="number" value={form.entryHeight} onChange={e=>setForm(f=>({...f,entryHeight:e.target.value}))}/></Field>
                <Field label="Altura de Saída (cm)"><Input type="number" value={form.exitHeight} onChange={e=>setForm(f=>({...f,exitHeight:e.target.value}))}/></Field>
                <Field label="Último Uso"><Input type="date" value={form.lastUsed} onChange={e=>setForm(f=>({...f,lastUsed:e.target.value}))}/></Field>
                <Field label="Última Aplic. Herbicida"><Input type="date" value={form.herbicideDate} onChange={e=>setForm(f=>({...f,herbicideDate:e.target.value}))}/></Field>
                <Field label="Produto (Herbicida)">
                  <Select value={form.herbicideProduct} onChange={e=>setForm(f=>({...f,herbicideProduct:e.target.value}))}>
                    <option value="">Selecione do estoque...</option>
                    {herbProducts.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                    <option value="__manual">Digitar manualmente</option>
                  </Select>
                  {form.herbicideProduct==="__manual"&&<Input style={{marginTop:6}} placeholder="Nome do herbicida" value={form.lastHerbicide} onChange={e=>setForm(f=>({...f,lastHerbicide:e.target.value}))}/>}
                </Field>
                <Field label="Receituário de Aplicação" col="1/-1">
                  <Textarea rows={3} value={form.herbicideRecipe} onChange={e=>setForm(f=>({...f,herbicideRecipe:e.target.value}))} placeholder="Dose, diluição, forma de aplicação, responsável técnico, ART..."/>
                </Field>
              </>}
              <Field label="Saldo Inicial" col="1/-1">
                <Input type="number" min="0" step="0.001" value={form.initialQty||0}
                  onChange={e=>setForm(f=>({...f,initialQty:e.target.value}))}
                  placeholder="Quantidade já existente em estoque (0 se novo)"/>
              </Field>
              <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
              <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}>
                <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
                {canCreate&&<Btn onClick={save}>Salvar</Btn>}
              </div>
            </div>
          </div>}
          {(() => {
            const focusedPastureId = form && !form._new ? String(form.id||"") : "";
            const listPastures = focusedPastureId
              ? visiblePastures.filter(item=>String(item.id)===focusedPastureId)
              : visiblePastures;
            return <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14}}>
            {listPastures.map(p=>{
              const svcs = pastureServices(p.id);
              const editingThis = form && !form._new && String(form.id)===String(p.id);
              const climateOperationalMeta = usesAnimalCapacity(p) ? buildPastureClimateOperationalMeta(p) : null;
              return(
                <React.Fragment key={p.id}>
                <div key={p.id} style={{background:T.card,border:`1px solid ${editingThis?(p.color||T.green):T.border}`,borderRadius:14,overflow:"hidden",cursor:"pointer",transition:"all .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=p.color||T.green;e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=editingThis?(p.color||T.green):T.border;e.currentTarget.style.transform="translateY(0)";}}>
                  <div style={{background:`${(p.color||T.green)}18`,borderBottom:`1px solid ${(p.color||T.green)}33`,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}
                    onClick={()=>setSelected(p)}>
                    <div>
                      <div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayLocationRecordNumber(p)}</div><div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,color:T.text}}>{p.name}</div>
                      <div style={{fontSize:12,color:T.muted,marginTop:2}}>{p.grassType} · {p.areHa||"—"} ha</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                      <Badge color={usesAnimalCapacity(p) ? weedColor(p.weedLevel) : T.accent}>{usesAnimalCapacity(p) ? (p.weedLevel||"—") : "Área simples"}</Badge>
                      <span style={{fontSize:10,color:T.muted,fontWeight:700}}>{getPastureModeLabel(p)}</span>
                      {usesAnimalCapacity(p)&&<span style={{fontSize:10,color:T.muted}}>{p.capacityUaPerHa||getClimateAdjustedMetrics(p)?.uaPerHa||"—"} UA/ha</span>}
                    </div>
                  </div>
                  <div style={{padding:"12px 16px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
                    <div><span style={{color:T.muted}}>Área: </span><span style={{color:T.text,fontWeight:600}}>{p.areHa||"—"} ha</span></div>
                    <div><span style={{color:T.muted}}>Modo: </span><span style={{color:T.text,fontWeight:600}}>{getPastureModeLabel(p)}</span></div>
                    {usesAnimalCapacity(p) ? <>
                      <div><span style={{color:T.muted}}>Capacidade: </span><span style={{color:T.text,fontWeight:600}}>{p.capacityUaTotal||getClimateAdjustedMetrics(p)?.uaTotal||"—"} UA</span></div>
                      <div><span style={{color:T.muted}}>UA/ha: </span><span style={{color:T.text,fontWeight:600}}>{p.capacityUaPerHa||getClimateAdjustedMetrics(p)?.uaPerHa||"—"}</span></div>
                      <div><span style={{color:T.muted}}>UA alocada: </span><span style={{color:T.text,fontWeight:600}}>{p.stockingUsedUa||"0.00"} UA</span></div>
                      <div><span style={{color:T.muted}}>Saldo UA: </span><span style={{color:T.text,fontWeight:600}}>{p.stockingAvailableUa||"0.00"} UA</span></div>
                      <div><span style={{color:T.muted}}>Altura atual: </span><span style={{color:T.text,fontWeight:600}}>{p.currentHeight||"—"} cm</span></div>
                      <div><span style={{color:T.muted}}>Última medição: </span><span style={{color:T.text,fontWeight:600}}>{p.lastHeightAt?fmtDate(p.lastHeightAt):"—"}</span></div>
                      <div><span style={{color:T.muted}}>Entrada: </span><span style={{color:T.green,fontWeight:600}}>{p.entryHeight||"—"} cm</span></div>
                      <div><span style={{color:T.muted}}>Saída: </span><span style={{color:T.red,fontWeight:600}}>{p.exitHeight||"—"} cm</span></div>
                      <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:`${getHeightStatusMeta(p).color}14`,border:`1px solid ${getHeightStatusMeta(p).color}33`}}>
                        <span style={{width:10,height:10,borderRadius:999,background:getHeightStatusMeta(p).color,display:"inline-block"}} />
                        <span style={{color:T.text,fontWeight:700}}>Altura Pastagem</span>
                        <span style={{color:getHeightStatusMeta(p).color,fontWeight:700,marginLeft:"auto"}}>{getHeightStatusMeta(p).label}</span>
                      </div>
                      <div style={{gridColumn:"1/-1",fontSize:11,color:T.muted}}>{getHeightStatusMeta(p).hint}</div>
                      <div style={{gridColumn:"1/-1",display:"grid",gap:8,padding:"10px 12px",borderRadius:12,background:`${climateOperationalMeta?.impactMeta?.color || T.accent}14`,border:`1px solid ${climateOperationalMeta?.impactMeta?.color || T.accent}33`}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:12,color:T.text,fontWeight:800}}>🌦️ Condição Climática</span>
                          <Badge color={climateOperationalMeta?.impactMeta?.color || T.accent}>{climateOperationalMeta ? `${climateOperationalMeta.impactMeta.icon} ${climateOperationalMeta.impactMeta.label}` : 'Sem leitura'}</Badge>
                          <Badge color={climateOperationalMeta?.trendMeta?.direction === 'worsening' ? T.red : (climateOperationalMeta?.trendMeta?.direction === 'improving' ? T.green : T.blue)}>{`Tendência ${climateOperationalMeta?.trendMeta?.label || 'Estável'}`}</Badge>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
                          <div><span style={{color:T.muted}}>Cap. climática: </span><span style={{color:T.text,fontWeight:700}}>{climateOperationalMeta ? `${climateOperationalMeta.adjustedUa.toFixed(2)} UA` : `${p.capacityUaTotal||"—"} UA`}</span></div>
                          <div><span style={{color:T.muted}}>Base estrutural: </span><span style={{color:T.text,fontWeight:700}}>{climateOperationalMeta ? `${climateOperationalMeta.baseUa.toFixed(2)} UA` : `${p.capacityStructuralUaTotal||"—"} UA`}</span></div>
                          <div><span style={{color:T.muted}}>Fator: </span><span style={{color:T.text,fontWeight:700}}>{climateOperationalMeta?.factorLabel || `${p.capacityClimateFactor||"1.00"}x`}</span></div>
                          <div><span style={{color:T.muted}}>Leitura banco: </span><span style={{color:T.text,fontWeight:700}}>{climateOperationalMeta?.observedLabel || '—'}</span></div>
                        </div>
                        <div style={{fontSize:11,color:T.muted}}>{climateOperationalMeta?.summaryLabel || p.capacityClimateSummary||farmClimateMeta.climateSummary||farmClimateMeta.summary}</div>
                      </div>
                      <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:999,background:`${getStockingBalloonMeta(p).color}14`,border:`1px solid ${getStockingBalloonMeta(p).color}33`}}><span style={{width:10,height:10,borderRadius:999,background:getStockingBalloonMeta(p).color,display:"inline-block"}} /><span style={{color:getStockingBalloonMeta(p).color,fontWeight:800,marginLeft:"auto"}}>{getStockingBalloonMeta(p).label}</span></div>
                      <div style={{gridColumn:"1/-1",fontSize:11,color:T.muted}}>UA usada {p.stockingUsedUa||"0.00"} · UA disponível {p.stockingAvailableUa||"0.00"} · {p.stockingHeadCount||0} animal(is) em {p.stockingLotsCount||0} lote(s)</div>
                      <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:`${getPastureAttentionState(p).color}14`,border:`1px solid ${getPastureAttentionState(p).color}33`}}><span style={{width:10,height:10,borderRadius:999,background:getPastureAttentionState(p).color,display:"inline-block"}} /><span style={{color:T.text,fontWeight:700}}>Status operacional</span><span style={{marginLeft:"auto",color:getPastureAttentionState(p).color,fontWeight:700}}>{getPastureAttentionState(p).label}</span></div>
                      <div style={{gridColumn:"1/-1",fontSize:11,color:T.muted}}>🧭 {getPastureAttentionState(p).recommendation}</div>
                      <div style={{gridColumn:"1/-1",display:"flex",gap:8,flexWrap:"wrap"}}>
                        <Badge color={getHeightStatusMeta(p).color}>Altura {getHeightStatusMeta(p).label}</Badge>
                        <Badge color={getStockingBalloonMeta(p).color}>{getStockingBalloonMeta(p).label}</Badge>
                        <Badge color={p.manualOverride?.active ? T.purple : T.accent}>{p.manualOverride?.active ? "Manual" : "Auto/Clima"}</Badge>
                      </div>
                      {p.manualOverride?.active && <div style={{gridColumn:"1/-1",fontSize:11,color:T.purple,fontWeight:700}}>🛠️ Ajuste manual ativo • {p.manualOverride?.reason || "sem justificativa"}</div>}
                    </> : <div style={{gridColumn:"1/-1",color:T.muted}}>Este local está em modo simples e não participa do cálculo pecuário.</div>}
                    {svcs.length>0&&<div style={{gridColumn:"1/-1",color:T.accent,fontSize:11}}>🔨 {svcs.length} serviço(s) registrado(s)</div>}
                    {(p.pois||[]).length>0&&<div style={{gridColumn:"1/-1",color:T.muted,fontSize:11}}>📍 {p.pois.length} ponto(s) de interesse</div>}
                  </div>
                  <div style={{padding:"8px 16px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8,flexWrap:"wrap"}}>
                    <Btn size="sm" variant="ghost" onClick={()=>setSelected(p)}>📋 Detalhes</Btn>
                    {usesAnimalCapacity(p) && canEdit && <Btn size="sm" onClick={()=>openHeightUpdate(p)} style={{background:getHeightStatusMeta(p).color,borderColor:getHeightStatusMeta(p).color,color:"#fff"}}>📏 Altura Pastagem</Btn>}
                    {usesAnimalCapacity(p) && canEdit && <Btn size="sm" variant="ghost" onClick={()=>openManualOverride(p)}>🛠️ Ajuste Manual</Btn>}
                    {usesAnimalCapacity(p) && p.manualOverride?.active && canEdit && <Btn size="sm" variant="ghost" onClick={()=>restoreAutoCalculation(p)}>♻️ Auto</Btn>}
                    {canEdit&&<Btn size="sm" variant="ghost" onClick={()=>itemEditableByCostCenter(session, "pastures", p) ? setForm({...p,_new:false}) : show("Sem permissão para editar este centro de custo.","error")}>✏️ Editar</Btn>}
                    {canEdit&&<Btn size="sm" variant="danger" onClick={()=>deletePastureRecord(p)}>🗑️</Btn>}
                  </div>
                </div>
                {editingThis && <div key={`${p.id}-edit`} style={{gridColumn:"1/-1",background:T.card,border:`1px solid ${p.color||T.green}`,borderRadius:16,padding:16}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Editar Lugar</div>
                    <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
                    <Field label="Nome do Local *" col="1/-1"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></Field>
                    <Field label="Cor no Mapa" col="1/-1">
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:4,alignItems:"center"}}>
                        {PASTURE_COLORS.map((c,i)=>(
                          <button key={c} type="button" title={PASTURE_COLOR_NAMES[i]} onClick={()=>setForm(f=>({...f,color:c}))}
                            style={{width:30,height:30,borderRadius:6,background:c,border:`3px solid ${form.color===c?"#fff":"transparent"}`,
                            cursor:"pointer",boxShadow:form.color===c?`0 0 0 2px ${c}`:"none",transition:"all .15s"}}/>
                        ))}
                        <span style={{fontSize:12,color:T.muted,marginLeft:6}}>{PASTURE_COLOR_NAMES[PASTURE_COLORS.indexOf(form.color||PASTURE_COLORS[0])]||"Personalizado"}</span>
                      </div>
                    </Field>
                    <Field label="Tipo de Pastagem / Cobertura"><Select value={form.grassType} onChange={e=>setForm(f=>normalizePastureEntry({...f,grassType:e.target.value}))}>{(SL.GRASS_TYPES||GRASS_TYPES).map(g=><option key={g}>{g}</option>)}</Select><div style={{fontSize:11,color:T.muted,marginTop:6}}>Entrada padrão: {computePastureCapacity(form, pastureCapacityConfig)?.entryHeight||"—"} cm • Saída padrão: {computePastureCapacity(form, pastureCapacityConfig)?.exitHeight||"—"} cm</div></Field>
                    <Field label="Modo do Local">
                      <label style={{display:"flex",alignItems:"center",gap:10,minHeight:42,color:T.text,fontWeight:600,cursor:"pointer"}}>
                        <input type="checkbox" checked={!!form.useAnimalCapacity} onChange={e=>setForm(f=>({...f,useAnimalCapacity:e.target.checked}))} />
                        <span>Usar cálculo de capacidade animal neste local</span>
                      </label>
                    </Field>
                    {form.useAnimalCapacity && <>
                      <Field label="Nível de Invasoras"><Select value={form.weedLevel} onChange={e=>setForm(f=>normalizePastureEntry({...f,weedLevel:e.target.value}))}>{(SL.WEED_LEVELS||WEED_LEVELS).map(w=><option key={w}>{w}</option>)}</Select></Field>
                      <Field label="Altura Atual (cm)"><Input type="number" value={form.currentHeight} onChange={e=>setForm(f=>normalizePastureEntry({...f,currentHeight:e.target.value}))}/></Field>
                    </>}
                    <Field label="Latitude GPS"><Input value={form.lat} onChange={e=>setForm(f=>({...f,lat:e.target.value}))} placeholder="-15.8000"/></Field>
                    <Field label="Longitude GPS"><Input value={form.lng} onChange={e=>setForm(f=>({...f,lng:e.target.value}))} placeholder="-47.9000"/></Field>
                    {apiKey&&<Field label="🗺️ Mapa — Desenhe o Polígono" col="1/-1">
                      <div style={{marginBottom:4,fontSize:11,color:T.muted}}>
                        Use o botão <strong style={{color:T.text}}>✏️ polígono</strong> no topo do mapa para marcar a área do local. A área em hectares é calculada automaticamente ao terminar de desenhar.
                      </div>
                      <GoogleMapPicker
                        apiKey={apiKey}
                        lat={parseFloat(form.lat)||undefined}
                        lng={parseFloat(form.lng)||undefined}
                        polygon={form.polygon||[]}
                        color={form.color||PASTURE_COLORS[0]}
                        pois={[]}
                        onChange={change=>{
                          if(change.polygon) setForm(f=>normalizePastureEntry(syncPolygonArea({ ...f, polygon: change.polygon })));
                          if(change.newPoi && change.newPoi.lat) {
                            setForm(f=>({...f,lat:String(change.newPoi.lat.toFixed(6)),lng:String(change.newPoi.lng.toFixed(6))}));
                          }
                        }}
                      />
                      {(form.polygon||[]).length>0&&(
                        <div style={{marginTop:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:11,color:T.green}}>✅ Polígono com {form.polygon.length} pontos desenhado</span>
                          {canEdit&&<button type="button" onClick={()=>setForm(f=>normalizePastureEntry(syncPolygonArea({ ...f, polygon: [] })))} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,padding:"3px 10px",color:T.red,fontSize:11,cursor:"pointer"}}>🗑️ Apagar polígono</button>}
                        </div>
                      )}
                    </Field>}
                    <Field label="Área (ha)"><Input type="number" step="0.01" value={form.areHa} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Valor calculado automaticamente pelo polígono desenhado no mapa.</div></Field>
                    {form.useAnimalCapacity && <>
                      <Field label="Capacidade Base (UA/ha)"><Input type="number" step="0.01" value={form.capacityBaseUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Definida na tabela de pastagens do Admin.</div></Field>
                <Field label="Capacidade Estrutural (UA/ha)"><Input type="number" step="0.01" value={form.capacityStructuralUaPerHa||form.autoCalculated?.capacityUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Base por pastagem + invasoras + altura.</div></Field>
                <Field label="Capacidade Atual Climática (UA/ha)"><Input type="number" step="0.01" value={form.capacityUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Ajustada pelo fator climático da fazenda.</div></Field>
                <Field label="Capacidade Atual (UA)"><Input type="number" step="0.01" value={form.capacityUaTotal||""} readOnly disabled /></Field>
                <Field label="Fator climático"><Input type="text" value={form.capacityClimateFactor?`${form.capacityClimateFactor}x`:""} readOnly disabled /></Field>
                      <Field label="Altura de Entrada (cm)"><Input type="number" value={form.entryHeight} onChange={e=>setForm(f=>({...f,entryHeight:e.target.value}))}/></Field>
                      <Field label="Altura de Saída (cm)"><Input type="number" value={form.exitHeight} onChange={e=>setForm(f=>({...f,exitHeight:e.target.value}))}/></Field>
                      <Field label="Último Uso"><Input type="date" value={form.lastUsed} onChange={e=>setForm(f=>({...f,lastUsed:e.target.value}))}/></Field>
                      <Field label="Última Aplic. Herbicida"><Input type="date" value={form.herbicideDate} onChange={e=>setForm(f=>({...f,herbicideDate:e.target.value}))}/></Field>
                      <Field label="Produto (Herbicida)">
                        <Select value={form.herbicideProduct} onChange={e=>setForm(f=>({...f,herbicideProduct:e.target.value}))}>
                          <option value="">Selecione do estoque...</option>
                          {herbProducts.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                          <option value="__manual">Digitar manualmente</option>
                        </Select>
                        {form.herbicideProduct==="__manual"&&<Input style={{marginTop:6}} placeholder="Nome do herbicida" value={form.lastHerbicide} onChange={e=>setForm(f=>({...f,lastHerbicide:e.target.value}))}/>}
                      </Field>
                      <Field label="Receituário de Aplicação" col="1/-1">
                        <Textarea rows={3} value={form.herbicideRecipe} onChange={e=>setForm(f=>({...f,herbicideRecipe:e.target.value}))} placeholder="Dose, diluição, forma de aplicação, responsável técnico, ART..."/>
                      </Field>
                    </>}
                    <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
                    <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}>
                      {canDelete&&!form._new&&<Btn variant="danger" onClick={()=>deletePastureRecord(form, { skipConfirm:true })}>Excluir</Btn>}
                      <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
                      {canEdit&&<Btn onClick={save}>Salvar</Btn>}
                    </div>
                  </div>
                </div>}
                </React.Fragment>
              );
            })}
            {listPastures.length===0&&<div style={{color:T.muted,padding:40,textAlign:"center",gridColumn:"1/-1"}}>Nenhum lugar cadastrado. Clique em "+ Novo Lugar" para começar.</div>}
          </div>;
          })()}
        </>)}

        {/* ── PASTURE DETAIL ── */}
        {selected&&(()=>{
          const svcs = pastureServices(selected.id);
          const capacity = calcCapacityMetrics(selected);
          const selectedClimateSummary = normalizeClimateDatabaseSummary(pastureClimateSummaryMap[String(selected?.id || "")] || climateDbSummary);
          const selectedClimateHistory = Array.isArray(selectedClimateSummary.calculationHistory) ? selectedClimateSummary.calculationHistory : [];
          return(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:20}}>←</button>
                <div><div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>{displayLocationRecordNumber(selected)}</div><div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,color:T.text}}>{`${selected.name}${getPoiFarmName(selected)?` - ${getPoiFarmName(selected)}`:""}`}</div></div>
                {usesAnimalCapacity(selected)
                  ? <>
                      <Badge color={weedColor(selected.weedLevel)}>{selected.weedLevel}</Badge>
                      <Badge color={getHeightStatusMeta(selected).color}>Altura {getHeightStatusMeta(selected).label}</Badge>
                      <Badge color={getStockingBalloonMeta(selected).color}>{getStockingBalloonMeta(selected).label}</Badge>
                      <Badge color={getPastureAttentionState(selected).color}>{getPastureAttentionState(selected).label}</Badge>
                    </>
                  : <Badge color={T.accent}>Área simples</Badge>}
                <div style={{marginLeft:"auto",display:"flex",gap:8,flexWrap:"wrap"}}>
                  {usesAnimalCapacity(selected) && canEdit && <Btn size="sm" onClick={()=>openHeightUpdate(selected)} style={{background:getHeightStatusMeta(selected).color,borderColor:getHeightStatusMeta(selected).color,color:"#fff"}}>📏 Altura Pastagem</Btn>}
                  {usesAnimalCapacity(selected) && canEdit && <Btn size="sm" variant="ghost" onClick={()=>openManualOverride(selected)}>🛠️ Ajuste Manual</Btn>}
                  {usesAnimalCapacity(selected) && selected.manualOverride?.active && canEdit && <Btn size="sm" variant="ghost" onClick={()=>restoreAutoCalculation(selected)}>♻️ Restaurar Auto</Btn>}
                  {canEdit&&<Btn size="sm" onClick={()=>setForm({...selected,_new:false})}>✏️ Editar</Btn>}
                  <Btn size="sm" variant="ghost" onClick={()=>openPoiModule(selected.id)}>+ Ponto de Interesse</Btn>
                </div>
              </div>

              {form && !form._new && String(form.id)===String(selected.id) && <div style={{background:T.card,border:`1px solid ${selected.color||T.green}`,borderRadius:16,padding:16,marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Editar Lugar</div>
                  <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
                </div>
                <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
                  <Field label="Nome do Local *" col="1/-1"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></Field>
                  <Field label="Tipo de Pastagem / Cobertura"><Select value={form.grassType} onChange={e=>setForm(f=>normalizePastureEntry({...f,grassType:e.target.value}))}>{(SL.GRASS_TYPES||GRASS_TYPES).map(g=><option key={g}>{g}</option>)}</Select><div style={{fontSize:11,color:T.muted,marginTop:6}}>Entrada padrão: {computePastureCapacity(form, pastureCapacityConfig)?.entryHeight||"—"} cm • Saída padrão: {computePastureCapacity(form, pastureCapacityConfig)?.exitHeight||"—"} cm</div></Field>
                  <Field label="Modo do Local">
                    <label style={{display:"flex",alignItems:"center",gap:10,minHeight:42,color:T.text,fontWeight:600,cursor:"pointer"}}>
                      <input type="checkbox" checked={!!form.useAnimalCapacity} onChange={e=>setForm(f=>({...f,useAnimalCapacity:e.target.checked}))} />
                      <span>Usar cálculo de capacidade animal neste local</span>
                    </label>
                  </Field>
                  {form.useAnimalCapacity && <>
                    <Field label="Nível de Invasoras"><Select value={form.weedLevel} onChange={e=>setForm(f=>normalizePastureEntry({...f,weedLevel:e.target.value}))}>{(SL.WEED_LEVELS||WEED_LEVELS).map(w=><option key={w}>{w}</option>)}</Select></Field>
                    <Field label="Altura Atual (cm)"><Input type="number" value={form.currentHeight} onChange={e=>setForm(f=>normalizePastureEntry({...f,currentHeight:e.target.value}))}/></Field>
                  </>}
                  <Field label="Área (ha)"><Input type="number" step="0.01" value={form.areHa} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Atualizada automaticamente a partir do polígono.</div></Field>
                  {form.useAnimalCapacity && <>
                    <Field label="Capacidade Base (UA/ha)"><Input type="number" step="0.01" value={form.capacityBaseUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Definida na tabela de pastagens do Admin.</div></Field>
                <Field label="Capacidade Estrutural (UA/ha)"><Input type="number" step="0.01" value={form.capacityStructuralUaPerHa||form.autoCalculated?.capacityUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Base por pastagem + invasoras + altura.</div></Field>
                <Field label="Capacidade Atual Climática (UA/ha)"><Input type="number" step="0.01" value={form.capacityUaPerHa||""} readOnly disabled /><div style={{fontSize:11,color:T.muted,marginTop:6}}>Ajustada pelo fator climático da fazenda.</div></Field>
                <Field label="Capacidade Atual (UA)"><Input type="number" step="0.01" value={form.capacityUaTotal||""} readOnly disabled /></Field>
                <Field label="Fator climático"><Input type="text" value={form.capacityClimateFactor?`${form.capacityClimateFactor}x`:""} readOnly disabled /></Field>
                    <Field label="Altura de Entrada (cm)"><Input type="number" value={form.entryHeight} onChange={e=>setForm(f=>({...f,entryHeight:e.target.value}))}/></Field>
                    <Field label="Altura de Saída (cm)"><Input type="number" value={form.exitHeight} onChange={e=>setForm(f=>({...f,exitHeight:e.target.value}))}/></Field>
                  </>}
                  <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
                  <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}>
                    <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
                    <Btn onClick={save}>Salvar</Btn>
                  </div>
                </div>
              </div>}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:20}}>
                {[
                  {icon:"📐",label:"Área",value:`${selected.areHa||"—"} ha`,color:T.accent},
                  ...(usesAnimalCapacity(selected) ? [
                    {icon:<CattleIcon size={20} />,label:"Cap. Atual",value:`${selected.capacityUaTotal||getClimateAdjustedMetrics(selected)?.uaTotal||"—"} UA`,color:T.yellow},
                    {icon:"🐮",label:"UA/ha",value:`${selected.capacityUaPerHa||getClimateAdjustedMetrics(selected)?.uaPerHa||"—"}`,color:T.purple},
                    {icon:"☁️",label:"Fator Clima",value:`${selected.capacityClimateFactor||"1.00"}x`,color:T.accent},
                    {icon:"📦",label:"UA Alocada",value:`${selected.stockingUsedUa||"0.00"} UA`,color:getStockingBalloonMeta(selected).color},
                    {icon:"📏",label:"Altura Atual",value:`${selected.currentHeight||"—"} cm`,color:T.green},
                    {icon:"↗️",label:"Altura Entrada",value:`${selected.entryHeight||"—"} cm`,color:T.green},
                    {icon:"↘️",label:"Altura Saída",value:`${selected.exitHeight||"—"} cm`,color:T.red},
                  ] : [
                    {icon:"🌿",label:"Modo",value:"Área simples",color:T.green},
                  ]),
                ].map(s=><StatCard key={s.label} icon={s.icon} label={s.label} value={s.value} color={s.color}/>)}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
                {/* Pastagem info */}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
                  <div style={{fontWeight:700,color:T.text,marginBottom:12}}>🌿 Informações do Local</div>
                  {[
                    ["Tipo de Pastagem / Cobertura", selected.grassType],
                    ["Modo", usesAnimalCapacity(selected) ? "Cálculo pecuário" : "Área simples"],
                    ...(usesAnimalCapacity(selected) ? [["Último Uso", selected.lastUsed?fmtDate(selected.lastUsed):"—"], ["Última medição de altura", selected.lastHeightAt?fmtDate(selected.lastHeightAt):"—"], ["Status da medição", getHeightStatusMeta(selected).label], ["Invasoras", selected.weedLevel], ["Base da Pastagem", `${selected.capacityBaseUaPerHa||capacity?.baseUaPerHa||"—"} UA/ha`], ["Capacidade estrutural", `${selected.capacityStructuralUaTotal||capacity?.uaTotal||"—"} UA`], ["Fator Invasoras", selected.capacityWeedFactor||capacity?.weedFactor||"—"], ["Fator Altura", selected.capacityHeightFactor||capacity?.heightFactor||"—"], ["Fator climático", `${selected.capacityClimateFactor||"1.00"}x`], ["Resumo clima", selected.capacityClimateSummary||selectedClimateSummary.summary||farmClimateMeta.climateSummary||farmClimateMeta.summary], ["Fonte climática", selectedClimateSummary.effectiveSource === "wunderground" ? "WU banco" : (selectedClimateSummary.effectiveSource === "inmet" ? "INMET banco" : (selectedClimateSummary.effectiveSource ? "Manual banco" : farmClimateMeta.sourceLabel))], ["Estação usada", selectedClimateSummary.calculation?.stationUsed?.stationName || selectedClimateSummary.sourceStationName || selectedClimateSummary.sourceStationId || "—"], ["Distância da estação", selectedClimateSummary.calculation?.stationUsed?.distanceKm == null ? "—" : `${selectedClimateSummary.calculation.stationUsed.distanceKm.toFixed(2)} km`], ["Status clima", selectedClimateSummary.integrationStatus === "online" ? "Online" : (selectedClimateSummary.integrationStatus === "delayed" ? "Em atraso" : (selectedClimateSummary.integrationStatus === "offline" ? "Offline" : farmClimateMeta.statusLabel))], ["Última leitura banco", selectedClimateSummary.latestObservedOn||normalizedClimateDbSummary.latestObservedOn||"—"], ["Fallback usado", selectedClimateSummary.fallbackUsed ? "Sim" : (farmClimateProfile.fallbackToInmet ? "Não" : "Fallback desativado")], ["Motivo do fallback", selectedClimateSummary.selectionMessage || selectedClimateSummary.calculation?.fallbackMessage || "Sem fallback"], ["Janela climática", `${farmClimateProfile.analysisWindowDays} dias`], ["Fallback", farmClimateProfile.fallbackToInmet ? "INMET ativo" : "Sem fallback"], ["UA alocada", `${selected.stockingUsedUa||"0.00"} UA`], ["Saldo UA", `${selected.stockingAvailableUa||"0.00"} UA`], ["Status de lotação", selected.stockingStatus||"Sem lotação"], ["Lotes vinculados", `${selected.stockingLotsCount||0}`], ["Animais vinculados", `${selected.stockingHeadCount||0}`]] : []),
                    ["Notas", selected.notes||"—"],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.border}33`,fontSize:13}}>
                      <span style={{color:T.muted}}>{k}</span>
                      <span style={{color:T.text,fontWeight:500,maxWidth:180,textAlign:"right"}}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Herbicide info */}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
                  <div style={{fontWeight:700,color:T.text,marginBottom:12}}>🧪 Controle de Herbicida</div>
                  {usesAnimalCapacity(selected) ? <>
                    {[
                      ["Produto Utilizado", selected.herbicideProduct||"—"],
                      ["Última Aplicação", selected.herbicideDate?fmtDate(selected.herbicideDate):"—"],
                    ].map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.border}33`,fontSize:13}}>
                        <span style={{color:T.muted}}>{k}</span>
                        <span style={{color:T.text,fontWeight:500}}>{v}</span>
                      </div>
                    ))}
                    {selected.herbicideRecipe&&(
                      <div style={{marginTop:12,background:T.card2,borderRadius:8,padding:10}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:.6}}>📋 Receituário</div>
                        <div style={{fontSize:12,color:T.text,whiteSpace:"pre-wrap",lineHeight:1.6}}>{selected.herbicideRecipe}</div>
                      </div>
                    )}
                  </> : <div style={{fontSize:13,color:T.muted}}>Este local está em modo de área simples e não usa controle de herbicida para cálculo pecuário.</div>}
                </div>
              </div>

              {usesAnimalCapacity(selected) && (
                <div style={{display:"grid",gap:12,marginBottom:20}}>
                  {!!selectedClimateSummary.diagnostics?.headline && <div style={{background:T.card,border:`1px solid ${selectedClimateSummary.diagnostics?.severity === 'critical' ? T.red : (selectedClimateSummary.diagnostics?.severity === 'attention' ? T.yellow : T.border)}`,borderRadius:12,padding:16,display:"grid",gap:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <div style={{fontWeight:700,color:T.text}}>Diagnóstico climático do Local</div>
                      <Badge color={selectedClimateSummary.diagnostics?.severity === 'critical' ? T.red : (selectedClimateSummary.diagnostics?.severity === 'attention' ? T.yellow : (selectedClimateSummary.diagnostics?.severity === 'info' ? T.blue : T.green))}>{selectedClimateSummary.diagnostics?.severity === 'critical' ? 'Crítico' : (selectedClimateSummary.diagnostics?.severity === 'attention' ? 'Revisar' : (selectedClimateSummary.diagnostics?.severity === 'info' ? 'Fallback' : 'OK'))}</Badge>
                    </div>
                    <div style={{fontSize:13,color:T.text,fontWeight:700}}>{selectedClimateSummary.diagnostics.headline}</div>
                    {!!selectedClimateSummary.diagnostics?.summary && <div style={{fontSize:12,color:T.muted}}>{selectedClimateSummary.diagnostics.summary}</div>}
                    {!!selectedClimateSummary.diagnostics?.issues?.length && <div style={{display:"grid",gap:6}}>{selectedClimateSummary.diagnostics.issues.slice(0,2).map((issue, idx)=><div key={`${issue.title||'issue'}:${idx}`} style={{fontSize:12,color:T.muted}}><b style={{color:T.text}}>{issue.title || 'Observação'}:</b> {issue.message || 'Sem detalhes.'}</div>)}</div>}
                  </div>}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginBottom:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <div>
                      <div style={{fontWeight:700,color:T.text}}>🕘 Histórico climático por cálculo</div>
                      <div style={{fontSize:12,color:T.muted,marginTop:4}}>Cada cálculo do Local grava a estação usada, a fonte, a janela aplicada, os dados climáticos consolidados e o fallback usado.</div>
                    </div>
                    <Badge color={T.blue}>{selectedClimateHistory.length} registro(s)</Badge>
                  </div>
                  {!selectedClimateHistory.length ? (
                    <div style={{fontSize:13,color:T.muted,marginTop:12}}>Ainda não há histórico climático salvo para este Local.</div>
                  ) : (
                    <div style={{display:"grid",gap:10,marginTop:12}}>
                      {selectedClimateHistory.map((entry, idx)=>(
                        <div key={entry.calcUid || idx} style={{padding:12,border:`1px solid ${T.border}`,borderRadius:12,background:T.card2}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                            <div>
                              <div style={{fontWeight:700,color:T.text}}>{entry.sourceStationName || entry.sourceStationId || "Sem estação"}</div>
                              <div style={{fontSize:12,color:T.muted,marginTop:4}}>{entry.effectiveSource === "wunderground" ? "WU banco" : (entry.effectiveSource === "inmet" ? "INMET banco" : (entry.effectiveSource || "Manual banco"))} · janela {entry.windowDays || farmClimateProfile.analysisWindowDays} dias{entry.distanceKm == null ? "" : ` · ${entry.distanceKm.toFixed(2)} km`}</div>
                            </div>
                            <div style={{fontSize:11,color:T.muted}}>{entry.createdAt || "—"}</div>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(3,minmax(0,1fr))",gap:8,marginTop:10}}>
                            <div style={{padding:10,border:`1px solid ${T.border}`,borderRadius:10,background:T.card}}><div style={{fontSize:11,color:T.muted}}>Última leitura</div><div style={{fontWeight:700,color:T.text,marginTop:4}}>{entry.latestObservedOn || "—"}</div></div>
                            <div style={{padding:10,border:`1px solid ${T.border}`,borderRadius:10,background:T.card}}><div style={{fontSize:11,color:T.muted}}>Fallback</div><div style={{fontWeight:700,color:T.text,marginTop:4}}>{entry.fallbackUsed ? "Sim" : "Não"}</div></div>
                            <div style={{padding:10,border:`1px solid ${T.border}`,borderRadius:10,background:T.card}}><div style={{fontSize:11,color:T.muted}}>Motivo</div><div style={{fontWeight:700,color:T.text,marginTop:4}}>{entry.fallbackMessage || entry.fallbackReason || "Sem fallback"}</div></div>
                            <div style={{padding:10,border:`1px solid ${T.border}`,borderRadius:10,background:T.card}}><div style={{fontSize:11,color:T.muted}}>Chuva da janela</div><div style={{fontWeight:700,color:T.text,marginTop:4}}>{entry.climateData?.window?.rainfall_mm ?? "—"} mm</div></div>
                            <div style={{padding:10,border:`1px solid ${T.border}`,borderRadius:10,background:T.card}}><div style={{fontSize:11,color:T.muted}}>Dias secos</div><div style={{fontWeight:700,color:T.text,marginTop:4}}>{entry.climateData?.window?.dry_days ?? "—"}</div></div>
                            <div style={{padding:10,border:`1px solid ${T.border}`,borderRadius:10,background:T.card}}><div style={{fontSize:11,color:T.muted}}>Temp. média</div><div style={{fontWeight:700,color:T.text,marginTop:4}}>{entry.climateData?.window?.avg_temp_c ?? "—"} °C</div></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </div>
              )}

              {/* GPS Coordinates */}
              {(selected.lat||selected.lng)&&(
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:14,marginBottom:16,fontSize:12,color:T.muted,display:"flex",gap:16}}>
                  <span>📍 Lat: <strong style={{color:T.text}}>{selected.lat}</strong></span>
                  <span>Lng: <strong style={{color:T.text}}>{selected.lng}</strong></span>
                  {selected.polygon?.length>0&&<span style={{color:T.green}}>✅ Polígono: {selected.polygon.length} pontos</span>}
                </div>
              )}

              {/* Google Map — always show if apiKey configured */}
              <div style={{marginBottom:20}}>
                <div style={{fontWeight:700,color:T.text,marginBottom:10}}>🗺️ Mapa do Local</div>
                <GoogleMapPicker
                  apiKey={apiKey}
                  lat={parseFloat(selected.lat)||undefined}
                  lng={parseFloat(selected.lng)||undefined}
                  polygon={selected.polygon}
                  color={selected.color||PASTURE_COLORS[0]}
                  pois={[
                    ...(selected.pois||[]),
                    ...activePois.filter(p=>(p.pastureIds||[]).includes(selected.id)).map(p=>({
                      id:`gpoi_${p.id}`, name:p.name, type:p.costCenter,
                      lat:p.lat, lng:p.lng, isGlobalPoi:true, status:p.status
                    }))
                  ]}
                  onChange={change=>handleMapChange(selected,change)}
                />
                {activePois.filter(p=>(p.pastureIds||[]).includes(selected.id)).length>0&&(
                  <div style={{marginTop:6,fontSize:11,color:T.purple}}>
                    📍 {activePois.filter(p=>(p.pastureIds||[]).includes(selected.id)).length} ponto(s) vinculado(s) exibido(s) no mapa
                  </div>
                )}
                <div style={{fontSize:11,color:T.muted,marginTop:6}}>
                  🖊️ Use o botão de polígono no topo do mapa para desenhar a área do local. A área em hectares é recalculada automaticamente sempre que o polígono for alterado.
                </div>
              </div>

              {/* Points of Interest — from global POI module */}
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontWeight:700,color:T.text}}>📍 Pontos de Interesse</div>
                  <Btn size="sm" onClick={()=>openPoiModule(selected.id)}>+ Adicionar</Btn>
                </div>
                {(()=>{
                  const linkedPois = activePois.filter(p=>(p.pastureIds||[]).map(String).includes(String(selected.id)));
                  if(linkedPois.length===0) return(
                    <div style={{color:T.muted,fontSize:13}}>
                      Nenhum ponto de interesse vinculado.{" "}
                      <span style={{color:T.accent,cursor:"pointer"}} onClick={()=>openPoiModule(selected.id)}>
                        Clique em "+ Adicionar" para ir ao módulo Pontos de Interesse com este local pré-selecionado.
                      </span>
                    </div>
                  );
                  return(
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {linkedPois.map(poi=>{
                        const stColor={"Ativo":T.green,"Inativo":T.muted,"Em Manutenção":T.yellow}[poi.status]||T.muted;
                        return(
                          <div key={poi.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:T.card2,borderRadius:8,border:`1px solid ${stColor}33`}}>
                            <span style={{fontSize:20}}>{poi.status==="Em Manutenção"?"🔧":poi.status==="Inativo"?"⛔":"📍"}</span>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:600,fontSize:13,color:T.text}}>{`${poi.name}${getPoiFarmName(poi)?` - ${getPoiFarmName(poi)}`:""}`}</div>
                              <div style={{fontSize:11,color:T.muted,marginTop:1}}>
                                {poi.costCenter}
                                {(poi.customFields||[]).length>0&&<span> · {poi.customFields.map(cf=>`${cf.key}: ${cf.value}`).join(" · ")}</span>}
                                {(poi.pastureIds||[]).length>1&&<span style={{color:T.accent}}> · também vinculado a {poi.pastureIds.length-1} outro(s) local(is)</span>}
                              </div>
                            </div>
                            <Badge color={stColor}>{poi.status}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {usesAnimalCapacity(selected) && <div style={{background:T.card,border:`1px solid ${getPastureAttentionState(selected).color}33`,borderRadius:12,padding:16,marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <div style={{fontWeight:700,color:T.text}}>🚦 Status operacional do local</div>
                  <Badge color={getPastureAttentionState(selected).color}>{getPastureAttentionState(selected).label}</Badge>
                </div>
                <div style={{fontSize:13,color:T.text,fontWeight:600,marginBottom:6}}>{getPastureAttentionState(selected).recommendation}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10,fontSize:12}}>
                  <div style={{padding:"10px 12px",borderRadius:10,background:T.card2,border:`1px solid ${T.border}`}}><div style={{color:T.muted,marginBottom:3}}>Última altura</div><div style={{color:T.text,fontWeight:700}}>{selected.lastHeightAt ? fmtDate(selected.lastHeightAt) : "—"}</div></div>
                  <div style={{padding:"10px 12px",borderRadius:10,background:T.card2,border:`1px solid ${T.border}`}}><div style={{color:T.muted,marginBottom:3}}>Próxima revisão</div><div style={{color:T.text,fontWeight:700}}>{(()=>{ const days = getHeightUpdateAgeDays(selected); if(days == null) return "Agora"; if(days >= 31) return "Vencida"; return `${Math.max(0, 30 - days)} dia(s)`; })()}</div></div>
                  <div style={{padding:"10px 12px",borderRadius:10,background:T.card2,border:`1px solid ${T.border}`}}><div style={{color:T.muted,marginBottom:3}}>Origem do cálculo</div><div style={{color:T.text,fontWeight:700}}>{selected.manualOverride?.active ? "Manual" : "Automático + clima"}</div></div>
                  <div style={{padding:"10px 12px",borderRadius:10,background:T.card2,border:`1px solid ${T.border}`}}><div style={{color:T.muted,marginBottom:3}}>Fonte climática</div><div style={{color:T.text,fontWeight:700}}>{farmClimateMeta.sourceLabel}</div></div>
                </div>
              </div>}

              {usesAnimalCapacity(selected) && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <div style={{fontWeight:700,color:T.text}}>🕘 Histórico técnico do local</div>
                  {selected.manualOverride?.active && <Badge color={T.purple}>Ajuste manual ativo</Badge>}
                </div>
                {selected.manualOverride?.active && <div style={{marginBottom:12,padding:"10px 12px",borderRadius:10,background:`${T.purple}14`,border:`1px solid ${T.purple}33`,fontSize:12,color:T.text}}>
                  <div style={{fontWeight:700,marginBottom:4}}>Override manual em vigor</div>
                  <div>Motivo: {selected.manualOverride?.reason || "—"}</div>
                  <div>Aplicado por: {selected.manualOverride?.by || selected.updatedBy || "—"} {selected.manualOverride?.at ? `• ${fmtDate(selected.manualOverride.at)}` : ""}</div>
                  <div style={{marginTop:6,color:T.muted}}>Valores automáticos preservados no histórico. Use <strong>Restaurar Auto</strong> para voltar ao cálculo do app.</div>
                </div>}
                {(!Array.isArray(selected.capacityHistory) || selected.capacityHistory.length===0) ? <div style={{color:T.muted,fontSize:13}}>Sem histórico técnico registrado ainda.</div> : <div style={{display:"flex",flexDirection:"column",gap:8}}>{selected.capacityHistory.slice(0,8).map(item => <div key={item.id} style={{padding:"10px 12px",background:T.card2,border:`1px solid ${T.border}`,borderRadius:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                    <div style={{fontWeight:700,color:T.text}}>{item.label || "Atualização"}</div>
                    <div style={{fontSize:11,color:T.muted}}>{item.at ? fmtDate(item.at) : "—"}{item.by ? ` • ${item.by}` : ""}</div>
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:3}}>{item.notes || "Sem observação registrada."}</div>
                  {item.snapshot && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8,fontSize:11,marginTop:8}}>
                    <div><span style={{color:T.muted}}>Área:</span> <strong style={{color:T.text}}>{item.snapshot.areHa || "—"} ha</strong></div>
                    <div><span style={{color:T.muted}}>Altura:</span> <strong style={{color:T.text}}>{item.snapshot.currentHeight || "—"} cm</strong></div>
                    <div><span style={{color:T.muted}}>Invasoras:</span> <strong style={{color:T.text}}>{item.snapshot.weedLevel || "—"}</strong></div>
                    <div><span style={{color:T.muted}}>UA/ha:</span> <strong style={{color:T.text}}>{item.snapshot.capacityUaPerHa || "—"}</strong></div>
                    <div><span style={{color:T.muted}}>UA total:</span> <strong style={{color:T.text}}>{item.snapshot.capacityUaTotal || "—"}</strong></div>
                  </div>}
                </div>)}</div>}
              </div>}

              {/* Last Services */}
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
                <div style={{fontWeight:700,color:T.text,marginBottom:12}}>🔨 Últimos Serviços Realizados</div>
                {svcs.length===0&&<div style={{color:T.muted,fontSize:13}}>Nenhum serviço registrado para este local.</div>}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {svcs.slice(0,5).map(s=>(
                    <div key={s.id} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 12px",background:T.card2,borderRadius:8}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:13,color:T.text}}>{s.serviceType}</div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                          {fmtDate(s.dateStart)}{s.dateEnd&&s.dateEnd!==s.dateStart?` → ${fmtDate(s.dateEnd)}`:""} · {s.costCenter}
                        </div>
                        {(s.materials||[]).length>0&&<div style={{fontSize:11,color:T.purple,marginTop:2}}>📦 {s.materials.map(m=>m.name).join(", ")}</div>}
                      </div>
                      <Badge color={s.dateEnd?T.green:T.yellow}>{s.dateEnd?"Concluído":"Em andamento"}</Badge>
                    </div>
                  ))}
                  {svcs.length>5&&<div style={{color:T.muted,fontSize:12,textAlign:"center"}}>+{svcs.length-5} serviço(s) anteriores</div>}
                </div>
              </div>
            </div>
          );
        })()}
        {manualOverride && <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16}}>
          <div style={{width:"100%",maxWidth:760,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,boxShadow:"0 24px 80px rgba(0,0,0,.35)",padding:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:14}}>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,color:T.text}}>Ajuste Manual</div>
                <div style={{fontSize:12,color:T.muted,marginTop:2}}>{manualOverride.name}</div>
              </div>
              <Btn variant="ghost" onClick={()=>setManualOverride(null)}>✕</Btn>
            </div>
            <div style={{padding:"10px 12px",borderRadius:10,background:T.card2,border:`1px solid ${T.border}`,fontSize:12,color:T.text,marginBottom:14}}>
              <div style={{fontWeight:700,marginBottom:6}}>Referência automática atual</div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(5,minmax(0,1fr))",gap:8}}>
                <div><span style={{color:T.muted}}>Área:</span> <strong>{manualOverride.autoSnapshot?.areHa || "—"} ha</strong></div>
                <div><span style={{color:T.muted}}>Altura:</span> <strong>{manualOverride.autoSnapshot?.currentHeight || "—"} cm</strong></div>
                <div><span style={{color:T.muted}}>Invasoras:</span> <strong>{manualOverride.autoSnapshot?.weedLevel || "—"}</strong></div>
                <div><span style={{color:T.muted}}>UA/ha:</span> <strong>{manualOverride.autoSnapshot?.capacityUaPerHa || "—"}</strong></div>
                <div><span style={{color:T.muted}}>UA total:</span> <strong>{manualOverride.autoSnapshot?.capacityUaTotal || "—"}</strong></div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
              <Field label="Área ajustada (ha)"><Input type="number" min="0" step="0.01" value={manualOverride.areaHa||""} onChange={e=>setManualOverride(v=>({...v,areaHa:e.target.value}))} /></Field>
              <Field label="Altura ajustada (cm)"><Input type="number" min="0" step="0.1" value={manualOverride.currentHeight||""} onChange={e=>setManualOverride(v=>({...v,currentHeight:e.target.value}))} /></Field>
              <Field label="Nível de invasoras"><Select value={manualOverride.weedLevel||"Limpo"} onChange={e=>setManualOverride(v=>({...v,weedLevel:e.target.value}))}>{WEED_LEVELS.map(level => <option key={level}>{level}</option>)}</Select></Field>
              <Field label="UA/ha ajustada"><Input type="number" min="0" step="0.01" value={manualOverride.capacityUaPerHa||""} onChange={e=>setManualOverride(v=>({...v,capacityUaPerHa:e.target.value}))} /></Field>
              <Field label="UA total ajustada"><Input type="number" min="0" step="0.01" value={manualOverride.capacityUaTotal||""} onChange={e=>setManualOverride(v=>({...v,capacityUaTotal:e.target.value}))} /></Field>
              <Field label="Justificativa" col="1/-1"><Textarea rows={3} value={manualOverride.reason||""} onChange={e=>setManualOverride(v=>({...v,reason:e.target.value}))} placeholder="Explique por que este local precisa de ajuste manual." /></Field>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:16}}>
              <Btn variant="ghost" onClick={()=>setManualOverride(null)}>Cancelar</Btn>
              <Btn onClick={saveManualOverride}>Salvar ajuste</Btn>
            </div>
          </div>
        </div>}

        {heightUpdate && <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16}}>
          <div style={{width:"100%",maxWidth:520,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,boxShadow:"0 24px 80px rgba(0,0,0,.35)",padding:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:14}}>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,color:T.text}}>Altura Pastagem</div>
                <div style={{fontSize:12,color:T.muted,marginTop:2}}>{heightUpdate.name}</div>
              </div>
              <Btn variant="ghost" onClick={()=>setHeightUpdate(null)}>✕</Btn>
            </div>
            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
              <Field label="Altura atual (cm)" col="1/-1"><Input type="number" min="0" step="0.1" value={heightUpdate.currentHeight||""} onChange={e=>setHeightUpdate(v=>({...v,currentHeight:e.target.value}))} /></Field>
              <Field label="Data da medição"><Input type="date" value={heightUpdate.measuredAt||""} onChange={e=>setHeightUpdate(v=>({...v,measuredAt:e.target.value}))} /></Field>
              <Field label="Status atual"><div style={{minHeight:42,display:"flex",alignItems:"center",gap:8,padding:"0 2px"}}><span style={{width:10,height:10,borderRadius:999,background:getHeightStatusMeta({ lastHeightAt: heightUpdate.measuredAt }).color,display:"inline-block"}} /><span style={{color:getHeightStatusMeta({ lastHeightAt: heightUpdate.measuredAt }).color,fontWeight:700}}>{getHeightStatusMeta({ lastHeightAt: heightUpdate.measuredAt }).label}</span></div></Field>
              <Field label="Observação" col="1/-1"><Textarea rows={3} value={heightUpdate.notes||""} onChange={e=>setHeightUpdate(v=>({...v,notes:e.target.value}))} placeholder="Observações da medição da altura atual da pastagem" /></Field>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:16}}>
              <Btn variant="ghost" onClick={()=>setHeightUpdate(null)}>Cancelar</Btn>
              <Btn onClick={saveHeightUpdate}>Salvar altura</Btn>
            </div>
          </div>
        </div>}
      </>)}
    </div>
  );
}

function POIModule({ session, preset, onConsumePreset }) {
  const { fid: currentFarmId, farm: currentFarm } = useFarm();
  const SL = useSysLists();
  const [pois,     setPois]    = useFarmCollection("poi",      []);
  const [pastures, ]           = useFarmCollection("pastures", []);
  const ensurePoiRecordNumbers = React.useCallback((records=[])=>{
    const working = Array.isArray(records) ? records.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{ if (!record || record.recordNumber) return; Object.assign(record, buildNextRecordNumber(working.filter(Boolean), { moduleKey:"locations" })); });
    return working;
  }, []);
  const [services, ]           = useFarmCollection("services", []);
  const [occurrences, ]        = useFarmCollection("occurrences", []);
  const [, setHarasStalls] = useFarmCollection("harasStalls", []);
  const [form,     setForm]    = useState(null);
  const [selected, setSelected]= useState(null);
  const poiFocusedId = String((selected && selected.id) || (form && !form._new ? form.id : "") || "");
  const isPoiInlineFocusMode = !!(selected || form);
  const [toast,    show]       = useToast();
  useBgSyncToast("poi", show);
  const syncOnSave = useReliableSyncOnSave();
  const poiSyncContext = React.useMemo(() => ({
    farmId: currentFarmId || "",
    farmName: currentFarm?.name || "",
    user: session?.name || "",
    scope: "poi",
    mapsKeyConfigured: !!window.__fzMapsConfig?.key,
    mapIdConfigured: !!window.__fzMapsConfig?.mapId,
  }), [currentFarmId, currentFarm?.name, session?.name]);
  const syncOnSaveTracked = React.useCallback((collection, reason = "") => {
    if (reason) {
      const payload = { collection, reason, scope: "poi", context: poiSyncContext };
      try { recordLocationSyncDebug("save-sync", payload); } catch {}
    }
    syncOnSave(collection);
  }, [poiSyncContext, syncOnSave]);
  const moduleCardsConfig = React.useMemo(()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){
      return {};
    }
  }, []);
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.pastures;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedPoiCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("menu:pontos_interesse", []);
    return Array.isArray(direct) && direct.length ? direct : ["pontos_ativos","em_manutencao","serv_pausados","serv_agendados"];
  }, [getConfiguredSectionCards]);
  const [,setPhotoModal]=useState(null); // {poiId, src}
  const [maintenanceTrace,setMaintenanceTrace]=useState(null);
  const [poiSearch, setPoiSearch] = useState("");
  const [poiFilterOpen, setPoiFilterOpen] = useState(false);
  const [poiFilterStatus, setPoiFilterStatus] = useState("all");
  const [poiFilterCostCenter, setPoiFilterCostCenter] = useState("all");
  const [poiFilterCategory, setPoiFilterCategory] = useState("all");
  function dedupeById(items = []) {
    const map = new Map();
    for (const raw of Array.isArray(items) ? items : []) {
      const item = raw && typeof raw === "object" ? raw : null;
      if (!item) continue;
      const key = String(item.id || item.clientId || createClientId("tmp"));
      const prev = map.get(key);
      if (!prev) { map.set(key, item); continue; }
      const prevAt = String(prev.updatedAt || prev.deletedAt || prev.createdAt || "");
      const nextAt = String(item.updatedAt || item.deletedAt || item.createdAt || "");
      if (nextAt >= prevAt) map.set(key, item);
    }
    return Array.from(map.values());
  }

  function isPoiDeleted(item) {
    return !!(item?._deleted || item?.deletedAt || item?.deleted_at);
  }

  const normalizePoiEntry = React.useCallback((item) => {
    if(!item || typeof item !== "object") return item;
    const now = new Date().toISOString();
    const next = { ...item };
    const canonicalId = String(next.clientId || next.id || createClientId("poi"));
    next.id = canonicalId;
    next.clientId = canonicalId;
    if(!next.farmId) next.farmId = currentFarmId || "";
    if(!next.farmName) next.farmName = currentFarm?.name || next.farmName || "";
    if(!next.createdAt) next.createdAt = next.updatedAt || now;
    if(!next.updatedAt) next.updatedAt = next.createdAt || now;
    if(!next.updatedBy) next.updatedBy = session?.name || "";
    return next;
  }, [currentFarmId, currentFarm?.name, session?.name]);

  useEffect(() => {
    if (!Array.isArray(pois) || pois.length === 0) return;
    const normalized = dedupeById(pois.map(normalizePoiEntry));
    const changed = normalized.length !== pois.length || normalized.some((item, idx) => JSON.stringify(item) !== JSON.stringify(pois[idx]));
    if (changed) {
      setPois(normalized);
    }
  }, [pois, currentFarmId, currentFarm?.name, session?.name, setPois, normalizePoiEntry]);


  const isAdmin = session?.role === "admin";
  const canCreate = canCreateInModule(session,"poi") || canEditInModule(session,"poi") || isAdmin;
  const canEdit = canEditInModule(session,"poi") || isAdmin;
  const canDelete = canDeleteInModule(session,"poi") || isAdmin;
  const allowedCostCenters = React.useMemo(()=>getAllowedCostCentersForModule(session, "poi"), [session]);
  const deniedCostCenters = React.useMemo(()=>getDeniedCostCentersForModule(session, "poi"), [session]);
  const visiblePasturesForPoi = React.useMemo(()=> (pastures||[]).filter(item=>!(item?._deleted || item?.deletedAt || item?.deleted_at)).filter(item=>itemMatchesCostCenterAccess(session, "pastures", item)), [pastures, session]);
  const visiblePoisBase = React.useMemo(()=> (pois||[]).filter(item=>!isPoiDeleted(item)).filter(item=>itemMatchesCostCenterAccess(session, "poi", item)), [pois, session]);
  const poiCategories = React.useMemo(() => [...new Set(visiblePoisBase.map(item => String(item?.category || "").trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b)), [visiblePoisBase]);
  const poiSearchNormalized = poiSearch.trim().toLowerCase();
  const visiblePois = React.useMemo(() => visiblePoisBase.filter(item => {
    if (poiFilterStatus !== "all" && String(item?.status || "") !== poiFilterStatus) return false;
    if (poiFilterCostCenter !== "all" && String(item?.costCenter || "") !== poiFilterCostCenter) return false;
    if (poiFilterCategory !== "all" && String(item?.category || "") !== poiFilterCategory) return false;
    if (!poiSearchNormalized) return true;
    const haystack = [
      item?.name,
      item?.category,
      item?.status,
      item?.costCenter,
      getPoiFarmName(item),
      ...(item?.pastureIds || []),
      ...(item?.customFields || []).flatMap(cf => [cf?.key, cf?.value]),
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(poiSearchNormalized);
  }).sort(compareOperationalRecords), [visiblePoisBase, poiFilterStatus, poiFilterCostCenter, poiFilterCategory, poiSearchNormalized]);
  const defForm = {
    _new:true, name:"", costCenter:allowedCostCenters[0] || COST_CENTERS[0], category:(SL.POI_CATEGORIES||["Pastos","Casas","Galpões","Curral","Baia"])[0],
    pastureIds:[], status:"Ativo", notes:"", lat:"", lng:"",
    customFields:[], photo:null,
  };

  function deletePoiRecord(poiLike, options = {}) {
    const { skipConfirm = false } = options || {};
    const base = normalizePoiEntry(poiLike || {});
    if (!base?.id) { show("POI não encontrado.", "error"); return false; }
    if (!canDelete || !canEditCostCenter(session, "poi", base.costCenter || form?.costCenter || selected?.costCenter || "")) {
      show("Sem permissão para excluir este ponto de interesse.", "error");
      return false;
    }
    if (!skipConfirm) {
      const ok = typeof window === "undefined" ? true : window.confirm(`Deseja realmente excluir o ponto de interesse "${base.name || "sem nome"}"?`);
      if (!ok) return false;
    }
    const actor = session?.name || "";
    const deletedAt = new Date().toISOString();
    const deletedPoi = markLocationUpdate(normalizePoiEntry({
      ...base,
      clientId: String(base.clientId || base.id || createClientId("poi")),
      _deleted: true,
      deletedAt,
      deleted_at: deletedAt,
      deletedBy: actor,
      updatedAt: deletedAt,
      updatedBy: actor,
    }), actor, "delete");
    const applyDelete = (items = []) => {
      const list = Array.isArray(items) ? items : [];
      let found = false;
      const next = list.map(item => {
        const normalized = normalizePoiEntry(item);
        if (String(normalized?.id) === String(deletedPoi.id) || String(normalized?.clientId) === String(deletedPoi.clientId)) {
          found = true;
          return deletedPoi;
        }
        return normalized;
      });
      if (!found) next.push(deletedPoi);
      return dedupeById(next);
    };
    setPois(prev => applyDelete(prev));
    if (String(base.category||"") === "Baia" && base.linkedStallId) {
      setHarasStalls(prev=>prev.filter(st=>String(st.id)!==String(base.linkedStallId)));
      syncOnSaveTracked("harasStalls", "user-linked-stall-delete");
    }
    setSelected(prev => prev && (String(prev.id) === String(deletedPoi.id) || String(prev.clientId) === String(deletedPoi.clientId)) ? null : prev);
    setForm(prev => prev && (String(prev.id) === String(deletedPoi.id) || String(prev.clientId) === String(deletedPoi.clientId)) ? null : prev);
    show("🗑️ Removido.");
    syncOnSaveTracked("poi", "user-delete");
    return true;
  }

  function save() {
    if(!form.name.trim()){show("Nome obrigatório.","error");return;}
    if(!canEditCostCenter(session, "poi", form.costCenter)){ show("Sem permissão para editar este centro de custo.","error"); return; }
    const{_new,...raw}=form;
    const latVal = raw.lat!==undefined && raw.lat!==null && String(raw.lat).trim()!=="" ? String(raw.lat).trim() : "";
    const lngVal = raw.lng!==undefined && raw.lng!==null && String(raw.lng).trim()!=="" ? String(raw.lng).trim() : "";
    const nextCustomFields = [...(raw.customFields||[])].filter(cf=>!['latitude','longitude','lat','lng'].includes(String(cf?.key||'').trim().toLowerCase()));
    if(latVal) nextCustomFields.push({key:"Latitude", value:latVal});
    if(lngVal) nextCustomFields.push({key:"Longitude", value:lngVal});
    const normalizedPastureIds = [...new Set((raw.pastureIds||[]).map(id=>String(id)).filter(Boolean))];
    const d={...raw, costCenter:String(raw.costCenter||""), pastureIds:normalizedPastureIds, lat:latVal, lng:lngVal, customFields:nextCustomFields};
    let savedPoi = null;
    if(_new) {
      const stableId = String(d.id || d.clientId || createClientId("poi"));
      const nextRecordNumber = buildNextRecordNumber(pois||[], { moduleKey:"locations" });
      savedPoi = markLocationCreate(normalizePoiEntry({...d,...nextRecordNumber,recordNumber:appendProvisionalSuffix(nextRecordNumber.recordNumber),id:stableId,clientId:String(d.clientId || stableId),_deleted:false,deletedAt:"",deleted_at:"",deletedBy:"",farmId:currentFarmId||d.farmId||"",farmName:currentFarm?.name||d.farmName||"",...makeCreateMeta(session?.name)}), session?.name || "");
      setPois(p=>dedupeById([...(p||[]), savedPoi]));
      setSelected(savedPoi);
    } else {
      const prevPoi = (pois||[]).find(x=>String(x.id)===String(d.id)) || {};
      const stableId = String(d.id || prevPoi.id || prevPoi.clientId || createClientId("poi"));
      savedPoi = markLocationUpdate(normalizePoiEntry({...prevPoi, ...d, id:stableId, clientId:String(d.clientId || prevPoi.clientId || stableId), _deleted:false, deletedAt:"", deleted_at:"", deletedBy:"", farmId:currentFarmId||d.farmId||"", farmName:currentFarm?.name||d.farmName||"", ...makeUpdateMeta(session?.name)}), session?.name || "");
      setPois(p=>dedupeById((p||[]).map(x=>String(x.id)===String(d.id)?savedPoi:normalizePoiEntry(x))));
      setSelected(prev=>prev && String(prev.id)===String(savedPoi.id) ? savedPoi : prev);
    }
    show("✅ Salvo!"); setForm(null); syncOnSaveTracked("poi", _new ? "user-create" : "user-save");
  }

  // Services / occurrences linked to this POI
  function poiServices(poiId) {
    return services.filter(s=>String(s.poiId)===String(poiId)).filter(s=>itemMatchesCostCenterAccess(session, "services", s))
      .sort(compareOperationalRecords);
  }

  function poiOccurrences(poiId) {
    return occurrences.filter(o=>String(o.poiId)===String(poiId)).filter(o=>itemMatchesCostCenterAccess(session, "occurrences", o))
      .sort(compareOperationalRecords);
  }

  function safeDaysInProgress(value, endValue=null) {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    const endDt = endValue ? new Date(endValue) : new Date();
    const endTs = Number.isNaN(endDt.getTime()) ? Date.now() : endDt.getTime();
    return Math.max(0, Math.floor((endTs-dt.getTime())/(1000*60*60*24)));
  }

  function getMaintenanceAnchor(item) {
    return item?.maintenanceStartedAt || item?.startedAt || item?.dateStart || item?.date || item?.reportedAt || item?.createdAt || null;
  }

  function getMaintenanceEnd(item) {
    return item?.maintenanceCompletedAt || item?.completedAt || item?.dateEnd || item?.completionDate || null;
  }

  function getPauseHistory(item) {
    return Array.isArray(item?.pauseHistory) ? item.pauseHistory.filter(Boolean) : [];
  }

  function getPausedDays(item) {
    const pauses = getPauseHistory(item);
    if (!pauses.length) return 0;
    return pauses.reduce((sum, pause) => sum + (safeDaysInProgress(pause?.startedAt, pause?.endedAt || null) || 0), 0);
  }

  function daysInProgress(item) {
    if (getMaintenanceEnd(item) || item?.status === "Concluída" || item?.status === "Concluído") return null;
    return safeDaysInProgress(getMaintenanceAnchor(item));
  }

  function getMaintenanceDurationDays(item) {
    const anchor = getMaintenanceAnchor(item);
    if (!anchor) return null;
    return safeDaysInProgress(anchor, getMaintenanceEnd(item));
  }

  function getMaintenanceResponsible(item, kind="service") {
    if (kind === "occurrence") {
      return item?.responsibleName || item?.assignedTo || item?.reportedBy || item?.createdBy || item?.updatedBy || "—";
    }
    const assignee = Array.isArray(item?.assignees) ? item.assignees.find(Boolean) : null;
    const assigneeName = typeof assignee === "string" ? assignee : (assignee?.name || "");
    return item?.responsibleName || assigneeName || item?.contactPerson || item?.assignedTo || item?.createdBy || item?.updatedBy || "—";
  }

  function getMaintenanceStatusTone(item) {
    const status = String(item?.status || "").trim();
    if (["Concluído","Concluída"].includes(status)) return T.green;
    if (["Pausada"].includes(status)) return T.orange || T.yellow;
    if (["Cancelado","Cancelada","Negado"].includes(status)) return T.red;
    return T.yellow;
  }

  function poiRecordLabel(item, kind="service") {
    if (kind === "occurrence") return item?.title || item?.category || item?.description || "Ocorrência";
    return item?.serviceType || item?.title || item?.category || (normalizeServiceType(item)==="external" ? "Serviço terceirizado" : "Serviço");
  }
  function buildMaintenanceTimeline(item, kind="service") {
    const base = Array.isArray(item?.timeline) ? item.timeline.filter(Boolean).map(ev=>({
      date: ev?.ts || ev?.date || "",
      action: ev?.action || "Atualização",
      actor: ev?.actor || ev?.by || ev?.user || getMaintenanceResponsible(item, kind),
      note: ev?.note || "",
      type: "event",
    })) : [];
    const pauses = getPauseHistory(item).map((pause, idx)=>({
      date: pause?.startedAt || "",
      action: `Pausa ${idx+1}`,
      actor: pause?.actor || getMaintenanceResponsible(item, kind),
      note: `${pause?.reason || "Sem justificativa"}${pause?.note ? ` — ${pause.note}` : ""}${pause?.endedAt ? ` · retomada em ${fmtDate(pause.endedAt)}` : " · ainda pausada"}`,
      type: "pause",
    }));
    return [...base, ...pauses].sort((a,b)=>String(b.date||"").localeCompare(String(a.date||"")));
  }

  function buildPoiMaintenanceEntries(poiId) {
    return [
      ...poiServices(poiId).map(item=>({ kind:"service", item })),
      ...poiOccurrences(poiId).map(item=>({ kind:"occurrence", item })),
    ].sort((a,b)=>compareOperationalRecords(a.item, b.item));
  }

  function isServiceScheduled(item) {
    const st = String(item?.status || "").trim();
    return st === "Agendado" || st === "Serviço Agendado";
  }

  function isServicePaused(item) {
    return String(item?.status || "").trim() === "Pausada";
  }

  function isLinkedServiceActive(item) {
    const st = String(item?.status || "").trim();
    return !["Concluído","Cancelado","Negado"].includes(st) && !isServicePaused(item) && !isServiceScheduled(item);
  }

  function isLinkedOccurrenceActive(item) {
    const st = String(item?.status || "").trim();
    return !["Concluída","Cancelada","Pausada","Serviço Agendado"].includes(st);
  }

  function getPoiActiveRecord(poiId) {
    const service = poiServices(poiId).find(isLinkedServiceActive);
    if (service) return { kind:"service", item:service };
    const occurrence = poiOccurrences(poiId).find(isLinkedOccurrenceActive);
    if (occurrence) return { kind:"occurrence", item:occurrence };
    return null;
  }

  function getPoiMaintenanceLine(poiId) {
    const active = getPoiActiveRecord(poiId);
    if (!active) return null;
    const label = poiRecordLabel(active.item, active.kind);
    const days = daysInProgress(active.item);
    const icon = active.kind === "occurrence" ? "🚨" : (normalizeServiceType(active.item)==="external" ? "👷" : "⚙️");
    return `${icon} ${label}${days == null ? " — em andamento" : ` — ${days}d em andamento`}`;
  }


  function getPoiPreviewViewport(target) {
    const coords = getPoiCoords(target||{});
    const linkedIds = (target?.pastureIds||[]).map(String);
    const linkedPastures = visiblePasturesForPoi.filter(p=>linkedIds.includes(String(p.id)));
    let center = coords.hasPin ? { lat: coords.lat, lng: coords.lng } : null;
    let zoom = coords.hasPin ? 15 : 13;
    let polygon = null;
    for (const pasture of linkedPastures) {
      if (Array.isArray(pasture?.polygon) && pasture.polygon.length) {
        const pts = pasture.polygon.filter(pt=>Number.isFinite(parseFloat(pt?.lat)) && Number.isFinite(parseFloat(pt?.lng))).map(pt=>({lat:parseFloat(pt.lat),lng:parseFloat(pt.lng)}));
        if (pts.length) { polygon = pts; break; }
      }
    }
    if (!center) center = getPoiFallbackCenter(target);
    if (polygon && polygon.length) {
      const lats = polygon.map(pt=>pt.lat);
      const lngs = polygon.map(pt=>pt.lng);
      const span = Math.max(Math.max(...lats)-Math.min(...lats), Math.max(...lngs)-Math.min(...lngs));
      if (span > 0.04) zoom = 12;
      else if (span > 0.02) zoom = 13;
      else if (span > 0.008) zoom = 14;
      else zoom = 15;
    }
    return { center, zoom, polygon, hasPin: coords.hasPin, pin: coords.hasPin ? { lat: coords.lat, lng: coords.lng } : null };
  }

  useEffect(()=>{
    const normalized = ensurePoiRecordNumbers(pois||[]);
    if ((normalized||[]).some((item, idx)=>String(item?.recordNumber||"") !== String((pois||[])[idx]?.recordNumber||""))) {
      setPois(normalized);
    }
  }, [ensurePoiRecordNumbers, pois, setPois]);

  const poiServicesAll = services.filter(s=>String(s.poiId||"").trim()!=="");
  const poiOccurrencesAll = occurrences.filter(o=>String(o.poiId||"").trim()!=="");
  const activePoiIds = new Set([
    ...poiServicesAll.filter(isLinkedServiceActive).map(s=>String(s.poiId)),
    ...poiOccurrencesAll.filter(isLinkedOccurrenceActive).map(o=>String(o.poiId)),
  ]);
  const pausedServiceCount = poiServicesAll.filter(isServicePaused).length;
  const scheduledServiceCount = poiServicesAll.filter(isServiceScheduled).length;

  const pastureMap = Object.fromEntries(visiblePasturesForPoi.map(p=>[p.id,p.name]));
  const statusColor = {"Ativo":T.green,"Inativo":T.muted,"Em Manutenção":T.yellow};
  const photoInputRef = useRef();
  const [photoPoiId, setPhotoPoiId] = useState(null);
  const apiKey = useServerManagedMapsApiKey();

  function updateCustomField(index, field, value) {
    setForm(prev => {
      if (!prev) return prev;
      const customFields = [...(prev.customFields || [])];
      if (!customFields[index]) return prev;
      customFields[index] = { ...customFields[index], [field]: value };
      return { ...prev, customFields };
    });
  }

  function removeCustomField(index) {
    setForm(prev => {
      if (!prev) return prev;
      return { ...prev, customFields: (prev.customFields || []).filter((_, idx) => idx !== index) };
    });
  }

  function addCustomField() {
    setForm(prev => ({ ...prev, customFields: [...(prev?.customFields || []), { key: "", value: "" }] }));
  }

  function handlePhoto(e, poiId) {
    const file = e?.target?.files?.[0];
    if (!file || !poiId) {
      if (e?.target) e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const photo = ev?.target?.result || null;
      if (!photo) {
        if (e?.target) e.target.value = "";
        return;
      }
      setPois(prev => dedupeById((prev || []).map(item => String(item.id) === String(poiId)
        ? markLocationUpdate(normalizePoiEntry({ ...item, photo, ...makeUpdateMeta(session?.name) }), session?.name || "")
        : normalizePoiEntry(item))));
      setSelected(prev => prev && String(prev.id) === String(poiId) ? { ...prev, photo } : prev);
      setForm(prev => prev && String(prev.id) === String(poiId) ? { ...prev, photo } : prev);
      setPhotoModal(prev => prev && String(prev.poiId) === String(poiId) ? { ...prev, src: photo } : prev);
      setPhotoPoiId(null);
      show("📷 Foto do POI atualizada.");
      syncOnSaveTracked("poi", "user-photo-update");
      if (e?.target) e.target.value = "";
    };
    reader.onerror = () => {
      show("Não foi possível carregar a foto.", "error");
      if (e?.target) e.target.value = "";
    };
    reader.readAsDataURL(file);
  }

  function getPoiCoords(poi){
    const normalizedFields = Array.isArray(poi?.customFields) ? poi.customFields : [];
    const fromField = (...keys) => {
      const wanted = keys.map(key=>String(key||"").trim().toLowerCase());
      const hit = normalizedFields.find(cf=>wanted.includes(String(cf?.key||"").trim().toLowerCase()));
      return hit?.value;
    };
    const firstDefined = (...values) => values.find(value => value !== undefined && value !== null && String(value).trim() !== "");
    const latRaw = firstDefined(
      poi?.lat,
      poi?.latitude,
      poi?.pin?.lat,
      poi?.pin?.latitude,
      poi?.position?.lat,
      poi?.position?.latitude,
      poi?.location?.lat,
      poi?.location?.latitude,
      poi?.coords?.lat,
      poi?.coordinates?.lat,
      fromField("latitude", "lat", "latitude gps", "gps latitude")
    );
    const lngRaw = firstDefined(
      poi?.lng,
      poi?.longitude,
      poi?.pin?.lng,
      poi?.pin?.longitude,
      poi?.position?.lng,
      poi?.position?.longitude,
      poi?.location?.lng,
      poi?.location?.longitude,
      poi?.coords?.lng,
      poi?.coordinates?.lng,
      fromField("longitude", "lng", "longitude gps", "gps longitude")
    );
    const lat = parseFloat(latRaw);
    const lng = parseFloat(lngRaw);
    return {
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
      hasPin: Number.isFinite(lat) && Number.isFinite(lng),
    };
  }

  function buildPoiStaticPreviewUrl(target, size = { width: 640, height: 240 }) {
    const viewport = getPoiPreviewViewport(target);
    const { center, polygon, pin } = viewport;
    const width = Math.max(240, Number(size?.width) || 140);
    const height = Math.max(120, Number(size?.height) || 240);
    if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return "";

    const pad = 18;
    const area = {
      left: pad,
      top: pad,
      right: width - pad,
      bottom: height - pad,
      width: Math.max(1, width - pad * 2),
      height: Math.max(1, height - pad * 2),
    };

    const points = [];
    if (Array.isArray(polygon)) {
      polygon.forEach((pt) => {
        const lat = parseFloat(pt?.lat);
        const lng = parseFloat(pt?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) points.push({ lat, lng });
      });
    }
    if (pin && Number.isFinite(pin.lat) && Number.isFinite(pin.lng)) points.push({ lat: pin.lat, lng: pin.lng });
    if (!points.length) points.push({ lat: center.lat, lng: center.lng });

    const lats = points.map((pt) => pt.lat);
    const lngs = points.map((pt) => pt.lng);
    let minLat = Math.min(...lats);
    let maxLat = Math.max(...lats);
    let minLng = Math.min(...lngs);
    let maxLng = Math.max(...lngs);

    if (!(maxLat > minLat)) { minLat -= 0.0008; maxLat += 0.0008; }
    if (!(maxLng > minLng)) { minLng -= 0.0008; maxLng += 0.0008; }

    const latSpan = Math.max(0.0001, maxLat - minLat);
    const lngSpan = Math.max(0.0001, maxLng - minLng);

    const project = (lat, lng) => {
      const x = area.left + ((lng - minLng) / lngSpan) * area.width;
      const y = area.top + (1 - ((lat - minLat) / latSpan)) * area.height;
      return { x: Math.max(area.left, Math.min(area.right, x)), y: Math.max(area.top, Math.min(area.bottom, y)) };
    };

    const polygonPath = Array.isArray(polygon) && polygon.length >= 3
      ? polygon
          .map((pt) => {
            const lat = parseFloat(pt?.lat);
            const lng = parseFloat(pt?.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            const pos = project(lat, lng);
            return `${pos.x.toFixed(1)},${pos.y.toFixed(1)}`;
          })
          .filter(Boolean)
          .join(" ")
      : "";

    const pinPos = pin && Number.isFinite(pin.lat) && Number.isFinite(pin.lng)
      ? project(pin.lat, pin.lng)
      : project(center.lat, center.lng);

    const linkedPastureNames = ((target?.pastureIds || [])
      .map((id) => visiblePasturesForPoi.find((p) => String(p.id) === String(id))?.name)
      .filter(Boolean)
      .slice(0, 2)
      .join(" • ")) || "";

    const safeName = String(target?.name || "Ponto de Interesse")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const safeSubtitle = String(linkedPastureNames || (pin ? "Coordenadas registradas" : "Sem alfinete no mapa"))
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const coordLabel = pin && Number.isFinite(pin.lat) && Number.isFinite(pin.lng)
      ? `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}`
      : "Sem coordenadas";

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <defs>
          <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#0F172A"/>
            <stop offset="100%" stop-color="#1E293B"/>
          </linearGradient>
          <linearGradient id="land" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="#0B5D3B" stop-opacity="0.18"/>
            <stop offset="100%" stop-color="#16A34A" stop-opacity="0.34"/>
          </linearGradient>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="6" stdDeviation="8" flood-color="#000000" flood-opacity="0.35"/>
          </filter>
        </defs>
        <rect width="${width}" height="${height}" rx="0" fill="url(#bg)"/>
        <g opacity="0.26">
          <path d="M0 ${height * 0.68} C ${width * 0.18} ${height * 0.58}, ${width * 0.32} ${height * 0.82}, ${width * 0.52} ${height * 0.68} S ${width * 0.82} ${height * 0.56}, ${width} ${height * 0.72} L ${width} ${height} L 0 ${height} Z" fill="#0B5D3B"/>
          <path d="M0 ${height * 0.52} C ${width * 0.16} ${height * 0.44}, ${width * 0.34} ${height * 0.62}, ${width * 0.55} ${height * 0.5} S ${width * 0.84} ${height * 0.38}, ${width} ${height * 0.54}" fill="none" stroke="#38BDF8" stroke-opacity="0.18" stroke-width="4"/>
        </g>
        ${polygonPath ? `<polygon points="${polygonPath}" fill="url(#land)" stroke="#4ADE80" stroke-opacity="0.9" stroke-width="3" filter="url(#shadow)"/>` : ""}
        <g transform="translate(${pinPos.x.toFixed(1)}, ${pinPos.y.toFixed(1)})" filter="url(#shadow)">
          <path d="M0 -22 C 8.8 -22 16 -14.8 16 -6 C 16 6 0 24 0 24 C 0 24 -16 6 -16 -6 C -16 -14.8 -8.8 -22 0 -22 Z" fill="#2563EB" stroke="#FFFFFF" stroke-width="2"/>
          <circle cx="0" cy="-6" r="5" fill="#FFFFFF" fill-opacity="0.95"/>
        </g>
        <rect x="14" y="14" width="${Math.min(width - 28, 280)}" height="52" rx="12" fill="#020617" fill-opacity="0.48" stroke="#FFFFFF" stroke-opacity="0.08"/>
        <text x="28" y="36" fill="#FFFFFF" font-size="16" font-family="Arial, sans-serif" font-weight="700">${safeName}</text>
        <text x="28" y="56" fill="#BFDBFE" font-size="11" font-family="Arial, sans-serif">${safeSubtitle}</text>
        <rect x="14" y="${height - 40}" width="180" height="22" rx="11" fill="#020617" fill-opacity="0.44"/>
        <text x="26" y="${height - 25}" fill="#E2E8F0" font-size="10" font-family="Arial, sans-serif">${coordLabel}</text>
      </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

function POIPinPreview({ poi }){
    const coords = getPoiCoords(poi);
    const staticUrl = buildPoiStaticPreviewUrl(poi, { width: 640, height: 240 });

    return (
      <div style={{height:120,position:"relative",borderBottom:`1px solid ${T.border}`,overflow:"hidden",background:T.card2}}>
        {staticUrl ? (
          <img
            src={staticUrl}
            alt={coords.hasPin ? `Prévia do mapa do POI ${poi?.name || ""}` : "Prévia da localização do POI"}
            loading="lazy"
            decoding="async"
            style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}}
          />
        ) : (
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:coords.hasPin?`linear-gradient(180deg, ${T.accent}18 0%, ${T.card2} 100%)`:T.card2}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6}}>
              <div style={{fontSize:34,lineHeight:1,filter:coords.hasPin?"drop-shadow(0 6px 14px rgba(37,99,235,.28))":"none",opacity:coords.hasPin?1:0.45}}>📍</div>
              <div style={{fontSize:11,color:coords.hasPin?T.accent:T.muted,fontWeight:700,textAlign:"center",padding:"0 10px"}}>{coords.hasPin?"Alfinete no mapa":"Sem alfinete no mapa"}</div>
              {coords.hasPin&&<div style={{fontSize:10,color:T.muted,textAlign:"center"}}>{coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</div>}
            </div>
          </div>
        )}
        <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg, rgba(15,23,42,.06) 0%, rgba(15,23,42,.24) 100%)",pointerEvents:"none"}} />
      </div>
    );
  }

  function getPoiFallbackCenter(target){
    const coords = getPoiCoords(target||{});
    if (coords.hasPin) return { lat: coords.lat, lng: coords.lng };
    const linkedIds = (target?.pastureIds||[]).map(String);
    const linkedPastures = visiblePasturesForPoi.filter(p=>linkedIds.includes(String(p.id)));
    for (const pasture of linkedPastures) {
      const plat = parseFloat(pasture?.lat);
      const plng = parseFloat(pasture?.lng);
      if (Number.isFinite(plat) && Number.isFinite(plng)) return { lat: plat, lng: plng };
      if (Array.isArray(pasture?.polygon) && pasture.polygon.length) {
        const pts = pasture.polygon.filter(pt=>Number.isFinite(parseFloat(pt?.lat)) && Number.isFinite(parseFloat(pt?.lng)));
        if (pts.length) {
          const avgLat = pts.reduce((s,pt)=>s+parseFloat(pt.lat),0)/pts.length;
          const avgLng = pts.reduce((s,pt)=>s+parseFloat(pt.lng),0)/pts.length;
          return { lat: avgLat, lng: avgLng };
        }
      }
    }
    return { lat: -15.8000, lng: -47.9000 };
  }

  function POIPinMapEditor({ value, onPick, onClear, canEditPin=true, baseCenter }) {
    const mapRef = useRef(null);
    const mapObj = useRef(null);
    const markerRef = useRef(null);
    const modeRef = useRef(null);
    const [ready, setReady] = useState(!!window.google?.maps);
    const [mode, setMode] = useState(null);
    const coords = getPoiCoords(value||{});

    useEffect(() => {
      if (window.google?.maps) { setReady(true); return; }
      if (!apiKey) return;
      waitForGoogleMaps(() => setReady(!!window.google?.maps));
    }, []);

    useEffect(() => {
      modeRef.current = mode;
      if (!mapObj.current || !window.google?.maps) return;
      mapObj.current.setOptions({
        draggableCursor: mode === "placing" ? "crosshair" : null,
        draggingCursor: mode === "placing" ? "crosshair" : null,
        gestureHandling: mode === "placing" ? "none" : "greedy",
        clickableIcons: mode !== "placing",
      });
    }, [mode]);

    useEffect(() => {
      if (!ready || !mapRef.current || !window.google?.maps) return;
      const center = coords.hasPin ? { lat: coords.lat, lng: coords.lng } : (baseCenter || { lat: -15.8, lng: -47.9 });
      if (!mapObj.current) {
        mapObj.current = new window.google.maps.Map(mapRef.current, {
          center,
          zoom: coords.hasPin ? 18 : 16,
          mapTypeId: window.google.maps.MapTypeId.HYBRID,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
          ...(getConfiguredMapId() ? { mapId: getConfiguredMapId() } : {}),
        });
        mapObj.current.addListener("click", (ev) => {
          const activeMode = modeRef.current;
          if (!canEditPin || (activeMode !== "placing" && activeMode !== "moving")) return;
          const next = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
          onPick?.(next);
          setMode(null);
        });
      } else {
        mapObj.current.setCenter(center);
        if (coords.hasPin) mapObj.current.setZoom(18);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, coords.hasPin, coords.lat, coords.lng, baseCenter?.lat, baseCenter?.lng, canEditPin, onPick]);

    useEffect(() => {
      if (!ready || !mapObj.current || !window.google?.maps) return;
      if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
      if (coords.hasPin) {
        markerRef.current = createMapMarker({
          position: { lat: coords.lat, lng: coords.lng },
          map: mapObj.current,
          draggable: !!canEditPin && mode === "moving",
          title: value?.name || "POI",
        });
        markerRef.current.addListener("dragend", (ev) => {
          if (!canEditPin) return;
          onPick?.({ lat: ev.latLng.lat(), lng: ev.latLng.lng() });
        });
      }
    }, [ready, coords.hasPin, coords.lat, coords.lng, canEditPin, value?.name, mode, onPick]);

    if (!apiKey) return <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:16,fontSize:12,color:T.muted}}>Mapa indisponível. Configure a Google Maps API Key no Painel do Admin &gt; 🗺️ Mapa.</div>;
    if (!ready) return <div style={{background:`${T.yellow}18`,border:`1px solid ${T.yellow}44`,borderRadius:10,padding:16,fontSize:12,color:T.yellow}}>⏳ Carregando mapa do POI...</div>;

    return (
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {canEditPin&&<div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          <Btn size="sm" variant={mode==="placing"?"primary":"ghost"} onClick={()=>setMode("placing")}>📍 Marcar no mapa</Btn>
          <Btn size="sm" variant={mode==="moving"?"primary":"ghost"} onClick={()=>setMode("moving")} disabled={!coords.hasPin}>↔️ Mover alfinete</Btn>
          <Btn size="sm" variant="ghost" onClick={()=>{ if (mapObj.current) mapObj.current.panTo(coords.hasPin ? { lat: coords.lat, lng: coords.lng } : (baseCenter || { lat:-15.8, lng:-47.9 })); }} disabled={!coords.hasPin && !baseCenter}>🎯 Centralizar</Btn>
          <Btn size="sm" variant="danger" onClick={()=>{ onClear?.(); setMode(null); }} disabled={!coords.hasPin}>🗑️ Remover alfinete</Btn>
        </div>}
        <div style={{fontSize:11,color:T.muted}}>
          {mode==="placing" && "Toque no mapa para marcar o local do POI."}
          {mode==="moving" && "Toque no mapa para reposicionar ou arraste o alfinete atual."}
          {!mode && (coords.hasPin ? "O mapa abre centrado no alfinete salvo deste POI." : "Marque o POI diretamente no mapa para salvar a localização." )}
        </div>
        <div style={{position:"relative",width:"100%",height:320,borderRadius:10,border:`1px solid ${T.border}`,overflow:"hidden"}}>
          <div ref={mapRef} style={{width:"100%",height:"100%"}} />
        </div>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:10}}>
          <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:.5}}>Latitude</div>
            <div style={{fontSize:13,color:T.text,fontWeight:700}}>{coords.hasPin ? coords.lat.toFixed(6) : "Não definida"}</div>
          </div>
          <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",letterSpacing:.5}}>Longitude</div>
            <div style={{fontSize:13,color:T.text,fontWeight:700}}>{coords.hasPin ? coords.lng.toFixed(6) : "Não definida"}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div><Toast toast={toast}/>
      {!!deniedCostCenters.length && <div style={{background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.text}}>⚠️ Sem acesso a: <strong>{deniedCostCenters.join(", ")}</strong>. Esses centros ficam ocultos neste módulo.</div>}
      <input ref={photoInputRef} type="file" accept="image/*" style={{display:"none"}}
        onChange={e=>photoPoiId&&handlePhoto(e,photoPoiId)}/>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:20}}>
        {[
          { id:"pontos_ativos", icon:"📍", label:"Pontos Ativos", value:visiblePois.filter(p=>p.status==="Ativo").length, color:T.green },
          { id:"em_manutencao", icon:"🔧", label:"Em Manutenção", value:activePoiIds.size, color:T.yellow },
          { id:"serv_pausados", icon:"⏸️", label:"Serv. Pausados", value:pausedServiceCount, color:T.orange||T.yellow },
          { id:"serv_agendados", icon:"📅", label:"Serv. Agendados", value:scheduledServiceCount, color:T.blue||T.accent },
        ].filter(card=>allowedPoiCards.includes(card.id)).map(card=>(
          <StatCard key={card.id} icon={card.icon} label={card.label} value={card.value} color={card.color} iconWrapStyle={card.iconWrapStyle} iconStyle={card.iconStyle}/>
        ))}
      </div>

      {!selected&&(<>
        <div style={{display:"flex",flexDirection:isCompactViewport()?"column":"row",gap:10,alignItems:isCompactViewport()?"stretch":"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{display:"flex",flex:1,gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <Input
              placeholder="Pesquisar ponto de interesse..."
              value={poiSearch}
              onChange={e=>setPoiSearch(e.target.value)}
              style={{minWidth:isCompactViewport()?"100%":260,flex:isCompactViewport()?"1 1 100%":"0 1 320px"}}
            />
            <Btn variant={poiFilterOpen?"primary":"ghost"} onClick={()=>setPoiFilterOpen(v=>!v)}>🔎 Filtrar</Btn>
            {(poiSearch || poiFilterStatus!=="all" || poiFilterCostCenter!=="all" || poiFilterCategory!=="all") && (
              <Btn variant="ghost" onClick={()=>{ setPoiSearch(""); setPoiFilterStatus("all"); setPoiFilterCostCenter("all"); setPoiFilterCategory("all"); }}>Limpar filtros</Btn>
            )}
          </div>
          {canCreate&&<div style={{display:"flex",justifyContent:isCompactViewport()?"stretch":"flex-end"}}>
            <Btn onClick={()=>setForm({...defForm})}>+ Novo Ponto de Interesse</Btn>
          </div>}
        </div>
        {poiFilterOpen && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(3,minmax(0,1fr))",gap:10,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12,marginBottom:14}}>
          <Field label="Status">
            <Select value={poiFilterStatus} onChange={e=>setPoiFilterStatus(e.target.value)}>
              <option value="all">Todos</option>
              {(SL.POI_STATUSES||POI_STATUSES).map(s=><option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Centro de custo">
            <Select value={poiFilterCostCenter} onChange={e=>setPoiFilterCostCenter(e.target.value)}>
              <option value="all">Todos</option>
              {allowedCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}
            </Select>
          </Field>
          <Field label="Categoria">
            <Select value={poiFilterCategory} onChange={e=>setPoiFilterCategory(e.target.value)}>
              <option value="all">Todas</option>
              {poiCategories.map(cat=><option key={cat} value={cat}>{cat}</option>)}
            </Select>
          </Field>
        </div>}
        {(() => {
          const visiblePoiCards = isPoiInlineFocusMode
            ? (form?._new ? [] : (poiFocusedId ? visiblePois.filter(poi=>String(poi.id)===poiFocusedId) : []))
            : visiblePois;
          return <>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:12,color:T.muted}}>{visiblePoiCards.length} ponto(s) encontrado(s)</div>
        </div>
        {form?._new && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:16,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Novo Ponto de Interesse</div>
            <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
            <Field label="Nome *" col="1/-1"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="ex: Bebedouro Pasto 5"/></Field>
            <Field label="Centro de Custo"><Select value={form.costCenter} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>{(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).map(c=><option key={c}>{c}</option>)}</Select></Field>
            <Field label="Categoria do POI"><Select value={form.category||""} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>{(SL.POI_CATEGORIES||["Pastos","Casas","Galpões","Curral","Baia"]).map(c=><option key={c}>{c}</option>)}</Select></Field>
            <Field label="Status"><Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{(SL.POI_STATUSES||POI_STATUSES).map(s=><option key={s}>{s}</option>)}</Select></Field>
            <Field label="Localizações vinculadas (Pastos)" col="1/-1"><div style={{display:"flex",flexDirection:"column",gap:6,marginTop:4,maxHeight:180,overflowY:"auto",padding:4}}>{visiblePasturesForPoi.length===0&&<div style={{color:T.muted,fontSize:12}}>Nenhuma localização acessível para vincular neste centro de custo.</div>}{visiblePasturesForPoi.map(p=><label key={p.id} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"7px 10px",borderRadius:8,background:(form.pastureIds||[]).includes(p.id)?`${T.green}18`:T.card2,border:`1px solid ${(form.pastureIds||[]).includes(p.id)?T.green:T.border}`}}><input type="checkbox" checked={(form.pastureIds||[]).includes(p.id)} onChange={e=>setForm(f=>({...f,pastureIds:e.target.checked?[...(f.pastureIds||[]),p.id]:(f.pastureIds||[]).filter(id=>id!==p.id)}))} style={{width:16,height:16,accentColor:T.green}}/><span style={{fontSize:13,color:T.text,fontWeight:500}}>{p.name}</span><span style={{fontSize:11,color:T.muted,marginLeft:"auto"}}>{p.grassType}</span></label>)}</div></Field>
            <Field label="🗺️ Alfinete do POI no mapa" col="1/-1"><POIPinMapEditor value={form} baseCenter={getPoiFallbackCenter(form)} canEditPin={canEdit} onPick={(pos)=>setForm(f=>({...f,lat:String(pos.lat.toFixed(6)),lng:String(pos.lng.toFixed(6))}))} onClear={()=>setForm(f=>({...f,lat:"",lng:"",customFields:(f.customFields||[]).filter(cf=>!['latitude','longitude','lat','lng'].includes(String(cf?.key||'').trim().toLowerCase()))}))}/></Field>
            <Field label="Saldo Inicial" col="1/-1"><Input type="number" min="0" step="0.001" value={form.initialQty||0} onChange={e=>setForm(f=>({...f,initialQty:e.target.value}))} placeholder="Quantidade já existente em estoque (0 se novo)"/></Field>
            <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
            <Field label="📋 Dados Adicionais Personalizados" col="1/-1"><div style={{marginTop:6}}>{(form.customFields||[]).map((cf,idx)=><div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:8,marginBottom:8,alignItems:"center"}}><Input value={cf.key} onChange={e=>updateCustomField(idx,"key",e.target.value)} placeholder="Nome do campo (ex: Capacidade)"/><Input value={cf.value} onChange={e=>updateCustomField(idx,"value",e.target.value)} placeholder="Valor (ex: 500 litros)"/><button type="button" onClick={()=>removeCustomField(idx)} style={{background:"none",border:"none",cursor:"pointer",color:T.red,fontSize:18,padding:"0 4px"}}>✕</button></div>)}<Btn variant="ghost" size="sm" onClick={addCustomField}>+ Adicionar Campo</Btn></div></Field>
            <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn><Btn onClick={save}>Salvar</Btn></div>
          </div>
        </div>}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14}}>
          {visiblePoiCards.map(poi=>{
            const svcs=poiServices(poi.id);
            const hasActiveSvc=svcs.some(s=>!s.dateEnd);
            const editingThis = form && !form._new && String(form.id)===String(poi.id);
            return(
              <React.Fragment key={poi.id}>
              <div key={poi.id} style={{background:T.card,border:`2px solid ${editingThis?T.purple:T.border}`,borderRadius:14,overflow:"hidden",cursor:"pointer",transition:"all .2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=T.purple;e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}>
                {/* Pin preview header */}
                <div onClick={()=>setSelected(poi)}>
                  <POIPinPreview poi={poi} />
                </div>
                <div style={{padding:"12px 16px"}} onClick={()=>setSelected(poi)}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,gap:8}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:T.text}}>{`${poi.name}${getPoiFarmName(poi)?` - ${getPoiFarmName(poi)}`:""}`}</div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                      <Badge color={statusColor[poi.status]||T.muted}>{poi.status}</Badge>
                      <button type="button" title={(poi.photo?"POI com foto cadastrada":"POI sem foto cadastrada")} onClick={e=>{e.stopPropagation(); if(poi.photo){ setSelected(poi); } else if(canEdit){ setPhotoPoiId(poi.id); photoInputRef.current.click(); } }} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:999,border:`1px solid ${poi.photo?`${T.accent}44`:T.border}`,background:(poi.photo?`${T.accent}14`:T.card2),color:(poi.photo?T.accent:T.muted),fontSize:11,fontWeight:700,cursor:(poi.photo||canEdit)?"pointer":"default"}}>
                        <ServicePhotoStatusIcon size={16} hasPhotos={!!poi.photo}/>
                      </button>
                    </div>
                  </div>
                  <div style={{fontSize:12,color:T.muted,marginBottom:6}}>
                    <CCBadge cc={poi.costCenter}/>
                  </div>
                  {poi.pastureIds?.length>0&&<div style={{fontSize:11,color:T.green,marginBottom:4}}>
                    🗺️ {poi.pastureIds.map(id=>pastureMap[id]||id).join(", ")}
                  </div>}
                  {getPoiMaintenanceLine(poi.id)&&<div style={{fontSize:11,color:T.yellow,marginTop:4,fontWeight:700}}>
                    {getPoiMaintenanceLine(poi.id)}
                  </div>}
                  {svcs.length>0&&!hasActiveSvc&&<div style={{fontSize:11,color:T.muted}}>
                    🔨 Último serviço: {fmtDate(svcs[0].dateStart)}
                  </div>}
                  {(poi.customFields||[]).length>0&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>
                    📋 {poi.customFields.length} dado(s) adicional(is)
                  </div>}
                </div>
                {(canEdit||canDelete)&&<div style={{padding:"8px 16px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8}}>
                  {canEdit&&<Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();setForm({...poi,_new:false});}}>✏️ Editar</Btn>}
                  {canDelete&&<Btn size="sm" variant="danger" confirmMessage={`Deseja realmente excluir o ponto de interesse "${poi.name || "sem nome"}"?`} onClick={e=>{e.stopPropagation(); deletePoiRecord(poi, { skipConfirm:true });}}>🗑️</Btn>}
                </div>}
              </div>
              {editingThis && <div key={`${poi.id}-edit`} style={{gridColumn:"1/-1",background:T.card,border:`1px solid ${T.purple}`,borderRadius:16,padding:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Editar Ponto de Interesse</div>
                  <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
                </div>
                <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
                  <Field label="Nome *" col="1/-1"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="ex: Bebedouro Pasto 5"/></Field>
                  <Field label="Centro de Custo"><Select value={form.costCenter} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>{(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).map(c=><option key={c}>{c}</option>)}</Select></Field>
                  <Field label="Categoria do POI"><Select value={form.category||""} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>{(SL.POI_CATEGORIES||["Pastos","Casas","Galpões","Curral","Baia"]).map(c=><option key={c}>{c}</option>)}</Select></Field>
                  <Field label="Status"><Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{(SL.POI_STATUSES||POI_STATUSES).map(s=><option key={s}>{s}</option>)}</Select></Field>
                  <Field label="Localizações vinculadas (Pastos)" col="1/-1"><div style={{display:"flex",flexDirection:"column",gap:6,marginTop:4,maxHeight:180,overflowY:"auto",padding:4}}>{visiblePasturesForPoi.length===0&&<div style={{color:T.muted,fontSize:12}}>Nenhuma localização acessível para vincular neste centro de custo.</div>}{visiblePasturesForPoi.map(p=><label key={p.id} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"7px 10px",borderRadius:8,background:(form.pastureIds||[]).includes(p.id)?`${T.green}18`:T.card2,border:`1px solid ${(form.pastureIds||[]).includes(p.id)?T.green:T.border}`}}><input type="checkbox" checked={(form.pastureIds||[]).includes(p.id)} onChange={e=>setForm(f=>({...f,pastureIds:e.target.checked?[...(f.pastureIds||[]),p.id]:(f.pastureIds||[]).filter(id=>id!==p.id)}))} style={{width:16,height:16,accentColor:T.green}}/><span style={{fontSize:13,color:T.text,fontWeight:500}}>{p.name}</span><span style={{fontSize:11,color:T.muted,marginLeft:"auto"}}>{p.grassType}</span></label>)}</div></Field>
                  <Field label="🗺️ Alfinete do POI no mapa" col="1/-1"><POIPinMapEditor value={form} baseCenter={getPoiFallbackCenter(form)} canEditPin={canEdit} onPick={(pos)=>setForm(f=>({...f,lat:String(pos.lat.toFixed(6)),lng:String(pos.lng.toFixed(6))}))} onClear={()=>setForm(f=>({...f,lat:"",lng:"",customFields:(f.customFields||[]).filter(cf=>!['latitude','longitude','lat','lng'].includes(String(cf?.key||'').trim().toLowerCase()))}))}/></Field>
                  <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
                  <Field label="📋 Dados Adicionais Personalizados" col="1/-1"><div style={{marginTop:6}}>{(form.customFields||[]).map((cf,idx)=><div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:8,marginBottom:8,alignItems:"center"}}><Input value={cf.key} onChange={e=>updateCustomField(idx,"key",e.target.value)} placeholder="Nome do campo (ex: Capacidade)"/><Input value={cf.value} onChange={e=>updateCustomField(idx,"value",e.target.value)} placeholder="Valor (ex: 500 litros)"/><button type="button" onClick={()=>removeCustomField(idx)} style={{background:"none",border:"none",cursor:"pointer",color:T.red,fontSize:18,padding:"0 4px"}}>✕</button></div>)}<Btn variant="ghost" size="sm" onClick={addCustomField}>+ Adicionar Campo</Btn></div></Field>
                  <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}>{!form._new&&<Btn variant="danger" confirmMessage={`Deseja realmente excluir o ponto de interesse "${form?.name || "sem nome"}"?`} onClick={()=>deletePoiRecord(form, { skipConfirm:true })}>Excluir</Btn>}<Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn><Btn onClick={save}>Salvar</Btn></div>
                </div>
              </div>}
              </React.Fragment>
            );
          })}
          {visiblePoiCards.length===0&&<div style={{color:T.muted,padding:48,textAlign:"center",gridColumn:"1/-1",fontSize:14}}>
            {isAdmin?"Nenhum ponto de interesse cadastrado. Clique em \"+ Novo Ponto de Interesse\" para começar.":"Nenhum ponto de interesse cadastrado ainda."}
          </div>}
        </div>
        </>;
        })()}
      </>)}

      {/* ── DETAIL VIEW ── */}
      {selected&&(()=>{
        const activeLink=getPoiActiveRecord(selected.id);
        return(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
              <button onClick={()=>setSelected(null)} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:20}}>←</button>
              <div>
                <div><div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:4}}>{displayLocationRecordNumber(selected)}</div><div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,color:T.text}}>{selected.name}</div></div>
                <div style={{fontSize:12,color:T.muted,marginTop:2}}>{selected.costCenter} {selected.pastureIds?.length?`· ${selected.pastureIds.map(id=>pastureMap[id]||id).join(", ")}`:""}</div>
              </div>
              <Badge color={statusColor[selected.status]||T.muted} style={{marginLeft:8}}>{selected.status}</Badge>
              {(canEdit||canCreate||canDelete)&&<div style={{marginLeft:"auto",display:"flex",gap:8,flexWrap:"wrap"}}>
                {canEdit&&<Btn size="sm" onClick={()=>setForm({...selected,_new:false})}>✏️ Editar</Btn>}
                {canEdit&&<Btn size="sm" variant="ghost" onClick={()=>{setPhotoPoiId(selected.id);photoInputRef.current.click();}}>📷 Foto</Btn>}
                {canDelete&&<Btn size="sm" variant="danger" confirmMessage={`Deseja realmente excluir o ponto de interesse "${selected?.name || "sem nome"}"?`} onClick={()=>deletePoiRecord(selected, { skipConfirm:true })}>🗑️</Btn>}
              </div>}
            </div>

            <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"minmax(0,0.94fr) minmax(0,1.06fr)":"1fr 1fr",gap:16,marginBottom:20,alignItems:"start"}}>
              {/* Photo */}
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden",minHeight:isCompactViewport()?180:200,display:"flex",alignItems:"center",justifyContent:"center",cursor:isAdmin?"pointer":"default"}}
                >
                {selected.photo
                  ? <img src={selected.photo} alt="POI" style={{width:"100%",height:isCompactViewport()?180:240,objectFit:"cover"}}/>
                  : <div style={{textAlign:"center",color:T.muted,fontSize:13,padding:20}}>
                      <div style={{fontSize:40,marginBottom:8}}>📷</div>
                      {isAdmin?"Clique em \"📷 Foto\" para adicionar":"Sem foto cadastrada"}
                    </div>}
              </div>

              {/* Info */}
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
                  <div style={{fontWeight:700,color:T.text,marginBottom:10}}>📋 Informações</div>
                  {[
                    ["Centro de Custo", <CCBadge cc={selected.costCenter}/>],
                    ["Status", <Badge color={statusColor[selected.status]||T.muted}>{selected.status}</Badge>],
                    ["Localizações", selected.pastureIds?.length?selected.pastureIds.map(id=>pastureMap[id]||id).join(", "):"—"],
                    ["Observações", selected.notes||"—"],
                  ].map(([k,v])=>{
                    const isElementValue = React.isValidElement(v);
                    const isCostCenterRow = k === "Centro de Custo";
                    return (
                    <div key={k} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"minmax(0,1fr) auto":"minmax(0,1fr) auto",alignItems:isCostCenterRow?"start":"center",columnGap:12,rowGap:6,padding:isCostCenterRow?"2px 0 8px":"8px 0",borderBottom:`1px solid ${T.border}22`,fontSize:13}}>
                      <span style={{color:T.muted,minWidth:0,paddingTop:isCostCenterRow?4:0}}>{k}</span>
                      <span style={{color:T.text,fontWeight:500,minWidth:0,maxWidth:isCompactViewport()?(isCostCenterRow?132:150):220,textAlign:"right",wordBreak:isElementValue?"normal":"break-word",whiteSpace:isElementValue?"normal":"normal",display:"inline-flex",justifyContent:"flex-end",alignItems:isCostCenterRow?"flex-start":"center",justifySelf:"end"}}>{v}</span>
                    </div>
                  )})}
                </div>

                {/* Active maintenance alert */}
                {activeLink&&<div style={{background:`${T.yellow}18`,border:`1px solid ${T.yellow}44`,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontWeight:700,color:T.yellow,fontSize:13,marginBottom:4}}>{activeLink.kind==="occurrence"?"🚨 Ocorrência em Andamento":"⚙️ Manutenção em Andamento"}</div>
                  <div style={{fontSize:12,color:T.text}}>{poiRecordLabel(activeLink.item, activeLink.kind)}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                    {fmtDate(getMaintenanceAnchor(activeLink.item))}
                    {daysInProgress(activeLink.item)!=null?` · ${daysInProgress(activeLink.item)} dias em andamento`:" · Em andamento"}
                  </div>
                </div>}
              </div>
            </div>

            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontWeight:700,color:T.text,marginBottom:10}}>🗺️ Mapa do ponto</div>
              <POIPinMapEditor
                value={selected}
                canEditPin={false}
                baseCenter={getPoiFallbackCenter(selected)}
              />
              <div style={{marginTop:10,fontSize:11,color:T.muted}}>
                O mapa interativo completo é aberto somente no detalhe deste POI para manter a listagem mais leve e estável, inclusive no offline.
              </div>
            </div>

            {/* Custom fields */}
            {(selected.customFields||[]).length>0&&(
              <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginBottom:16}}>
                <div style={{fontWeight:700,color:T.text,marginBottom:12}}>📋 Dados Adicionais</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
                  {selected.customFields.map((cf,i)=>(
                    <div key={i} style={{background:T.card2,borderRadius:8,padding:"10px 14px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.6,marginBottom:3}}>{cf.key}</div>
                      <div style={{fontSize:14,color:T.text,fontWeight:600}}>{cf.value||"—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{fontSize:11,color:T.muted,marginBottom:10}}>Os fluxos de edição e histórico deste ponto agora ficam ancorados dentro deste detalhe.</div>
            {form && !form._new && String(form.id)===String(selected.id) && <div style={{background:T.card,border:`1px solid ${T.purple}`,borderRadius:16,padding:16,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>Editar Ponto de Interesse</div>
                <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
                <Field label="Nome *" col="1/-1"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="ex: Bebedouro Pasto 5"/></Field>
                <Field label="Centro de Custo"><Select value={form.costCenter} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>{(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).map(c=><option key={c}>{c}</option>)}</Select></Field>
                <Field label="Categoria do POI"><Select value={form.category||""} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>{(SL.POI_CATEGORIES||["Pastos","Casas","Galpões","Curral","Baia"]).map(c=><option key={c}>{c}</option>)}</Select></Field>
                <Field label="Status"><Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{(SL.POI_STATUSES||POI_STATUSES).map(s=><option key={s}>{s}</option>)}</Select></Field>
                <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
                <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}><Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn><Btn onClick={save}>Salvar</Btn></div>
              </div>
            </div>}
            {maintenanceTrace && String(maintenanceTrace.item?.poiId || maintenanceTrace.item?.poi || selected.id)===String(selected.id) && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:16,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>{`Histórico da manutenção — ${maintenanceTrace.label}`}</div>
                <Btn variant="ghost" onClick={()=>setMaintenanceTrace(null)}>← Fechar</Btn>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:10,marginBottom:12}}>
                <div style={{background:T.card2,borderRadius:10,padding:12}}><div style={{fontSize:11,color:T.muted}}>Título</div><div style={{fontWeight:700,color:T.text}}>{maintenanceTrace.label}</div></div>
                <div style={{background:T.card2,borderRadius:10,padding:12}}><div style={{fontSize:11,color:T.muted}}>Responsável</div><div style={{fontWeight:700,color:T.text}}>{maintenanceTrace.responsible}</div></div>
                <div style={{background:T.card2,borderRadius:10,padding:12}}><div style={{fontSize:11,color:T.muted}}>Início</div><div style={{fontWeight:700,color:T.text}}>{fmtDate(getMaintenanceAnchor(maintenanceTrace.item))}</div></div>
                <div style={{background:T.card2,borderRadius:10,padding:12}}><div style={{fontSize:11,color:T.muted}}>Dias corridos</div><div style={{fontWeight:700,color:T.text}}>{maintenanceTrace.durationDays ?? 0} dia(s)</div></div>
                <div style={{background:T.card2,borderRadius:10,padding:12}}><div style={{fontSize:11,color:T.muted}}>Tempo em pausa</div><div style={{fontWeight:700,color:T.text}}>{maintenanceTrace.pausedDays} dia(s)</div></div>
                <div style={{background:T.card2,borderRadius:10,padding:12}}><div style={{fontSize:11,color:T.muted}}>Status</div><div style={{fontWeight:700,color:T.text}}>{maintenanceTrace.item?.status || "—"}</div></div>
              </div>
              <div style={{fontWeight:700,color:T.text,marginBottom:8}}>Linha do tempo</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:360,overflowY:"auto"}}>
                {maintenanceTrace.timeline.length===0 && <div style={{color:T.muted,fontSize:13}}>Sem eventos registrados.</div>}
                {maintenanceTrace.timeline.map((ev, idx)=><div key={`${ev.type||'event'}-${idx}`} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:10,padding:12}}><div style={{display:"flex",justifyContent:"space-between",gap:8,marginBottom:4,flexWrap:"wrap"}}><strong style={{color:T.text,fontSize:13}}>{ev.action}</strong><span style={{fontSize:11,color:T.muted}}>{fmtDate(ev.date)}</span></div><div style={{fontSize:12,color:T.muted}}>👤 {ev.actor || "—"}</div>{ev.note && <div style={{fontSize:12,color:T.text,marginTop:4}}>{ev.note}</div>}</div>)}
              </div>
            </div>}
            {/* Service history */}
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
              <div style={{fontWeight:700,color:T.text,marginBottom:12}}>🔨 Rastreamento de manutenção</div>
              {buildPoiMaintenanceEntries(selected.id).length===0 && <div style={{color:T.muted,fontSize:13}}>Nenhum serviço ou ocorrência registrado para este ponto de interesse.</div>}
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {buildPoiMaintenanceEntries(selected.id).map(({ kind, item })=>{
                  const durationDays = getMaintenanceDurationDays(item);
                  const pausedDays = getPausedDays(item);
                  const startedAt = getMaintenanceAnchor(item);
                  const endedAt = getMaintenanceEnd(item);
                  const tone = getMaintenanceStatusTone(item);
                  const label = poiRecordLabel(item, kind);
                  const responsible = getMaintenanceResponsible(item, kind);
                  return (
                    <button
                      key={`${kind}-${item.id}`}
                      type="button"
                      onClick={()=>setMaintenanceTrace({ kind, item, label, responsible, durationDays, pausedDays, timeline: buildMaintenanceTimeline(item, kind) })}
                      style={{display:"flex",gap:12,alignItems:"flex-start",padding:"12px 14px",background:T.card2,borderRadius:10,border:`1px solid ${tone}33`,textAlign:"left",cursor:"pointer"}}
                    >
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                          <span style={{fontWeight:700,fontSize:14,color:T.text}}>{kind==="occurrence"?"🚨":"⚙️"} {label}</span>
                          <Badge color={tone}>{item.status || (kind==="occurrence"?"Ocorrência":"Serviço")}</Badge>
                        </div>
                        <div style={{fontSize:12,color:T.muted,display:"flex",gap:12,flexWrap:"wrap"}}>
                          <span>📅 Início: {fmtDate(startedAt)}</span>
                          {endedAt ? <span>🏁 Fim: {fmtDate(endedAt)}</span> : <span>⏳ {durationDays ?? 0} dia(s) corridos</span>}
                          <span>⏸️ Pausada por {pausedDays} dia(s)</span>
                          <span>👤 {responsible}</span>
                        </div>
                        {item.costCenter&&<div style={{fontSize:11,color:T.muted,marginTop:4}}><CCBadge cc={item.costCenter}/></div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {selected?.photo && (
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,marginTop:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:12,flexWrap:"wrap"}}>
            <div style={{fontWeight:700,color:T.text}}>🖼️ Foto do ponto</div>
            <div style={{fontSize:11,color:T.muted}}>Visualização inline no detalhe do registro</div>
          </div>
          <img src={selected.photo} alt="POI" style={{width:"100%",maxHeight:isCompactViewport()?260:420,objectFit:"cover",borderRadius:12,border:`1px solid ${T.border}`}}/>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════

  return { PasturesModule, POIModule };
}